import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";
import api from "@/lib/api";
import { RecruiterAvatar, rdPhotoSrc } from "@/components/RecruiterAvatar";
import { frenchDateLong, frenchDateFull } from "@/lib/shariti";
import { PageHeader, PageBody } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CalendarClock, AlertTriangle, ClipboardCheck, Users, KeyRound, RefreshCw,
  Copy, MessageSquare, Plus, Trash2, MapPin, Link2, RotateCcw, Ban, Loader2,
  ShieldCheck, ChevronLeft, ChevronRight, CheckCircle2, Pencil, FileText, Sparkles,
  Hourglass, ShieldQuestion, Navigation, XCircle, History, Eye, CalendarPlus, Info,
  UserCheck, Flag, Archive, ArchiveRestore, Search, UserPlus, GitMerge, UserRound, Mail,
  ArrowUp, ArrowDown, Minus, TrendingUp, Trophy, Store, Route, Settings2, Filter,
  BarChart3, TrendingDown, ArrowRight, Phone, PhoneCall, CalendarRange, GraduationCap,
  Gauge, Target, CalendarDays, Percent, Users2, Clock, Download,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { fr } from "date-fns/locale";

const fmtH = (h) => (h === null || h === undefined ? "—" : Number(h).toFixed(2).replace(".", ","));

// Date + heure courtes en FR (ex. « 12 août 14:22 ») pour la traçabilité (provenance).
const frDateTimeShort = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
      + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

// Badge visuel de l'état d'accès à l'espace (Actif / Lecture seule / Désactivé).
const ACCESS_META = {
  active: { label: "Actif", icon: ShieldCheck, cls: "border-[rgba(31,157,85,0.35)] bg-[rgba(31,157,85,0.10)] text-[#0F5A2F]" },
  readonly: { label: "Lecture seule", icon: Eye, cls: "border-[rgba(178,106,0,0.35)] bg-[rgba(238,114,46,0.12)] text-[#B26A00]" },
  disabled: { label: "Désactivé", icon: Ban, cls: "border-[rgba(155,34,38,0.35)] bg-[rgba(214,69,69,0.10)] text-[#9B2226]" },
};

function AccessBadge({ mode }) {
  const M = ACCESS_META[mode] || ACCESS_META.active;
  const Icon = M.icon;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${M.cls}`} data-testid={`access-badge-${mode}`}>
      <Icon className="h-3 w-3" />{M.label}
    </span>
  );
}

// Barèmes d'OBJECTIF de productivité BS/h (défauts ; réglables par un admin depuis
// l'écran Productivité, stockés en base). Couleur associée pour repérer les écarts :
//   • Magasin       : vert ≥ 0,36 · orange 0,30–0,35 · rouge < 0,30
//   • Porte-à-porte : vert ≥ 0,30 · orange 0,24–0,29 · rouge < 0,24
const BSH_SEUILS_DEFAUT = {
  magasin: { vert: 0.36, orange: 0.3 },
  terrain: { vert: 0.3, orange: 0.24 },
};
// Contexte fournissant le barème courant (+ setter) à tous les onglets Pointage.
const SeuilsContext = createContext({ seuils: BSH_SEUILS_DEFAUT, setSeuils: () => {} });
// Renvoie la classe de couleur du texte selon la valeur BS/h, le contexte
// (« magasin » par défaut, « terrain » pour le porte-à-porte) et le barème courant.
// Valeur nulle → violet neutre (« — »).
function bshTone(v, kind = "magasin", seuils = BSH_SEUILS_DEFAUT) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "text-[#5D3FD3]";
  const s = (seuils || BSH_SEUILS_DEFAUT)[kind === "terrain" ? "terrain" : "magasin"] || BSH_SEUILS_DEFAUT.magasin;
  if (v >= s.vert) return "text-emerald-600";
  if (v >= s.orange) return "text-amber-500";
  return "text-rose-600";
}
// « Rouge » = mesurable (ratio non nul) ET strictement sous le seuil orange.
function isRedBsh(v, kind = "magasin", seuils = BSH_SEUILS_DEFAUT) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return false;
  const s = (seuils || BSH_SEUILS_DEFAUT)[kind === "terrain" ? "terrain" : "magasin"] || BSH_SEUILS_DEFAUT.magasin;
  return v < s.orange;
}

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const monthISO = () => todayISO().slice(0, 7);

// Libellé complet et lisible d'une semaine : « samedi 1 août – dimanche 2 août »
// (au lieu de « 01/08 – 02/08 »). Utilise les jours réels de la semaine (day_dates).
const frenchWeekRange = (w) => {
  const ds = (w && w.day_dates) || [];
  if (!ds.length) return (w && w.range) || "";
  const opt = { weekday: "long", day: "numeric", month: "long" };
  const f = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", opt);
  return `${f(ds[0])} – ${f(ds[ds.length - 1])}`;
};

const STATUT = {
  pointe: { label: "Pointé", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" },
  en_mission: { label: "En mission", cls: "bg-[rgba(47,111,237,0.10)] text-[#31517d]" },
  deduit_a_confirmer: { label: "Déduit — à confirmer", cls: "bg-[rgba(47,111,237,0.12)] text-[#2F6FED]" },
  corrige_admin: { label: "Corrigé", cls: "bg-[rgba(185,137,0,0.14)] text-[#8a6600]" },
  saisie_admin_retroactive: { label: "Saisie admin", cls: "bg-[rgba(93,63,211,0.12)] text-[#5D3FD3]" },
  annule: { label: "Annulé", cls: "bg-[var(--surface-2)] text-muted-ink line-through" },
  absence_a_trancher: { label: "Absence à trancher", cls: "bg-[rgba(214,69,69,0.12)] text-[#9B2226]" },
  anomalie: { label: "Anomalie", cls: "bg-[rgba(214,69,69,0.12)] text-[#9B2226]" },
  exceptionnel_en_attente: { label: "Exceptionnel — en attente", cls: "bg-[rgba(185,137,0,0.14)] text-[#8a6600]" },
  exceptionnel_valide: { label: "Exceptionnel — validé", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" },
  exceptionnel_rejete: { label: "Exceptionnel — refusé", cls: "bg-[rgba(214,69,69,0.12)] text-[#9B2226]" },
  hors_pointage: { label: "Hors pointage", cls: "bg-[rgba(93,63,211,0.10)] text-[#5D3FD3]" },
};

// Motif d'un pointage exceptionnel (ton neutre).
const MOTIF_LABEL = {
  hors_zone: "Hors zone GPS",
  gps_refuse: "Localisation désactivée",
  gps_indisponible: "GPS indisponible",
  spot_sans_gps: "Spot sans coordonnées",
};

function StatutBadge({ statut }) {
  const s = STATUT[statut] || { label: statut, cls: "bg-[var(--surface-2)] text-muted-ink" };
  return <Badge className={`${s.cls} hover:${s.cls} font-semibold`} data-testid={`statut-${statut}`}>{s.label}</Badge>;
}

// Provenance LISIBLE des heures : « d'où viennent-elles / qui les a posées » (petit texte gris).
function ProvenanceLine({ prov, testid }) {
  if (!prov || !prov.label) return null;
  const bits = [];
  if (prov.by) bits.push(`par ${prov.by}`);
  const at = frDateTimeShort(prov.at);
  if (at) bits.push(`le ${at}`);
  return (
    <div className="mt-1 flex max-w-[260px] items-start gap-1 text-[11px] leading-snug text-muted-ink" data-testid={testid}>
      <Info className="mt-[1px] h-3 w-3 shrink-0 opacity-70" />
      <span>
        {prov.label}{bits.length ? ` · ${bits.join(" ")}` : ""}
        {prov.confirme_collaborateur ? " · confirmé par le collaborateur" : ""}
      </span>
    </div>
  );
}

// Métadonnées sous le badge de statut : POURQUOI ce statut (explication) + provenance des heures.
function StatutMeta({ row }) {
  const explCls = row.statut === "deduit_a_confirmer" ? "text-[#2F6FED]" : "text-[#9B2226]";
  return (
    <>
      {row.explication && (
        <div className={`mt-1 max-w-[260px] text-[11px] leading-snug ${explCls}`} data-testid={`journee-explic-${row.code_rd}`}>
          {row.explication}
        </div>
      )}
      <ProvenanceLine prov={row.provenance} testid={`journee-provenance-${row.code_rd}`} />
    </>
  );
}

// Indicateur de vérification de position (geofencing) — neutre & informatif.
function PositionTag({ row }) {
  const gs = row.geofence_statut;
  const dist = row.gps_distance_m;
  if (gs === "exceptionnel_en_attente") {
    return (
      <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-[rgba(185,137,0,0.14)] px-1.5 py-0.5 text-[10px] font-bold text-[#8a6600]" data-testid={`position-tag-${row.code_rd}`}>
        <Hourglass className="h-3 w-3" /> Pointage exceptionnel en attente
      </div>
    );
  }
  if (gs === "exceptionnel_valide") {
    return (
      <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-[rgba(31,157,85,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#0F5A2F]" data-testid={`position-tag-${row.code_rd}`}>
        <ShieldCheck className="h-3 w-3" /> Exceptionnel validé
      </div>
    );
  }
  if (gs === "exceptionnel_rejete") {
    return (
      <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-[rgba(214,69,69,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#9B2226]" data-testid={`position-tag-${row.code_rd}`}>
        <XCircle className="h-3 w-3" /> Exceptionnel refusé
      </div>
    );
  }
  if (row.position_verifiee || gs === "verifiee") {
    return (
      <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#0F5A2F]" data-testid={`position-tag-${row.code_rd}`}>
        <ShieldCheck className="h-3 w-3" /> Position vérifiée{dist != null ? ` · ${dist} m` : ""}
      </div>
    );
  }
  if (dist != null) {
    return (
      <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-ink"><MapPin className="h-3 w-3" />{dist} m du spot</div>
    );
  }
  return null;
}

function CodeBadge({ code }) {
  if (!code) return <span className="text-muted-ink">—</span>;
  return (
    <button onClick={() => { navigator.clipboard?.writeText(code); toast.success("Code copié"); }}
      className="rounded-md bg-[var(--surface-2)] px-1.5 py-0.5 font-mono text-[12px] font-bold text-[var(--shariti-orange)] transition-colors hover:bg-[rgba(238,114,46,0.18)]"
      data-testid={`code-rd-${code}`} title="Copier le code">
      {code}
    </button>
  );
}

function Kpi({ label, value, tone = "ink", testid, valueClass, sub }) {
  const toneCls = { ink: "text-ink", orange: "text-[var(--shariti-orange)]", blue: "text-[#2F6FED]", red: "text-[#9B2226]", green: "text-[#0F5A2F]", violet: "text-[#5D3FD3]" }[tone];
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">{label}</div>
      <div className={`mt-1 text-2xl font-extrabold tabular-nums ${valueClass || toneCls}`}>{value}</div>
      {sub ? <div className="mt-0.5 text-[11px] text-muted-ink">{sub}</div> : null}
    </div>
  );
}

export default function AdminPointage() {
  const [tab, setTab] = useState("journee");
  const [seuils, setSeuils] = useState(BSH_SEUILS_DEFAUT);
  useEffect(() => {
    api.get("/pointage/seuils-productivite")
      .then((r) => setSeuils({ magasin: r.data.magasin || BSH_SEUILS_DEFAUT.magasin, terrain: r.data.terrain || BSH_SEUILS_DEFAUT.terrain }))
      .catch(() => {});
  }, []);
  return (
    <SeuilsContext.Provider value={{ seuils, setSeuils }}>
    <div data-testid="admin-pointage-page">
      <PageHeader title="Pointage & Heures"
        subtitle="Affectez les collaborateurs, suivez le pointage terrain et validez les heures retenues.">
      </PageHeader>
      <PageBody>
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-5 flex h-auto flex-wrap gap-1 bg-[var(--surface-2)] p-1" data-testid="pointage-tabs">
            <TabsTrigger value="journee" data-testid="tab-journee" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><CalendarClock className="mr-1.5 h-4 w-4" />Journée</TabsTrigger>
            <TabsTrigger value="terrain-jour" data-testid="tab-terrain-jour" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><PhoneCall className="mr-1.5 h-4 w-4" />Terrain du jour</TabsTrigger>
            <TabsTrigger value="heures" data-testid="tab-heures" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><Hourglass className="mr-1.5 h-4 w-4" />Heures</TabsTrigger>
            <TabsTrigger value="productivite" data-testid="tab-productivite" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><TrendingUp className="mr-1.5 h-4 w-4" />Productivité</TabsTrigger>
            <TabsTrigger value="suivi-quotidien" data-testid="tab-suivi-quotidien" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><Gauge className="mr-1.5 h-4 w-4" />Pilotage du jour</TabsTrigger>
            <TabsTrigger value="histo-bs" data-testid="tab-histo-bs" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><BarChart3 className="mr-1.5 h-4 w-4" />Historique BS</TabsTrigger>
            <TabsTrigger value="anomalies" data-testid="tab-anomalies" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><AlertTriangle className="mr-1.5 h-4 w-4" />Anomalies</TabsTrigger>
            <TabsTrigger value="confirmations" data-testid="tab-confirmations" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><ClipboardCheck className="mr-1.5 h-4 w-4" />Confirmations</TabsTrigger>
            <TabsTrigger value="responsables" data-testid="tab-responsables" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><UserCheck className="mr-1.5 h-4 w-4" />Responsables</TabsTrigger>
            <TabsTrigger value="collaborateurs" data-testid="tab-collaborateurs" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><Users className="mr-1.5 h-4 w-4" />Collaborateurs</TabsTrigger>
            <TabsTrigger value="api" data-testid="tab-api" className="data-[state=active]:bg-white data-[state=active]:text-[var(--shariti-orange)]"><KeyRound className="mr-1.5 h-4 w-4" />API & Jetons</TabsTrigger>
          </TabsList>

          <TabsContent value="journee"><JourneeTab /></TabsContent>
          <TabsContent value="terrain-jour"><TerrainJourTab /></TabsContent>
          <TabsContent value="heures"><HeuresMatrixTab /></TabsContent>
          <TabsContent value="productivite"><ProductiviteTab /></TabsContent>
          <TabsContent value="suivi-quotidien"><SuiviQuotidienTab /></TabsContent>
          <TabsContent value="histo-bs"><HistoBulletinsTab /></TabsContent>
          <TabsContent value="anomalies"><AnomaliesTab /></TabsContent>
          <TabsContent value="confirmations"><ConfirmationsTab /></TabsContent>
          <TabsContent value="responsables"><ResponsablesTab /></TabsContent>
          <TabsContent value="collaborateurs"><CollaborateursTab /></TabsContent>
          <TabsContent value="api"><ApiTab /></TabsContent>
        </Tabs>
      </PageBody>
    </div>
    </SeuilsContext.Provider>
  );
}

// ————————————————————————————————————————————————————————————————
// Onglet PRODUCTIVITÉ (BS/h) : carte des meilleurs collaborateurs & magasins
// ————————————————————————————————————————————————————————————————
const PROD_PERIODS = [
  { key: "this_month", label: "Ce mois-ci" },
  { key: "last_month", label: "Mois dernier" },
  { key: "last_30", label: "30 derniers jours" },
  { key: "this_year", label: "Cette année" },
];
function computeProdRange(key) {
  const now = new Date();
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (key === "last_month") {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: iso(first), to: iso(last) };
  }
  if (key === "last_30") {
    const f = new Date(now); f.setDate(now.getDate() - 29);
    return { from: iso(f), to: iso(now) };
  }
  if (key === "this_year") return { from: `${now.getFullYear()}-01-01`, to: iso(now) };
  return { from: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`, to: iso(now) };
}

function ProductiviteTab() {
  const { seuils, setSeuils } = useContext(SeuilsContext);
  const [periodKey, setPeriodKey] = useState("this_month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailSpot, setDetailSpot] = useState(null);   // magasin ouvert en drill-down
  const [onlyRed, setOnlyRed] = useState(false);         // filtre « sous objectif »
  const [seuilsOpen, setSeuilsOpen] = useState(false);   // dialogue d'édition des seuils

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const { from, to } = computeProdRange(periodKey);
    api.get("/pointage/productivite", { params: { date_from: from, date_to: to, top: 100 } })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [periodKey]);

  const t = data?.totals || {};
  const indep = data?.independants || null;

  // Filtre « seulement les rouges » (sous l'objectif). Le barème dépend du contexte :
  // collaborateurs → leur propre type ; magasins → magasin ; prospecteurs → terrain.
  const collabRows = (data?.by_collaborateur || []).filter((r) => !onlyRed || isRedBsh(r.bs_h, r.kind || "magasin", seuils));
  const magasinRows = (data?.by_magasin || []).filter((r) => !onlyRed || isRedBsh(r.bs_h, "magasin", seuils));
  const terrainCollabRows = (data?.by_terrain_collab || []).filter((r) => !onlyRed || isRedBsh(r.bs_h, "terrain", seuils));

  return (
    <div className="space-y-5" data-testid="productivite-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="mb-1 text-[11px] font-semibold text-muted-ink">Période</div>
          <Select value={periodKey} onValueChange={setPeriodKey}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="prod-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROD_PERIODS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {data && (
          <span className="ml-auto pb-2 text-[11px] text-muted-ink" data-testid="prod-range">
            {data.date_from} → {data.date_to}
          </span>
        )}
        <div className={`flex items-center gap-2 rounded-xl border border-[var(--border-soft)] bg-white px-3 py-1.5 ${data ? "" : "ml-auto"}`}>
          <Filter className={`h-4 w-4 ${onlyRed ? "text-rose-600" : "text-muted-ink"}`} />
          <Label htmlFor="only-red" className="cursor-pointer text-[12px] font-semibold text-ink">Seulement les rouges</Label>
          <Switch id="only-red" checked={onlyRed} onCheckedChange={setOnlyRed} data-testid="prod-only-red" />
        </div>
        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setSeuilsOpen(true)} data-testid="prod-open-seuils">
          <Settings2 className="h-4 w-4" /> Objectifs
        </Button>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-[12px] text-muted-ink" data-testid="prod-hint">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
        <span><b className="text-ink">BS/h</b> = bulletins comptés (source Reporting, <b className="text-ink">tous sauf invalides et annulés</b> → décompte en temps réel) ÷ heures <b className="text-ink">terrain</b> (les heures de <b className="text-ink">formation</b> sont payées mais EXCLUES du ratio). Le ratio ne concerne que les <b className="text-ink">salariés</b> (soumis aux heures), pour ne pas être faussé. La production des <b className="text-[#5D3FD3]">indépendants</b> (non soumis aux heures) est affichée séparément, en volume de bulletins.</span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="BS/h global (salariés)" value={loading ? "…" : fmtH(t.bs_h)} valueClass={loading ? "text-[var(--shariti-orange)]" : bshTone(t.bs_h, "magasin", seuils)} testid="prod-kpi-bsh" />
        <Kpi label="BS comptés (salariés)" value={loading ? "…" : (t.bs_valides ?? 0).toLocaleString("fr-FR")} testid="prod-kpi-bs" />
        <Kpi label="Heures retenues" value={loading ? "…" : fmtH(t.heures)} sub={!loading && t.heures_terrain != null && t.heures != null && t.heures_terrain !== t.heures ? `${fmtH(t.heures_terrain)} terrain` : null} testid="prod-kpi-heures" />
        <Kpi label="BS indépendants" value={loading ? "…" : (t.bs_independants ?? 0).toLocaleString("fr-FR")} tone="violet" testid="prod-kpi-bs-indep" />
      </div>

      {loading ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-80 w-full rounded-2xl" />
          <Skeleton className="h-80 w-full rounded-2xl" />
        </div>
      ) : (
        <>
          {onlyRed && (
            <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] font-semibold text-rose-700" data-testid="prod-only-red-note">
              <Filter className="h-4 w-4" /> Filtre actif : seuls les collaborateurs, magasins et prospecteurs <b>sous l'objectif</b> (rouges) sont affichés.
            </div>
          )}
          <div className="grid gap-4 lg:grid-cols-2">
            <ProdRankCard title="Meilleurs collaborateurs (salariés)" icon={Trophy} entity="Collaborateur"
              rows={collabRows} nameKey="nom" subKey="code_rd" testid="prod-collab" ratioKind="auto" />
            <ProdRankCard title="Meilleurs magasins (salariés)" icon={Store} entity="Magasin"
              rows={magasinRows} nameKey="spot_name" subKey="ville" testid="prod-magasin"
              onRowClick={(r) => setDetailSpot({ spot_id: r.spot_id, spot_name: r.spot_name })} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TerrainRankCard rows={data?.by_terrain || []} totaux={data?.terrain_totaux} />
            <ProdRankCard title="Prospecteurs terrain (porte-à-porte)" icon={Route} entity="Prospecteur"
              rows={terrainCollabRows} nameKey="nom" subKey="code_rd" testid="prod-terrain-collab" ratioKind="terrain" />
          </div>
          <IndependantsCard indep={indep} />
          <MagasinDetailDialog spot={detailSpot} range={computeProdRange(periodKey)} onClose={() => setDetailSpot(null)} />
        </>
      )}
      <SeuilsDialog open={seuilsOpen} onOpenChange={setSeuilsOpen} seuils={seuils} onSaved={setSeuils} />
    </div>
  );
}

// Dialogue d'édition des OBJECTIFS BS/h (admin). Permet de régler les seuils
// vert/orange par contexte (magasin & porte-à-porte) sans repasser par le code.
// Champs inline (pas de sous-composant) pour éviter toute perte de focus à la frappe.
function SeuilsDialog({ open, onOpenChange, seuils, onSaved }) {
  const [form, setForm] = useState(seuils);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(seuils); }, [open, seuils]);

  const upd = (kind, band, val) => setForm((f) => ({ ...f, [kind]: { ...f[kind], [band]: val } }));
  const num = (v) => { const n = parseFloat(String(v).replace(",", ".")); return Number.isNaN(n) ? 0 : n; };

  const save = async () => {
    const payload = {
      magasin: { vert: num(form.magasin.vert), orange: num(form.magasin.orange) },
      terrain: { vert: num(form.terrain.vert), orange: num(form.terrain.orange) },
    };
    if (payload.magasin.orange > payload.magasin.vert || payload.terrain.orange > payload.terrain.vert) {
      toast.error("Le seuil orange doit être inférieur ou égal au seuil vert.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/pointage/seuils-productivite", payload);
      onSaved({ magasin: r.data.magasin, terrain: r.data.terrain });
      toast.success("Objectifs BS/h mis à jour.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="seuils-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-4 w-4 text-[var(--shariti-orange)]" /> Objectifs de productivité (BS/h)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-[12px] text-muted-ink">
            Définissez les seuils de couleur. <span className="font-bold text-emerald-600">Vert</span> = objectif atteint,
            <span className="font-bold text-amber-500"> orange</span> = à surveiller,
            <span className="font-bold text-rose-600"> rouge</span> = sous l'objectif.
          </p>

          <div className="rounded-xl border border-[var(--border-soft)] p-3">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-bold"><Store className="h-4 w-4 text-[var(--shariti-orange)]" /> En magasin</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-muted-ink">Vert si ≥</Label>
                <Input type="number" step="0.01" min="0" value={form.magasin.vert}
                  onChange={(e) => upd("magasin", "vert", e.target.value)} className="mt-1 h-9" data-testid="seuil-magasin-vert" />
              </div>
              <div>
                <Label className="text-[11px] text-muted-ink">Orange si ≥</Label>
                <Input type="number" step="0.01" min="0" value={form.magasin.orange}
                  onChange={(e) => upd("magasin", "orange", e.target.value)} className="mt-1 h-9" data-testid="seuil-magasin-orange" />
              </div>
            </div>
            <p className="mt-2 text-[11px] text-muted-ink">
              <span className="font-bold text-emerald-600">Vert ≥ {num(form.magasin.vert)}</span> ·
              <span className="font-bold text-amber-500"> Orange {num(form.magasin.orange)}–{Math.max(0, num(form.magasin.vert) - 0.01).toFixed(2)}</span> ·
              <span className="font-bold text-rose-600"> Rouge &lt; {num(form.magasin.orange)}</span>
            </p>
          </div>

          <div className="rounded-xl border border-[var(--border-soft)] p-3">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-bold"><Route className="h-4 w-4 text-[#5D3FD3]" /> En porte-à-porte</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-muted-ink">Vert si ≥</Label>
                <Input type="number" step="0.01" min="0" value={form.terrain.vert}
                  onChange={(e) => upd("terrain", "vert", e.target.value)} className="mt-1 h-9" data-testid="seuil-terrain-vert" />
              </div>
              <div>
                <Label className="text-[11px] text-muted-ink">Orange si ≥</Label>
                <Input type="number" step="0.01" min="0" value={form.terrain.orange}
                  onChange={(e) => upd("terrain", "orange", e.target.value)} className="mt-1 h-9" data-testid="seuil-terrain-orange" />
              </div>
            </div>
            <p className="mt-2 text-[11px] text-muted-ink">
              <span className="font-bold text-emerald-600">Vert ≥ {num(form.terrain.vert)}</span> ·
              <span className="font-bold text-amber-500"> Orange {num(form.terrain.orange)}–{Math.max(0, num(form.terrain.vert) - 0.01).toFixed(2)}</span> ·
              <span className="font-bold text-rose-600"> Rouge &lt; {num(form.terrain.orange)}</span>
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="seuils-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="seuils-save">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Carte « Production des indépendants » : volume de bulletins validés par
// indépendant (auto-entrepreneur). Pas de ratio BS/h — ils ne pointent pas
// d'heures — donc ils sont exclus du BS/h ci-dessus pour ne pas le fausser.
function IndependantsCard({ indep }) {
  const rows = indep?.collaborateurs || [];
  return (
    <div className="rounded-2xl border border-[rgba(93,63,211,0.25)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid="prod-indep-card">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-xl bg-[rgba(93,63,211,0.12)] text-[#5D3FD3]"><UserRound className="h-4 w-4" /></div>
        <h3 className="text-sm font-extrabold">Production des indépendants</h3>
        <span className="ml-auto rounded-full bg-[rgba(93,63,211,0.12)] px-2.5 py-0.5 text-[11px] font-bold text-[#5D3FD3]" data-testid="prod-indep-total">
          {(indep?.total_bs ?? 0).toLocaleString("fr-FR")} BS · {indep?.count ?? 0} indép.
        </span>
      </div>
      <p className="mb-3 text-[11px] leading-relaxed text-muted-ink">
        Les indépendants (auto-entrepreneurs) ne pointent pas d'heures : on affiche uniquement leur <b>volume de bulletins validés</b>, sans BS/h. Ils sont exclus du ratio ci-dessus pour ne pas le fausser.
      </p>
      {rows.length === 0 ? (
        <div className="grid place-items-center py-10 text-center text-sm text-muted-ink" data-testid="prod-indep-empty">
          Aucun bulletin d'indépendant sur cette période.
        </div>
      ) : (
        <div className="max-h-[360px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-ink">
                <th className="py-1.5 pr-2 font-semibold">#</th>
                <th className="py-1.5 pr-2 font-semibold">Indépendant</th>
                <th className="py-1.5 text-right font-semibold">BS comptés</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.rd_id || i} className="border-t border-[var(--border-soft)]" data-testid={`prod-indep-row-${i}`}>
                  <td className="py-2 pr-2">
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-extrabold ${i === 0 ? "bg-[rgba(93,63,211,0.2)] text-[#5D3FD3]" : i < 3 ? "bg-[var(--surface-2)] text-ink" : "text-muted-ink"}`}>{i + 1}</span>
                  </td>
                  <td className="py-2 pr-2">
                    <div className="break-words font-semibold">{r.nom}</div>
                    {r.code_rd ? <div className="text-[11px] text-muted-ink">{r.code_rd}</div> : null}
                  </td>
                  <td className="py-2 text-right tabular-nums font-extrabold text-[#5D3FD3]">{(r.bs_valides ?? 0).toLocaleString("fr-FR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProdRankCard({ title, icon: Icon, entity, rows, nameKey, subKey, testid, onRowClick, ratioKind = "magasin" }) {
  const { seuils } = useContext(SeuilsContext);
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid={`${testid}-card`}>
      <div className="mb-3 flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-xl bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"><Icon className="h-4 w-4" /></div>
        <h3 className="text-sm font-extrabold">{title}</h3>
        <span className="ml-auto text-[11px] text-muted-ink">Classé par BS/h</span>
      </div>
      {rows.length === 0 ? (
        <div className="grid place-items-center py-12 text-center text-sm text-muted-ink" data-testid={`${testid}-empty`}>
          Aucune donnée sur cette période.
        </div>
      ) : (
        <div className="max-h-[440px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-ink">
                <th className="py-1.5 pr-2 font-semibold">#</th>
                <th className="py-1.5 pr-2 font-semibold">{entity}</th>
                <th className="py-1.5 pr-2 text-right font-semibold">Heures</th>
                <th className="py-1.5 pr-2 text-right font-semibold">BS</th>
                <th className="py-1.5 text-right font-semibold">BS/h</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.rd_id || r.spot_id || i}
                  className={`border-t border-[var(--border-soft)] ${onRowClick ? "cursor-pointer transition-colors hover:bg-[var(--surface-2)]" : ""}`}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  data-testid={`${testid}-row-${i}`}>
                  <td className="py-2 pr-2">
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-extrabold ${
                      i === 0 ? "bg-[rgba(238,114,46,0.2)] text-[var(--shariti-orange)]"
                        : i < 3 ? "bg-[var(--surface-2)] text-ink" : "text-muted-ink"}`}>{i + 1}</span>
                  </td>
                  <td className="py-2 pr-2">
                    <div className="flex items-center gap-1">
                      <div className="break-words font-semibold">{r[nameKey]}</div>
                      {onRowClick && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-ink" />}
                    </div>
                    {r[subKey] ? <div className="text-[11px] text-muted-ink">{r[subKey]}</div> : null}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums">{fmtH(r.heures)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums font-semibold">{(r.bs_valides ?? 0).toLocaleString("fr-FR")}</td>
                  <td className={`py-2 text-right tabular-nums font-extrabold ${bshTone(r.bs_h, ratioKind === "auto" ? (r.kind || "magasin") : ratioKind, seuils)}`}>
                    {r.bs_h === null || r.bs_h === undefined ? "—" : fmtH(r.bs_h)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// Carte « Terrain — porte-à-porte » : classement EN VOLUME de bulletins par ville /
// zone (le PAP n'est pas rattaché à un magasin ⇒ pas de ratio BS/h). Salariés et
// indépendants sont comptés séparément.
function TerrainRankCard({ rows, totaux }) {
  const list = rows || [];
  const t = totaux || {};
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid="prod-terrain-card">
      <div className="mb-3 flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-xl bg-[rgba(93,63,211,0.12)] text-[#5D3FD3]"><Route className="h-4 w-4" /></div>
        <div>
          <h3 className="text-sm font-extrabold">Terrain — porte-à-porte</h3>
          <p className="text-[11px] text-muted-ink">
            {(t.villes || 0).toLocaleString("fr-FR")} ville(s) · {(t.bs_total || 0).toLocaleString("fr-FR")} bulletin(s)
          </p>
        </div>
        <span className="ml-auto text-[11px] text-muted-ink">Classé par volume</span>
      </div>
      {list.length === 0 ? (
        <div className="grid place-items-center py-12 text-center text-sm text-muted-ink" data-testid="prod-terrain-empty">
          Aucun bulletin en porte-à-porte sur cette période.
        </div>
      ) : (
        <div className="max-h-[440px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-ink">
                <th className="py-1.5 pr-2 font-semibold">#</th>
                <th className="py-1.5 pr-2 font-semibold">Ville / Zone</th>
                <th className="py-1.5 pr-2 text-right font-semibold">Salariés</th>
                <th className="py-1.5 pr-2 text-right font-semibold">Indép.</th>
                <th className="py-1.5 text-right font-semibold">Total BS</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r, i) => (
                <tr key={`${r.ville}-${i}`} className="border-t border-[var(--border-soft)]" data-testid={`prod-terrain-row-${i}`}>
                  <td className="py-2 pr-2">
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-extrabold ${
                      i === 0 ? "bg-[rgba(93,63,211,0.2)] text-[#5D3FD3]" : i < 3 ? "bg-[var(--surface-2)] text-ink" : "text-muted-ink"}`}>{i + 1}</span>
                  </td>
                  <td className="py-2 pr-2">
                    <div className="flex items-center gap-1.5">
                      <span className="break-words font-semibold">{r.ville}</span>
                      {r.zone ? <Badge variant="secondary" className="border border-[var(--border-soft)] bg-[var(--surface-2)] px-1.5 py-0 text-[10px] font-bold text-muted-ink">Zone {r.zone}</Badge> : null}
                    </div>
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums font-semibold">{(r.bs_valides ?? 0).toLocaleString("fr-FR")}</td>
                  <td className="py-2 pr-2 text-right tabular-nums font-semibold text-[#5D3FD3]">{(r.bs_independants ?? 0).toLocaleString("fr-FR")}</td>
                  <td className="py-2 text-right tabular-nums font-extrabold">{(r.bs_total ?? 0).toLocaleString("fr-FR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Drill-down d'un MAGASIN : qui a produit / travaillé (salariés vs indépendants),
// pour comprendre un ratio « — » ou « 0,00 » d'un coup d'œil.
function MagasinDetailDialog({ spot, range, onClose }) {
  const { seuils } = useContext(SeuilsContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!spot) { setData(null); return undefined; }
    let cancel = false;
    setLoading(true);
    api.get("/pointage/productivite/magasin-detail", { params: { spot_id: spot.spot_id, date_from: range.from, date_to: range.to } })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [spot, range.from, range.to]);

  const rows = data?.collaborateurs || [];
  const sal = rows.filter((r) => r.population === "salarie");
  const ind = rows.filter((r) => r.population === "independant");
  const rz = data?.resume || {};
  const aucunBsSalarie = !loading && (rz.bs_salaries || 0) === 0;

  return (
    <Dialog open={!!spot} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="magasin-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
            <span className="break-words">{spot?.spot_name}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2 py-2">
            <Skeleton className="h-6 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-ink">Heures salariés</div>
                <div className="text-base font-extrabold tabular-nums" data-testid="magdet-heures">{fmtH(rz.heures_salaries)}</div>
              </div>
              <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-ink">BS salariés</div>
                <div className="text-base font-extrabold tabular-nums text-[var(--shariti-orange)]" data-testid="magdet-bs-sal">{(rz.bs_salaries ?? 0).toLocaleString("fr-FR")}</div>
              </div>
              <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-ink">BS indép.</div>
                <div className="text-base font-extrabold tabular-nums text-[#5D3FD3]" data-testid="magdet-bs-indep">{(rz.bs_independants ?? 0).toLocaleString("fr-FR")}</div>
              </div>
            </div>

            {aucunBsSalarie && (
              <div className="flex items-start gap-2 rounded-xl border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.06)] p-3 text-[12px] text-ink" data-testid="magdet-note">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                <span>
                  {(rz.bs_independants || 0) > 0
                    ? <>Aucun bulletin <b>salarié</b> ici : la production est le fait des <b className="text-[#5D3FD3]">indépendants</b> (non comptés dans le ratio). D'où le « — ».</>
                    : (rz.heures_salaries || 0) > 0
                      ? <>Des heures salariées ont été pointées mais <b>aucun bulletin</b> n'a été produit sur ce magasin sur la période.</>
                      : <>Aucune heure ni bulletin salarié sur ce magasin pour la période.</>}
                </span>
              </div>
            )}

            <div className="max-h-[46vh] space-y-4 overflow-y-auto">
              <div>
                <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
                  <Users className="h-3.5 w-3.5" /> Salariés ({sal.length})
                </div>
                {sal.length === 0 ? (
                  <p className="px-1 text-[12px] text-muted-ink">Aucun salarié n'a pointé d'heures sur ce magasin.</p>
                ) : (
                  <table className="w-full text-sm" data-testid="magdet-sal-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wide text-muted-ink">
                        <th className="py-1 pr-2 font-semibold">Collaborateur</th>
                        <th className="py-1 pr-2 text-right font-semibold">Heures</th>
                        <th className="py-1 pr-2 text-right font-semibold">BS</th>
                        <th className="py-1 text-right font-semibold">BS/h</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sal.map((c, i) => (
                        <tr key={c.rd_id || i} className="border-t border-[var(--border-soft)]" data-testid={`magdet-sal-row-${i}`}>
                          <td className="py-1.5 pr-2">
                            <div className="font-semibold">{c.nom}</div>
                            {c.code_rd ? <div className="text-[11px] text-muted-ink">{c.code_rd}</div> : null}
                          </td>
                          <td className="py-1.5 pr-2 text-right tabular-nums">{fmtH(c.heures)}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums font-semibold">{(c.bs_valides ?? 0).toLocaleString("fr-FR")}</td>
                          <td className={`py-1.5 text-right tabular-nums font-extrabold ${bshTone(c.bs_h, "magasin", seuils)}`}>{c.bs_h === null || c.bs_h === undefined ? "—" : fmtH(c.bs_h)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {ind.length > 0 && (
                <div>
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#5D3FD3]">
                    <UserRound className="h-3.5 w-3.5" /> Indépendants ({ind.length}) — hors ratio
                  </div>
                  <table className="w-full text-sm" data-testid="magdet-indep-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wide text-muted-ink">
                        <th className="py-1 pr-2 font-semibold">Collaborateur</th>
                        <th className="py-1 text-right font-semibold">BS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ind.map((c, i) => (
                        <tr key={c.rd_id || i} className="border-t border-[var(--border-soft)]" data-testid={`magdet-indep-row-${i}`}>
                          <td className="py-1.5 pr-2">
                            <div className="font-semibold">{c.nom}</div>
                            {c.code_rd ? <div className="text-[11px] text-muted-ink">{c.code_rd}</div> : null}
                          </td>
                          <td className="py-1.5 text-right tabular-nums font-extrabold text-[#5D3FD3]">{(c.bs_valides ?? 0).toLocaleString("fr-FR")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* --------------------------------------------- Pilotage du jour (taux brut coloré) */
// Pastille de ZONE couleur (vert / orange / rouge) alignée sur le barème de pilotage
// par canal (Sites privés / Rue / Porte-à-porte), indépendants et welcome call.
const ZONE_PILL = {
  vert: "border-[rgba(31,157,85,0.35)] bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]",
  orange: "border-[rgba(178,106,0,0.35)] bg-[rgba(238,114,46,0.12)] text-[#B26A00]",
  rouge: "border-[rgba(155,34,38,0.35)] bg-[rgba(214,69,69,0.10)] text-[#9B2226]",
};
// Point « feu tricolore » plein (indicateur visuel compact).
const FEU_DOT = { vert: "bg-[#1F9D55]", orange: "bg-[#EE722E]", rouge: "bg-[#D64545]" };
function FeuDot({ zone, className = "h-3 w-3" }) {
  if (!zone || !FEU_DOT[zone]) return <span className="text-muted-ink">—</span>;
  const lbl = zone === "vert" ? "Objectif atteint" : zone === "orange" ? "Vigilance" : "Sous l'objectif";
  return <span className={`inline-block shrink-0 rounded-full ${className} ${FEU_DOT[zone]}`} title={lbl} aria-label={lbl} />;
}
// Tags de canal fin (couleur dédiée par canal).
const CANAL_TAG = {
  sites_prives: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]",
  rue_fixe: "bg-[rgba(45,114,178,0.14)] text-[#1E5F83]",
  rue_itin: "bg-[rgba(93,63,211,0.12)] text-[#5D3FD3]",
  pap: "bg-[rgba(238,114,46,0.16)] text-[#B26A00]",
};
const CANAL_SHORT = { sites_prives: "SP", rue_fixe: "Rue fixe", rue_itin: "Rue itin.", pap: "PAP" };
// Badges NOUVELLES RECRUES : Nouveau (10 premiers jours) / À recadrer (5 j en rouge) /
// Décision fin d'essai (10 j en orange ou rouge).
const RECRUE_BADGE = {
  nouveau: { label: "Nouveau", cls: "border-[rgba(45,114,178,0.35)] bg-[rgba(45,114,178,0.12)] text-[#1E5F83]", icon: Sparkles },
  recadrer: { label: "À recadrer", cls: ZONE_PILL.orange, icon: AlertTriangle },
  decision: { label: "Décision fin d'essai", cls: ZONE_PILL.rouge, icon: Flag },
};
function RecrueBadge({ badge }) {
  const m = RECRUE_BADGE[badge];
  if (!m) return null;
  const Ic = m.icon;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded border px-1 py-0.5 text-[9px] font-bold uppercase ${m.cls}`} data-testid={`recrue-badge-${badge}`}>
      <Ic className="h-2.5 w-2.5" />{m.label}
    </span>
  );
}

const fmtTaux = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2).replace(".", ","));
const fmtInt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("fr-FR"));
const fmtObj = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 1 }));
const _iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Dialogue de réglage des OBJECTIFS DE PILOTAGE : repère d'équipe, objectifs de volume
// par canal, seuils de taux brut par canal, indépendants et welcome call. Admin
// uniquement. Réutilise l'endpoint /pointage/seuils-productivite (champ « pilotage »).
function SuiviObjectifsDialog({ open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [base, setBase] = useState(null); // magasin / terrain / objectif_bs_jour (conservés tels quels)
  const [pil, setPil] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/pointage/seuils-productivite");
        if (cancel) return;
        setBase({ magasin: data.magasin, terrain: data.terrain, objectif_bs_jour: data.objectif_bs_jour });
        setPil(JSON.parse(JSON.stringify(data.pilotage || {})));
      } catch { toast.error("Chargement des objectifs impossible"); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [open]);

  const setBrut = (grp, field, val) => setPil((p) => ({ ...p, brut: { ...(p.brut || {}), [grp]: { ...((p.brut || {})[grp] || {}), [field]: val } } }));
  const setFlatPair = (grp, field, val) => setPil((p) => ({ ...p, [grp]: { ...(p[grp] || {}), [field]: val } }));
  const setObjCanal = (key, val) => setPil((p) => ({ ...p, obj_canal: { ...(p.obj_canal || {}), [key]: val } }));

  const numPair = (o) => ({ vert: Number(o?.vert) || 0, orange: Number(o?.orange) || 0 });

  const save = async () => {
    if (!base || !pil) return;
    setSaving(true);
    try {
      await api.put("/pointage/seuils-productivite", {
        magasin: { vert: Number(base.magasin?.vert) || 0, orange: Number(base.magasin?.orange) || 0 },
        terrain: { vert: Number(base.terrain?.vert) || 0, orange: Number(base.terrain?.orange) || 0 },
        objectif_bs_jour: base.objectif_bs_jour,
        pilotage: {
          brut: {
            sites_prives: numPair(pil.brut?.sites_prives),
            rue: numPair(pil.brut?.rue),
            pap: numPair(pil.brut?.pap),
          },
          obj_canal: {
            sites_prives: Number(pil.obj_canal?.sites_prives) || 0,
            rue: Number(pil.obj_canal?.rue) || 0,
            pap: Number(pil.obj_canal?.pap) || 0,
          },
          indep: numPair(pil.indep),
          wc: numPair(pil.wc),
          repere_bs_jour: Math.max(1, parseInt(pil.repere_bs_jour, 10) || 70),
        },
      });
      toast.success("Objectifs de pilotage enregistrés");
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error("Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  const Num = ({ value, onChange, step = "0.01", min = "0", testid }) => (
    <Input type="number" step={step} min={min} value={value ?? ""} data-testid={testid}
           onChange={(e) => onChange(e.target.value)} className="h-9 w-20 text-right tabular-nums" />
  );
  const PairRow = ({ label, pair, onVert, onOrange, vTid, oTid }) => (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[13px] text-[#181818]">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold text-[#0F5A2F]">Vert ≥</span>
        <Num value={pair?.vert} onChange={onVert} testid={vTid} />
        <span className="text-[11px] font-semibold text-[#B26A00]">Orange ≥</span>
        <Num value={pair?.orange} onChange={onOrange} testid={oTid} />
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="suivi-objectifs-dialog">
        <DialogHeader><DialogTitle>Objectifs de pilotage</DialogTitle></DialogHeader>
        {loading || !pil ? (
          <div className="space-y-2 py-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-xl" />)}</div>
        ) : (
          <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
            {/* Repère équipe */}
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Repère d'équipe</div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[13px] text-[#181818]">Repère bulletins bruts / jour</span>
                <Input type="number" min="1" value={pil.repere_bs_jour ?? 70} data-testid="suivi-obj-repere"
                       onChange={(e) => setPil((p) => ({ ...p, repere_bs_jour: e.target.value }))} className="h-9 w-24 text-right" />
              </div>
            </div>
            {/* Objectif de volume par canal (BS bruts / jour / présent) */}
            <div className="rounded-xl border border-[var(--border-soft)] p-3">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Objectif de volume par canal (BS bruts / jour / présent)</div>
              <div className="space-y-2">
                {[["sites_prives", "Sites privés"], ["rue", "Rue (fixe + itinérance)"], ["pap", "Porte-à-porte"]].map(([k, lbl]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <span className="text-[13px] text-[#181818]">{lbl}</span>
                    <Num value={pil.obj_canal?.[k]} onChange={(v) => setObjCanal(k, v)} step="0.1" testid={`suivi-obj-canal-${k}`} />
                  </div>
                ))}
              </div>
            </div>
            {/* Seuils de taux brut par canal */}
            <div className="rounded-xl border border-[var(--border-soft)] p-3">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Taux brut (BS bruts / h terrain)</div>
              <div className="space-y-2">
                <PairRow label="Sites privés" pair={pil.brut?.sites_prives}
                         onVert={(v) => setBrut("sites_prives", "vert", v)} onOrange={(v) => setBrut("sites_prives", "orange", v)}
                         vTid="suivi-brut-sp-vert" oTid="suivi-brut-sp-orange" />
                <PairRow label="Rue (fixe + itin.)" pair={pil.brut?.rue}
                         onVert={(v) => setBrut("rue", "vert", v)} onOrange={(v) => setBrut("rue", "orange", v)}
                         vTid="suivi-brut-rue-vert" oTid="suivi-brut-rue-orange" />
                <PairRow label="Porte-à-porte" pair={pil.brut?.pap}
                         onVert={(v) => setBrut("pap", "vert", v)} onOrange={(v) => setBrut("pap", "orange", v)}
                         vTid="suivi-brut-pap-vert" oTid="suivi-brut-pap-orange" />
              </div>
            </div>
            {/* Indépendants (volume) */}
            <div className="rounded-xl border border-[var(--border-soft)] p-3">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Indépendants (BS bruts / jour)</div>
              <PairRow label="Volume / jour" pair={pil.indep}
                       onVert={(v) => setFlatPair("indep", "vert", v)} onOrange={(v) => setFlatPair("indep", "orange", v)}
                       vTid="suivi-indep-vert" oTid="suivi-indep-orange" />
            </div>
            {/* Annulations Welcome Call (plus bas = mieux) */}
            <div className="rounded-xl border border-[var(--border-soft)] p-3">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Annulations welcome call (%) — plus bas = mieux</div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] text-[#181818]">Taux d'annulation</span>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-[#0F5A2F]">Vert ≤</span>
                  <Num value={pil.wc?.vert} onChange={(v) => setFlatPair("wc", "vert", v)} step="0.5" testid="suivi-wc-vert" />
                  <span className="text-[11px] font-semibold text-[#B26A00]">Orange ≤</span>
                  <Num value={pil.wc?.orange} onChange={(v) => setFlatPair("wc", "orange", v)} step="0.5" testid="suivi-wc-orange" />
                </div>
              </div>
            </div>
            <p className="text-[12px] text-muted-ink">Au-delà du seuil orange → rouge. Les deux canaux « Rue » partagent le même barème.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="suivi-obj-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="suivi-obj-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialogue EXPORT EXCEL : choisit la période (défaut : 1er janvier → veille) et
// l'option « Détail par recruteur », puis télécharge le .xlsx auto-rempli.
function SuiviExportDialog({ open, onOpenChange }) {
  const yStart = `${new Date().getFullYear()}-01-01`;
  const veille = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return _iso(d); })();
  const [from, setFrom] = useState(yStart);
  const [to, setTo] = useState(veille);
  const [recruteurs, setRecruteurs] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setFrom(yStart); setTo(veille); setRecruteurs(false); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    if (from && to && from > to) { toast.error("La date de début doit précéder la date de fin"); return; }
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      if (recruteurs) params.set("include_recruteurs", "true");
      const res = await api.get(`/pointage/suivi-quotidien/export?${params.toString()}`, { responseType: "blob" });
      let fn = "SHARITI_Suivi_Production.xlsx";
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m) fn = m[1];
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fn; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
      onOpenChange(false);
    } catch { toast.error("Export impossible"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="suivi-export-dialog">
        <DialogHeader><DialogTitle>Télécharger le suivi (Excel)</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-[13px] text-muted-ink">
            Génère un fichier <strong>.xlsx</strong> propre et rempli automatiquement (onglets Cibles, Jour,
            Semaine, Mois, Année). Aucune saisie nécessaire.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="exp-from" className="text-[12px] font-semibold text-muted-ink">Du</Label>
              <Input id="exp-from" type="date" value={from} max={to || undefined}
                     onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="suivi-export-from" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="exp-to" className="text-[12px] font-semibold text-muted-ink">Au</Label>
              <Input id="exp-to" type="date" value={to} min={from || undefined}
                     onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="suivi-export-to" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2.5">
            <div className="pr-3">
              <div className="text-[13px] font-semibold text-[#181818]">Détail par recruteur</div>
              <div className="text-[11px] text-muted-ink">Ajoute un onglet avec une ligne par recruteur et par jour</div>
            </div>
            <Switch checked={recruteurs} onCheckedChange={setRecruteurs} data-testid="suivi-export-recruteurs" />
          </div>
          <p className="text-[11px] text-muted-ink">Par défaut : du 1ᵉʳ janvier de l'année en cours à hier.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="suivi-export-cancel">Annuler</Button>
          <Button onClick={download} disabled={busy} data-testid="suivi-export-download">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="mr-1.5 h-4 w-4" />Télécharger</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Badge de FRAÎCHEUR de la synchronisation des bulletins (source Reporting). Affiché sur le
// Pilotage du jour : VERT si une synchro a réussi récemment, ROUGE si aucune depuis plus de
// 15 min (la remontée des BS est alors en retard). Masqué si la source n'est pas configurée.
// Se rafraîchit tout seul chaque minute.
function SyncFreshnessBadge() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    let alive = true;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/bulletins/sync/reporting/status");
        if (alive) setSt(data);
      } catch { /* silencieux : le badge disparaît simplement */ }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 60000); // rafraîchit chaque minute
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (!st || !st.configured) return null;
  const stale = !!st.sync_stale;
  const mins = st.minutes_since_success;
  const label = stale
    ? (mins == null ? "Aucune synchro réussie" : `Pas de synchro depuis ${Math.round(mins)} min`)
    : (mins == null ? "Synchro active"
       : (mins < 1 ? "Synchro à jour · à l'instant" : `Synchro à jour · il y a ${Math.round(mins)} min`));
  const cls = stale
    ? "border-[rgba(185,28,28,0.35)] bg-[rgba(185,28,28,0.08)] text-[#B91C1C]"
    : "border-[rgba(15,90,47,0.3)] bg-[rgba(31,157,85,0.10)] text-[#0F5A2F]";
  const Icon = stale ? AlertTriangle : CheckCircle2;
  return (
    <div
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold ${cls}`}
      title={stale
        ? "La remontée automatique des bulletins semble en retard. Ouvrez « Bulletins & RD » pour vérifier/relancer la synchronisation."
        : "La synchronisation automatique des bulletins fonctionne."}
      data-testid="sync-freshness-badge"
      data-stale={stale ? "true" : "false"}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
  );
}

// Onglet PILOTAGE DU JOUR : tableau de bord quotidien (présence + taux brut coloré par
// canal fin). Navigation Jour / Cumul (semaine, mois), filtre 4 canaux, objectif
// dynamique vs repère 70, sous-totaux par canal, badges recrues et arrêts récents.
function SuiviQuotidienTab() {
  const [mode, setMode] = useState("jour");
  const [date, setDate] = useState(todayISO());
  const [cumulGran, setCumulGran] = useState("month"); // month | year | last7
  const [cumulOffset, setCumulOffset] = useState(0);    // 0 = période courante, -1 = précédente…
  const [canal, setCanal] = useState("tous");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calOpen, setCalOpen] = useState(false);
  const [objOpen, setObjOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [showArrets, setShowArrets] = useState(true);
  // --- Tri & filtres du tableau « Pilotage du jour » (client-side, sur les lignes chargées) ---
  const [sortKey, setSortKey] = useState(null);       // nom | heures_terrain | bs_bruts | taux_brut | wc_rate
  const [sortDir, setSortDir] = useState("desc");      // desc | asc
  const [canalSel, setCanalSel] = useState([]);        // [] = tous
  const [presenceSel, setPresenceSel] = useState("all"); // all | present | absent
  const [collabQuery, setCollabQuery] = useState("");
  const [collabSel, setCollabSel] = useState([]);      // rd_id ([] = tous)
  const [chefSel, setChefSel] = useState([]);          // chef_id ([] = tous)
  const toggleSort = (k) => { if (sortKey === k) setSortDir((d) => (d === "desc" ? "asc" : "desc")); else { setSortKey(k); setSortDir("desc"); } };
  const toggleInArr = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const resetFilters = () => { setCanalSel([]); setPresenceSel("all"); setCollabQuery(""); setCollabSel([]); setChefSel([]); };

  // Plage du mode Cumul : Mois / Année (navigables) ou 7 derniers jours.
  const cumulInfo = useMemo(() => {
    const now = new Date();
    const t = todayISO();
    if (cumulGran === "last7") {
      const d = new Date(); d.setDate(d.getDate() - 6);
      return { from: _iso(d), to: t, label: "7 derniers jours", canForward: false };
    }
    if (cumulGran === "year") {
      const y = now.getFullYear() + cumulOffset;
      const to = y === now.getFullYear() ? t : `${y}-12-31`;
      return { from: `${y}-01-01`, to, label: String(y), canForward: cumulOffset < 0 };
    }
    // Mois
    const base = new Date(now.getFullYear(), now.getMonth() + cumulOffset, 1);
    const y = base.getFullYear(), m = base.getMonth();
    const isCurrent = y === now.getFullYear() && m === now.getMonth();
    const to = isCurrent ? t : _iso(new Date(y, m + 1, 0));
    const label = base.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    return { from: _iso(new Date(y, m, 1)), to, label: label.charAt(0).toUpperCase() + label.slice(1), canForward: cumulOffset < 0 };
  }, [cumulGran, cumulOffset]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      if (mode === "cumul") { url = `/pointage/suivi-quotidien?mode=cumul&date_from=${cumulInfo.from}&date_to=${cumulInfo.to}&canal=${canal}`; }
      else url = `/pointage/suivi-quotidien?mode=jour&date=${date}&canal=${canal}`;
      const { data: res } = await api.get(url);
      setData(res);
    } catch { toast.error("Impossible de charger le pilotage du jour"); setData(null); }
    finally { setLoading(false); }
  }, [mode, date, canal, cumulInfo]);

  useEffect(() => { load(); }, [load]);

  const shiftDay = (delta) => { const d = new Date(date + "T12:00:00"); d.setDate(d.getDate() + delta); setDate(_iso(d)); };
  const isFuture = date >= todayISO();

  const s = data?.summary;
  const rows = data?.rows || [];
  const canaux = data?.canaux || [];
  const arrets = data?.arrets || [];
  const pct = s ? Math.min(100, Math.max(0, s.pct || 0)) : 0;
  const objTone = s?.feu === "vert" ? "text-emerald-600" : (s?.feu === "orange" ? "text-amber-500" : "text-rose-600");
  const barTone = s?.feu === "vert" ? "bg-emerald-500" : (s?.feu === "orange" ? "bg-amber-400" : "bg-rose-400");
  // BS / recruteur / JOUR = bulletins réalisés ÷ jours-recruteurs (somme des jours de
  // présence de chaque recruteur sur la période). En mode « jour », jours-recruteurs =
  // nombre de présents → c'est bien la moyenne du jour. En mode « cumul », on obtient une
  // MOYENNE QUOTIDIENNE stable (≈ 2–3), au lieu d'un total cumulé par recruteur.
  const perRec = (bs, recJours) => (recJours > 0 ? (Number(bs || 0) / recJours).toFixed(1).replace(".", ",") : "—");

  // Canaux présents dans les lignes (pour le filtre d'en-tête « Canal »).
  const canauxInRows = [];
  { const seen = new Set(); rows.forEach((r) => { if (r.canal && !seen.has(r.canal)) { seen.add(r.canal); canauxInRows.push({ key: r.canal, label: CANAL_SHORT[r.canal] || r.canal_label || r.canal }); } }); }
  // Responsables d'équipe présents dans les lignes (filtre « par responsable d'équipe »).
  const chefsInRows = [];
  { const seen = new Set(); rows.forEach((r) => { if (r.chef_id && !seen.has(r.chef_id)) { seen.add(r.chef_id); chefsInRows.push({ id: r.chef_id, nom: r.chef_nom || "—" }); } }); }
  chefsInRows.sort((a, b) => (a.nom || "").localeCompare(b.nom || ""));

  const sortVal = (r, key) => {
    if (key === "nom") return (r.nom || "").toLowerCase();
    if (key === "heures_terrain") return r.population === "independant" ? -1 : (r.heures_terrain ?? -1);
    if (key === "bs_bruts") return r.bs_bruts ?? -1;
    if (key === "taux_brut") return (r.taux_brut ?? r.bs_jour ?? -1);
    if (key === "wc_rate") return (r.wc_rate ?? -1);
    return 0;
  };
  let displayRows = rows.filter((r) => {
    if (canalSel.length && !canalSel.includes(r.canal)) return false;
    if (presenceSel === "present" && r.statut_presence !== "present") return false;
    if (presenceSel === "formation" && r.statut_presence !== "formation") return false;
    if (presenceSel === "absent" && (r.statut_presence ? r.statut_presence !== "absent" : r.present)) return false;
    if (chefSel.length && !chefSel.includes(r.chef_id)) return false;
    if (collabSel.length && !collabSel.includes(r.rd_id)) return false;
    if (collabQuery.trim()) {
      const q = collabQuery.trim().toLowerCase();
      if (!`${r.nom || ""} ${r.code_rd || ""}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  if (sortKey) {
    displayRows = [...displayRows].sort((a, b) => {
      const va = sortVal(a, sortKey), vb = sortVal(b, sortKey);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }
  const filtersActive = !!(canalSel.length || presenceSel !== "all" || chefSel.length || collabSel.length || collabQuery.trim());

  // Bouton d'en-tête triable : libellé + flèche de sens de tri.
  const SortBtn = ({ label, k, align }) => (
    <button type="button" onClick={() => toggleSort(k)} data-testid={`suivi-sort-${k}`}
      className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide transition-colors hover:text-[var(--shariti-orange)] ${align === "right" ? "flex-row-reverse" : ""} ${sortKey === k ? "text-[var(--shariti-orange)]" : ""}`}>
      <span>{label}</span>
      {sortKey === k
        ? (sortDir === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)
        : <ArrowDown className="h-3 w-3 opacity-25" />}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="suivi-quotidien-tab">
      {/* Bandeau explicatif + badge de fraîcheur de la synchro */}
      <div className="flex items-start gap-2 rounded-2xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-4 py-3">
        <Gauge className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
        <p className="flex-1 text-[13px] leading-snug text-[#181818]">
          <strong>Pilotage du jour</strong> : qui est <strong>présent</strong>, combien de <strong>bulletins bruts</strong>{" "}
          (tous statuts) et leur <strong>taux brut</strong> (BS bruts ÷ heures terrain), coloré selon l'objectif de chaque{" "}
          <strong>canal</strong> (Sites privés, Rue fixe, Rue itinérance, Porte-à-porte). L'objectif du jour s'ajuste{" "}
          <strong>automatiquement</strong> aux présents. Les indépendants sont comptés en volume.
        </p>
        <SyncFreshnessBadge />
      </div>

      {/* Barre d'outils */}
      <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {/* Bascule Jour / Cumul */}
          <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1">
            {[["jour", "Jour", CalendarDays], ["cumul", "Cumul", CalendarRange]].map(([m, lbl, Ic]) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`suivi-mode-${m}`}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors ${mode === m ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <Ic className="h-4 w-4" />{lbl}
              </button>
            ))}
          </div>
          {/* Navigation date (mode Jour) */}
          {mode === "jour" ? (
            <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1">
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => shiftDay(-1)} data-testid="suivi-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" className="h-8 min-w-0 justify-center gap-2 px-2 font-semibold capitalize" data-testid="suivi-date-input">
                    <CalendarDays className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                    <span className="truncate">{frenchDateFull(date)}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" locale={fr} weekStartsOn={1}
                    selected={date ? new Date(date + "T00:00:00") : undefined}
                    defaultMonth={date ? new Date(date + "T00:00:00") : undefined}
                    onSelect={(d) => { if (d) { setDate(_iso(d)); setCalOpen(false); } }} />
                </PopoverContent>
              </Popover>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => shiftDay(1)} disabled={isFuture} data-testid="suivi-next-day"><ChevronRight className="h-4 w-4" /></Button>
              <Button variant="ghost" className="h-8 shrink-0 px-2 text-xs font-semibold" onClick={() => setDate(todayISO())} data-testid="suivi-today">Auj.</Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {/* Granularité du cumul : Mois / Année / 7 derniers jours */}
              <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1">
                {[["month", "Mois"], ["year", "Année"], ["last7", "7 j"]].map(([g, lbl]) => (
                  <button key={g} onClick={() => { setCumulGran(g); setCumulOffset(0); }} data-testid={`suivi-gran-${g}`}
                    className={`rounded-md px-2.5 py-1.5 text-[13px] font-semibold transition-colors ${cumulGran === g ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              {/* Navigation (Mois / Année) */}
              {cumulGran !== "last7" && (
                <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setCumulOffset((o) => o - 1)} data-testid="suivi-cumul-prev"><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="min-w-[110px] px-1 text-center text-[13px] font-semibold capitalize" data-testid="suivi-cumul-label">{cumulInfo.label}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => cumulInfo.canForward && setCumulOffset((o) => o + 1)} disabled={!cumulInfo.canForward} data-testid="suivi-cumul-next"><ChevronRight className="h-4 w-4" /></Button>
                  {cumulOffset !== 0 && <Button variant="ghost" className="h-8 shrink-0 px-2 text-xs font-semibold" onClick={() => setCumulOffset(0)} data-testid="suivi-cumul-today">Auj.</Button>}
                </div>
              )}
            </div>
          )}
          {/* Filtre canal (4 canaux fins) */}
          <Select value={canal} onValueChange={setCanal}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="suivi-canal"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tous" data-testid="suivi-canal-tous">Tous canaux</SelectItem>
              <SelectItem value="sites_prives" data-testid="suivi-canal-sites_prives">Sites privés</SelectItem>
              <SelectItem value="rue_fixe" data-testid="suivi-canal-rue_fixe">Rue fixe</SelectItem>
              <SelectItem value="rue_itin" data-testid="suivi-canal-rue_itin">Rue itinérance</SelectItem>
              <SelectItem value="pap" data-testid="suivi-canal-pap">Porte-à-porte</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-9" onClick={() => setExportOpen(true)} data-testid="suivi-export-btn"><Download className="mr-1.5 h-4 w-4" />Télécharger le suivi (Excel)</Button>
          <Button variant="outline" className="h-9" onClick={() => setObjOpen(true)} data-testid="suivi-objectifs-btn"><Target className="mr-1.5 h-4 w-4" />Objectifs</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={load} data-testid="suivi-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Synthèse */}
      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}</div>
      ) : s && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {/* Objectif dynamique + feu global */}
          <div className="col-span-2 rounded-2xl border border-[var(--border-soft)] bg-white px-4 py-3 shadow-[var(--shadow-sm)]" data-testid="suivi-kpi-objectif">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-ink">{mode === "cumul" ? "Réalisé vs objectif (période)" : "Réalisé vs objectif du jour"}</div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-soft)] px-2 py-0.5 text-[10px] font-bold">
                <FeuDot zone={s.feu} className="h-2.5 w-2.5" />{s.pct != null ? `${s.pct}%` : "—"}
              </span>
            </div>
            <div className="mt-0.5 flex items-baseline gap-1.5">
              <span className={`text-2xl font-bold tabular-nums ${objTone}`} data-testid="suivi-bs-total">{fmtInt(s.bs_bruts_total)}</span>
              <span className="text-sm font-medium text-muted-ink">/ objectif {fmtObj(s.objectif_jour)}</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
              <div className={`h-full rounded-full ${barTone}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-ink">
              <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(238,114,46,0.10)] px-2 py-0.5 text-[12px] font-bold text-[var(--shariti-orange)]" data-testid="suivi-perrec-global">
                {perRec(s.bs_bruts_total, s.rec_jours_total)} BS / recruteur / jour
              </span>
              <span data-testid="suivi-repere">Repère équipe {mode === "cumul" ? `${fmtInt(s.repere_total)} (${s.repere_bs_jour}/j × ${s.nb_jours} j)` : `${s.repere_bs_jour}/jour`}</span>
            </div>
          </div>
          {/* Présents */}
          <div className="rounded-2xl border border-[var(--border-soft)] bg-white px-4 py-3 shadow-[var(--shadow-sm)]" data-testid="suivi-kpi-presents">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-ink">Présents</div>
            <div className="mt-0.5 text-2xl font-bold tabular-nums text-[#181818]">{s.presents_count}</div>
            <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-ink">
              <span className="inline-flex items-center gap-1"><FeuDot zone="vert" className="h-2 w-2" />{s.verts}</span>
              <span className="inline-flex items-center gap-1"><FeuDot zone="orange" className="h-2 w-2" />{s.oranges}</span>
              <span className="inline-flex items-center gap-1"><FeuDot zone="rouge" className="h-2 w-2" />{s.rouges}</span>
              {s.formation_count > 0 && (
                <span className="inline-flex items-center gap-1 font-semibold text-[#0F6E6E]" title="En formation (hors terrain)"><GraduationCap className="h-3 w-3" />{s.formation_count} en formation</span>
              )}
            </div>
          </div>
          {/* Taux brut global (salariés) */}
          <div className="rounded-2xl border border-[var(--border-soft)] bg-white px-4 py-3 shadow-[var(--shadow-sm)]" data-testid="suivi-kpi-taux">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-ink">Taux brut global</div>
            <div className="mt-0.5 text-2xl font-bold tabular-nums text-[#181818]" data-testid="suivi-taux-global">{fmtTaux(s.taux_brut_global)}</div>
            <div className="mt-1 text-[11px] text-muted-ink">
              {fmtH(s.heures_terrain_total)} h terrain · salariés
              {s.heures_formation_total > 0 && <span className="text-[#0F6E6E]"> · {fmtH(s.heures_formation_total)} h formation (exclues)</span>}
            </div>
          </div>
        </div>
      )}

      {/* Sous-totaux par canal */}
      {!loading && canaux.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="suivi-canaux">
          {canaux.map((c) => (
            <div key={c.key} className="rounded-2xl border border-[var(--border-soft)] bg-white px-4 py-3 shadow-[var(--shadow-sm)]" data-testid={`suivi-canal-card-${c.key}`}>
              <div className="flex items-center justify-between">
                <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${CANAL_TAG[c.key] || "bg-slate-100 text-slate-500"}`}>{c.label}</span>
                <FeuDot zone={c.zone} />
              </div>
              <div className="mt-1.5 flex items-baseline gap-1.5">
                <span className="text-xl font-bold tabular-nums text-[var(--shariti-orange)]">{fmtInt(c.bs_bruts)}</span>
                <span className="text-[11px] text-muted-ink">BS · obj {fmtObj(c.objectif)}</span>
              </div>
              <div className="mt-0.5 text-[12px] font-bold text-[var(--shariti-orange)]" data-testid={`suivi-perrec-${c.key}`}>
                {perRec(c.bs_bruts, c.rec_jours)} BS / recruteur / jour
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-muted-ink">
                <span>{c.presents} présent{c.presents > 1 ? "s" : ""}</span>
                {c.en_formation > 0 && <span className="inline-flex items-center gap-1 font-semibold text-[#0F6E6E]" title="En formation (hors terrain)"><GraduationCap className="h-3 w-3" />{c.en_formation}</span>}
                <span>{fmtH(c.heures_terrain)} h{c.heures_formation > 0 ? <span className="text-[#0F6E6E]"> +{fmtH(c.heures_formation)} form.</span> : null}</span>
                <span className={`inline-block rounded border px-1.5 py-0.5 font-bold tabular-nums ${ZONE_PILL[c.zone] || "text-muted-ink"}`}>Taux {fmtTaux(c.taux_brut)}</span>
                {c.pct != null && <span>{c.pct}%</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tableau collaborateurs */}
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-11 w-full" />)}</div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="suivi-empty">
          <Gauge className="h-8 w-8 text-muted-ink" />
          <p className="text-sm font-medium text-[#181818]">Aucune activité {mode === "cumul" ? "sur la période" : "ce jour"}</p>
          <p className="text-xs text-muted-ink">Personne n'a produit de bulletin ni pointé d'heures{canal !== "tous" ? " pour ce canal" : ""}.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtersActive && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[12px]" data-testid="suivi-filter-bar">
              <Filter className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
              <span className="font-semibold text-[#181818]">{displayRows.length}</span>
              <span className="text-muted-ink">collaborateur{displayRows.length > 1 ? "s" : ""} affiché{displayRows.length > 1 ? "s" : ""} sur {rows.length}</span>
              <button type="button" onClick={resetFilters} className="ml-auto inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white px-2 py-1 font-semibold text-muted-ink hover:text-[var(--shariti-orange)]" data-testid="suivi-reset-filters">
                <XCircle className="h-3.5 w-3.5" />Réinitialiser les filtres
              </button>
            </div>
          )}
          <div className="overflow-x-auto rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]" data-testid="suivi-table-wrap">
          <table className="w-full border-collapse text-sm" data-testid="suivi-table">
            <thead>
              <tr className="bg-[var(--surface-2)] text-[11px] font-bold uppercase tracking-wide text-muted-ink">
                <th className="px-3 py-2 text-left">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="suivi-filter-collab"
                        className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide transition-colors hover:text-[var(--shariti-orange)] ${(collabSel.length || chefSel.length || collabQuery.trim()) ? "text-[var(--shariti-orange)]" : ""}`}>
                        Collaborateur <Filter className="h-3 w-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72 p-3" data-testid="suivi-filter-collab-pop">
                      <div className="relative mb-2">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
                        <Input value={collabQuery} onChange={(e) => setCollabQuery(e.target.value)} placeholder="Rechercher un collaborateur…" className="h-9 pl-8" data-testid="suivi-collab-search" />
                      </div>
                      <div className="flex items-center justify-between pb-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
                        <span>Collaborateurs</span>
                        <button type="button" className="text-[var(--shariti-orange)] hover:underline" onClick={() => toggleSort("nom")} data-testid="suivi-sort-nom">{sortKey === "nom" && sortDir === "asc" ? "Z → A" : "A → Z"}</button>
                      </div>
                      <div className="max-h-44 space-y-0.5 overflow-y-auto border-t border-[var(--border-soft)] pt-1">
                        {rows.filter((r) => !collabQuery.trim() || `${r.nom || ""} ${r.code_rd || ""}`.toLowerCase().includes(collabQuery.trim().toLowerCase())).map((r) => (
                          <label key={r.rd_id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[12px] hover:bg-[var(--surface-2)]" data-testid={`suivi-collab-opt-${r.rd_id}`}>
                            <Checkbox checked={collabSel.includes(r.rd_id)} onCheckedChange={() => toggleInArr(collabSel, setCollabSel, r.rd_id)} />
                            <span className="truncate normal-case">{r.nom}</span>
                          </label>
                        ))}
                      </div>
                      {chefsInRows.length > 0 && (
                        <div className="mt-2 border-t border-[var(--border-soft)] pt-2">
                          <div className="pb-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink">Par responsable d'équipe</div>
                          <div className="max-h-28 space-y-0.5 overflow-y-auto">
                            {chefsInRows.map((c) => (
                              <label key={c.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[12px] hover:bg-[var(--surface-2)]" data-testid={`suivi-chef-opt-${c.id}`}>
                                <Checkbox checked={chefSel.includes(c.id)} onCheckedChange={() => toggleInArr(chefSel, setChefSel, c.id)} />
                                <span className="truncate normal-case">{c.nom}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      {(collabSel.length || chefSel.length || collabQuery.trim()) ? (
                        <button type="button" onClick={() => { setCollabSel([]); setChefSel([]); setCollabQuery(""); }} className="mt-2 w-full rounded-lg border border-[var(--border-soft)] py-1.5 text-[12px] font-semibold text-muted-ink hover:bg-[var(--surface-2)]" data-testid="suivi-collab-reset">Effacer</button>
                      ) : null}
                    </PopoverContent>
                  </Popover>
                </th>
                <th className="px-3 py-2 text-left">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="suivi-filter-canal"
                        className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide transition-colors hover:text-[var(--shariti-orange)] ${canalSel.length ? "text-[var(--shariti-orange)]" : ""}`}>
                        Canal <Filter className="h-3 w-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-48 p-2" data-testid="suivi-filter-canal-pop">
                      {canauxInRows.map((c) => (
                        <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[12px] hover:bg-[var(--surface-2)]" data-testid={`suivi-canal-opt-${c.key}`}>
                          <Checkbox checked={canalSel.includes(c.key)} onCheckedChange={() => toggleInArr(canalSel, setCanalSel, c.key)} />
                          <span className="normal-case">{c.label}</span>
                        </label>
                      ))}
                      {canalSel.length ? (<button type="button" onClick={() => setCanalSel([])} className="mt-1 w-full rounded-lg border border-[var(--border-soft)] py-1 text-[12px] font-semibold text-muted-ink hover:bg-[var(--surface-2)]" data-testid="suivi-canal-reset">Tous les canaux</button>) : null}
                    </PopoverContent>
                  </Popover>
                </th>
                <th className="px-3 py-2 text-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="suivi-filter-presence"
                        className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide transition-colors hover:text-[var(--shariti-orange)] ${presenceSel !== "all" ? "text-[var(--shariti-orange)]" : ""}`}>
                        Présence <Filter className="h-3 w-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="center" className="w-40 p-2" data-testid="suivi-filter-presence-pop">
                      {[["all", "Tous"], ["present", "Présents"], ["formation", "En formation"], ["absent", "Absents"]].map(([v, lbl]) => (
                        <button key={v} type="button" onClick={() => setPresenceSel(v)} data-testid={`suivi-presence-opt-${v}`}
                          className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] normal-case hover:bg-[var(--surface-2)] ${presenceSel === v ? "font-bold text-[var(--shariti-orange)]" : ""}`}>
                          {presenceSel === v ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="h-3.5 w-3.5" />}{lbl}
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                </th>
                <th className="px-3 py-2 text-right"><SortBtn label="Heures" k="heures_terrain" align="right" /></th>
                <th className="px-3 py-2 text-right"><SortBtn label="BS bruts" k="bs_bruts" align="right" /></th>
                <th className="px-3 py-2 text-right"><SortBtn label="Taux brut" k="taux_brut" align="right" /></th>
                <th className="px-3 py-2 text-right"><SortBtn label="Annul. WC" k="wc_rate" align="right" /></th>
                <th className="px-3 py-2 text-center">Feu</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 && (
                <tr data-testid="suivi-filter-empty">
                  <td colSpan={8} className="px-3 py-8 text-center text-[13px] text-muted-ink">
                    Aucun collaborateur ne correspond aux filtres.{" "}
                    <button type="button" onClick={resetFilters} className="font-semibold text-[var(--shariti-orange)] hover:underline" data-testid="suivi-filter-empty-reset">Réinitialiser</button>
                  </td>
                </tr>
              )}
              {displayRows.map((r) => (
                <tr key={r.rd_id} className="border-t border-[var(--border-soft)] hover:bg-[var(--surface-2)]" data-testid={`suivi-row-${r.rd_id}`}>
                  <td className="px-3 py-2">
                    <div className="truncate text-[13px] font-semibold text-[#181818]" title={r.nom}>{r.nom}</div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {r.code_rd && <span className="text-[10px] font-medium text-muted-ink">{r.code_rd}</span>}
                      <span className={`rounded px-1 py-0.5 text-[9px] font-bold uppercase ${r.population === "independant" ? "bg-[#EDE7FB] text-[#5D3FD3]" : "bg-slate-100 text-slate-500"}`}>{r.population === "independant" ? "Indép." : "Salarié"}</span>
                      <RecrueBadge badge={r.badge} />
                      {!r.actif && <span className="rounded px-1 py-0.5 text-[9px] font-bold uppercase bg-[rgba(155,34,38,0.10)] text-[#9B2226]">Parti</span>}
                      {r.badge && r.date_entree && <span className="text-[9px] font-medium text-muted-ink" title="Date d'entrée · jours terrain">entré le {frenchDateFull(r.date_entree)}{r.jours_terrain != null ? ` · ${r.jours_terrain} j terrain` : ""}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${CANAL_TAG[r.canal] || "bg-slate-100 text-slate-500"}`} title={r.canal_label}>{CANAL_SHORT[r.canal] || r.canal_label}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {r.statut_presence === "present" || (r.present && !r.statut_presence)
                      ? <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(31,157,85,0.35)] bg-[rgba(31,157,85,0.10)] px-2 py-0.5 text-[11px] font-bold text-[#0F5A2F]"><CheckCircle2 className="h-3 w-3" />Présent</span>
                      : r.statut_presence === "formation"
                        ? <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(15,110,110,0.35)] bg-[rgba(15,110,110,0.10)] px-2 py-0.5 text-[11px] font-bold text-[#0F6E6E]" title="En formation toute la journée (aucune heure terrain)"><GraduationCap className="h-3 w-3" />En formation</span>
                        : <span className="inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-bold text-muted-ink"><Minus className="h-3 w-3" />Absent</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-[#181818]">
                    {r.population === "independant" ? "—" : fmtH(r.heures_terrain)}
                    {r.population !== "independant" && r.heures_formation > 0 && (
                      <div className="text-[10px] font-semibold text-[#0F6E6E]" title="Heures de formation (hors terrain)">
                        +{fmtH(r.heures_formation)} formation
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-[var(--shariti-orange)]">{r.bs_bruts}</td>
                  <td className="px-3 py-2 text-right">
                    {r.population === "independant"
                      ? <span className={`inline-block rounded-md border px-2 py-0.5 text-[12px] font-bold tabular-nums ${ZONE_PILL[r.feu] || ""}`} data-testid={`suivi-taux-${r.rd_id}`} title="Indépendant : volume BS bruts / jour">{r.bs_jour != null ? `${fmtTaux(r.bs_jour)}/j` : "volume"}</span>
                      : (r.taux_brut === null
                        ? <span className="text-xs font-medium text-muted-ink">—</span>
                        : <span className={`inline-block rounded-md border px-2 py-0.5 text-[13px] font-bold tabular-nums ${ZONE_PILL[r.feu] || ""}`} data-testid={`suivi-taux-${r.rd_id}`}>{fmtTaux(r.taux_brut)}</span>)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.wc_rate === null || r.wc_rate === undefined
                      ? <span className="text-xs text-muted-ink">—</span>
                      : <span className={`inline-block rounded border px-1.5 py-0.5 text-[11px] font-bold tabular-nums ${ZONE_PILL[r.wc_zone] || "text-muted-ink"}`} title={`${r.wc_annul} annulation(s)`}>{r.wc_annul} · {fmtTaux(r.wc_rate)}%</span>}
                  </td>
                  <td className="px-3 py-2 text-center"><FeuDot zone={r.feu} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Arrêts récents (archivés ou inactifs 7 j+ sur 30 j) */}
      {!loading && arrets.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]" data-testid="suivi-arrets">
          <button onClick={() => setShowArrets((v) => !v)} className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-[var(--surface-2)]" data-testid="suivi-arrets-toggle">
            <span className="inline-flex items-center gap-2 text-[13px] font-bold text-[#181818]">
              <Archive className="h-4 w-4 text-[#9B2226]" />Arrêts récents
              <span className="rounded-full bg-[rgba(155,34,38,0.10)] px-2 py-0.5 text-[11px] font-bold text-[#9B2226]">{arrets.length}</span>
            </span>
            {showArrets ? <ChevronRight className="h-4 w-4 rotate-90 text-muted-ink" /> : <ChevronRight className="h-4 w-4 text-muted-ink" />}
          </button>
          {showArrets && (
            <div className="border-t border-[var(--border-soft)]">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[var(--surface-2)] text-[11px] font-bold uppercase tracking-wide text-muted-ink">
                    <th className="px-3 py-2 text-left">Collaborateur</th>
                    <th className="px-3 py-2 text-left">Motif</th>
                    <th className="px-3 py-2 text-left">Dernière activité</th>
                    <th className="px-3 py-2 text-right">Jours sans</th>
                  </tr>
                </thead>
                <tbody>
                  {arrets.map((a) => (
                    <tr key={a.rd_id} className="border-t border-[var(--border-soft)] hover:bg-[var(--surface-2)]" data-testid={`suivi-arret-${a.rd_id}`}>
                      <td className="px-3 py-2">
                        <div className="truncate text-[13px] font-semibold text-[#181818]" title={a.nom}>{a.nom}</div>
                        <div className="flex items-center gap-1.5">
                          {a.code_rd && <span className="text-[10px] font-medium text-muted-ink">{a.code_rd}</span>}
                          <span className={`rounded px-1 py-0.5 text-[9px] font-bold uppercase ${a.population === "independant" ? "bg-[#EDE7FB] text-[#5D3FD3]" : "bg-slate-100 text-slate-500"}`}>{a.population === "independant" ? "Indép." : "Salarié"}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {a.raison === "archive"
                          ? <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(155,34,38,0.35)] bg-[rgba(214,69,69,0.10)] px-2 py-0.5 text-[11px] font-bold text-[#9B2226]"><Archive className="h-3 w-3" />Archivé{a.date_arret ? ` le ${frenchDateFull(a.date_arret)}` : ""}</span>
                          : <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(178,106,0,0.35)] bg-[rgba(238,114,46,0.12)] px-2 py-0.5 text-[11px] font-bold text-[#B26A00]"><Clock className="h-3 w-3" />Inactif</span>}
                      </td>
                      <td className="px-3 py-2 text-[13px] text-[#181818]">{a.derniere_activite ? frenchDateFull(a.derniere_activite) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#9B2226]">{a.jours_sans != null ? `${a.jours_sans} j` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <SuiviObjectifsDialog open={objOpen} onOpenChange={setObjOpen} onSaved={() => load()} />
      <SuiviExportDialog open={exportOpen} onOpenChange={setExportOpen} />
    </div>
  );
}


/* --------------------------------------------- Historique BS (mensuel) */
// Pastille de TENDANCE (hausse / baisse / stable) d'un collaborateur, calculée
// côté serveur en comparant ses derniers mois actifs aux précédents.
function TrendBadge({ t }) {
  if (t === "up")
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600" data-testid="histo-trend-up">
        <ArrowUp className="h-3 w-3" />Hausse
      </span>
    );
  if (t === "down")
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-600" data-testid="histo-trend-down">
        <ArrowDown className="h-3 w-3" />Baisse
      </span>
    );
  if (t === "stable")
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-bold text-muted-ink" data-testid="histo-trend-stable">
        <ArrowRight className="h-3 w-3" />Stable
      </span>
    );
  return null;
}

// Matrice « depuis les débuts » : 1 ligne par médiateur (salariés + indépendants),
// 1 colonne par mois, cumul des bulletins VALIDES. Heatmap (intensité = volume du
// mois rapporté au pic personnel) + tendance, pour repérer d'un coup d'œil les
// hausses, baisses ou la stabilité. Scroll horizontal fluide (mois récents à droite).
function HistoBulletinsTab() {
  // Filtres mémorisés (favori) : population (refetch), + actifs/canal/responsable (client).
  const saved = (() => { try { return JSON.parse(localStorage.getItem("histo-bs-filters") || "{}"); } catch { return {}; } })();
  const [pop, setPop] = useState(saved.pop || "tous");
  const [onlyActive, setOnlyActive] = useState(saved.onlyActive !== undefined ? saved.onlyActive : true);
  const [canal, setCanal] = useState(saved.canal || "tous");
  const [chef, setChef] = useState(saved.chef || "tous");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  // Mémorisation du dernier filtre choisi (favori conservé d'une visite à l'autre).
  useEffect(() => {
    try { localStorage.setItem("histo-bs-filters", JSON.stringify({ pop, onlyActive, canal, chef })); } catch { /* noop */ }
  }, [pop, onlyActive, canal, chef]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/pointage/bulletins-mensuel?population=${pop}`);
      setData(res);
    } catch (e) {
      toast.error("Impossible de charger l'historique des bulletins");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [pop]);

  useEffect(() => { load(); }, [load]);

  // Au chargement : on positionne le scroll TOUT À DROITE (mois les plus récents).
  useEffect(() => {
    if (!loading && scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
      });
    }
  }, [loading, data, pop]);

  const scrollStep = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  const months = data?.months || [];
  const monthTotals = data?.month_totals || {};
  const todayMois = monthISO();

  const monthHead = (mo) => {
    const [y, m] = mo.split("-").map(Number);
    const label = new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "short" }).replace(".", "");
    return { label: label.charAt(0).toUpperCase() + label.slice(1), y, isJan: m === 1 };
  };

  const nf = (v) => Number(v || 0).toLocaleString("fr-FR");

  // Filtres combinés : recherche (nom/code) + actifs + canal + responsable.
  const q = query.trim().toLowerCase();
  const chefs = data?.chefs || [];
  const rows = (data?.rows || []).filter((r) => {
    if (q && !((r.nom || "").toLowerCase().includes(q) || (r.code_rd || "").toLowerCase().includes(q))) return false;
    if (onlyActive && !r.actif) return false;
    if (canal !== "tous" && r.canal !== canal) return false;
    if (chef !== "tous" && r.chef_equipe_id !== chef) return false;
    return true;
  });

  // Heatmap : couleur de fond orange marque, alpha proportionnel au volume du mois
  // rapporté au PIC personnel de la ligne (0 → cellule vide/discrète).
  const cellStyle = (n, peak) => {
    if (!n) return undefined;
    const ratio = peak > 0 ? n / peak : 0;
    const alpha = (0.12 + ratio * 0.72).toFixed(2);
    return { backgroundColor: `rgba(238,114,46,${alpha})`, color: ratio > 0.55 ? "#fff" : "#7A2E00" };
  };

  const period = months.length ? `${monthHead(months[0]).label} ${monthHead(months[0]).y} → ${monthHead(months[months.length - 1]).label} ${monthHead(months[months.length - 1]).y}` : "—";

  return (
    <div className="space-y-4" data-testid="histo-bs-tab">
      {/* Bandeau explicatif */}
      <div className="flex items-start gap-2 rounded-2xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-4 py-3">
        <BarChart3 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
        <p className="text-[13px] leading-snug text-[#181818]">
          Nombre de bulletins <strong>valides</strong> par médiateur et par mois, <strong>depuis ses débuts</strong>{" "}
          (salariés + indépendants, hors invalides/annulés). La couleur indique l'intensité du mois par rapport
          au pic de la personne — repérez d'un coup d'œil les hausses, baisses ou la stabilité.
        </p>
      </div>

      {/* Barre d'outils : population + recherche + scroll */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Select value={pop} onValueChange={setPop}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="histo-pop-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tous" data-testid="histo-pop-tous">Tous les médiateurs</SelectItem>
              <SelectItem value="salarie" data-testid="histo-pop-salarie">Salariés</SelectItem>
              <SelectItem value="independant" data-testid="histo-pop-independant">Indépendants</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative w-full max-w-[260px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un médiateur…"
              className="h-9 pl-8"
              data-testid="histo-search"
            />
          </div>
          {/* Canal : porte-à-porte vs site privé */}
          <Select value={canal} onValueChange={setCanal}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="histo-canal-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tous" data-testid="histo-canal-tous">Tous canaux</SelectItem>
              <SelectItem value="pap" data-testid="histo-canal-pap">Porte-à-porte</SelectItem>
              <SelectItem value="prive" data-testid="histo-canal-prive">Site privé (SP)</SelectItem>
            </SelectContent>
          </Select>
          {/* Responsable (chef d'équipe) */}
          {chefs.length > 0 && (
            <Select value={chef} onValueChange={setChef}>
              <SelectTrigger className="h-9 w-[170px]" data-testid="histo-chef-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tous" data-testid="histo-chef-tous">Tous responsables</SelectItem>
                {chefs.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`histo-chef-${c.id}`}>{c.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Actifs uniquement (dans les effectifs) — mémorisé comme favori */}
          <label className="flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-3 text-[13px] font-medium text-[#181818]" data-testid="histo-actifs-toggle-wrap">
            <Switch checked={onlyActive} onCheckedChange={setOnlyActive} data-testid="histo-actifs-toggle" />
            <span>Actifs uniquement</span>
          </label>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1">
          <span className="px-2 text-xs font-medium text-muted-ink">Défiler</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => scrollStep(-1)} data-testid="histo-scroll-left" title="Vers la gauche (mois anciens)"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => scrollStep(1)} data-testid="histo-scroll-right" title="Vers la droite (mois récents)"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* KPIs */}
      {!loading && data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-2xl border border-[var(--border-soft)] bg-white px-4 py-3 shadow-[var(--shadow-sm)]" data-testid="histo-kpi-total">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-ink">Bulletins valides (médiateurs)</div>
            <div className="mt-0.5 text-2xl font-bold tabular-nums text-[var(--shariti-orange)]">{nf(data.grand_total)}</div>
          </div>
          <div className="rounded-2xl border border-[var(--border-soft)] bg-white px-4 py-3 shadow-[var(--shadow-sm)]" data-testid="histo-kpi-collabs">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-ink">Médiateurs</div>
            <div className="mt-0.5 text-2xl font-bold tabular-nums text-[#181818]">{nf(rows.length)}</div>
          </div>
          <div className="col-span-2 rounded-2xl border border-[var(--border-soft)] bg-white px-4 py-3 shadow-[var(--shadow-sm)]" data-testid="histo-kpi-periode">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-ink">Période observée</div>
            <div className="mt-0.5 truncate text-base font-bold capitalize text-[#181818]">{period}</div>
          </div>
        </div>
      )}

      {/* Réconciliation avec EverGiving (source Reporting, tous médiateurs) */}
      {!loading && data?.reconciliation && (
        <div className="rounded-2xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-4 py-3" data-testid="histo-reconciliation">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-ink">Réconciliation EverGiving · source Reporting (tous médiateurs)</div>
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[14px] text-[#181818]">
            <span data-testid="histo-recon-total"><b className="tabular-nums">{nf(data.reconciliation.total_tous_statuts)}</b> au total (tous statuts)</span>
            <span className="rounded-full bg-[rgba(31,157,85,0.14)] px-2 py-0.5 text-[11px] font-bold text-[#0F5A2F]">= total EverGiving</span>
            <span className="text-muted-ink">=</span>
            <span data-testid="histo-recon-valides"><b className="tabular-nums text-[var(--shariti-orange)]">{nf(data.reconciliation.valides)}</b> valides</span>
            <span className="text-muted-ink">+</span>
            <span data-testid="histo-recon-invalides"><b className="tabular-nums text-[#9B2226]">{nf(data.reconciliation.invalides)}</b> invalides/annulés exclus</span>
          </div>
          <div className="mt-1 text-[12px] text-muted-ink">
            La matrice ci-dessous ne compte que les <b>valides</b> (hors invalides/annulés), d'où un total inférieur à EverGiving : c'est normal.
          </div>
        </div>
      )}

      {/* Matrice */}
      {loading ? (
        <div className="space-y-2" data-testid="histo-loading">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : !data || rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="histo-empty">
          <BarChart3 className="h-8 w-8 text-muted-ink" />
          <p className="text-sm font-medium text-[#181818]">Aucun bulletin à afficher</p>
          <p className="text-xs text-muted-ink">{q ? "Aucun médiateur ne correspond à votre recherche." : "Aucune donnée pour ce filtre."}</p>
        </div>
      ) : (
        <div ref={scrollRef} className="relative max-h-[70vh] overflow-auto rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]" data-testid="histo-table-wrap">
          <table className="w-full border-collapse text-sm" data-testid="histo-table">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 min-w-[210px] border-b border-r border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-muted-ink shadow-[6px_0_8px_-6px_rgba(24,24,24,0.16)]">
                  Collaborateur
                </th>
                {months.map((mo) => {
                  const h = monthHead(mo);
                  const isNow = mo === todayMois;
                  return (
                    <th
                      key={mo}
                      className={`sticky top-0 z-20 min-w-[52px] border-b px-1 py-1.5 text-center text-[10px] font-semibold leading-tight ${h.isJan ? "border-l-2 border-l-[var(--border-soft)]" : "border-l border-[var(--border-soft)]"} ${isNow ? "bg-[#FBE0D0] text-[var(--shariti-orange)]" : "bg-[var(--surface-2)] text-[#181818]"}`}
                      data-testid={`histo-month-head-${mo}`}
                    >
                      <div className="capitalize">{h.label}</div>
                      <div className="text-[9px] font-medium text-muted-ink">{h.y}</div>
                    </th>
                  );
                })}
                <th className="sticky right-0 top-0 z-30 w-[76px] min-w-[76px] border-b border-l-2 border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-[var(--shariti-orange)] shadow-[-6px_0_8px_-6px_rgba(24,24,24,0.16)]">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.rd_id} className="group" data-testid={`histo-row-${r.rd_id}`}>
                  <td className="sticky left-0 z-10 min-w-[210px] border-b border-r border-[var(--border-soft)] bg-white px-3 py-2 shadow-[6px_0_8px_-6px_rgba(24,24,24,0.16)] group-hover:bg-[var(--surface-2)]">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-semibold text-[#181818]" title={r.nom} data-testid={`histo-name-${r.rd_id}`}>{r.nom}</div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {r.code_rd && <span className="text-[10px] font-medium text-muted-ink">{r.code_rd}</span>}
                          <span className={`rounded px-1 py-0.5 text-[9px] font-bold uppercase ${r.population === "independant" ? "bg-[#EDE7FB] text-[#5D3FD3]" : "bg-slate-100 text-slate-500"}`}>
                            {r.population === "independant" ? "Indép." : "Salarié"}
                          </span>
                          <span className={`rounded px-1 py-0.5 text-[9px] font-bold uppercase ${r.canal === "pap" ? "bg-[rgba(238,114,46,0.14)] text-[#B26A00]" : "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]"}`}>
                            {r.canal === "pap" ? "PAP" : "SP"}
                          </span>
                          {!r.actif && (
                            <span className="rounded px-1 py-0.5 text-[9px] font-bold uppercase bg-[rgba(155,34,38,0.10)] text-[#9B2226]" data-testid={`histo-parti-${r.rd_id}`}>Parti</span>
                          )}
                        </div>
                      </div>
                      <TrendBadge t={r.trend} />
                    </div>
                  </td>
                  {months.map((mo) => {
                    const n = r.counts[mo] || 0;
                    const h = monthHead(mo);
                    return (
                      <td
                        key={mo}
                        style={cellStyle(n, r.peak)}
                        className={`border-b px-1 py-1.5 text-center text-[12px] font-semibold tabular-nums ${h.isJan ? "border-l-2 border-l-[var(--border-soft)]" : "border-l border-[var(--border-soft)]"} ${n ? "" : "text-[rgba(24,24,24,0.20)]"}`}
                        data-testid={`histo-cell-${r.rd_id}-${mo}`}
                      >
                        {n ? nf(n) : "·"}
                      </td>
                    );
                  })}
                  <td className="sticky right-0 z-10 w-[76px] min-w-[76px] border-b border-l-2 border-[var(--border-soft)] bg-[#FDEEE4] px-2 py-2 text-center font-bold tabular-nums text-[var(--shariti-orange)] shadow-[-6px_0_8px_-6px_rgba(24,24,24,0.16)] group-hover:bg-[#FBE0D0]" data-testid={`histo-total-${r.rd_id}`}>
                    {nf(r.total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="sticky bottom-0 left-0 z-30 border-t-2 border-r border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink shadow-[6px_0_8px_-6px_rgba(24,24,24,0.16)]">
                  Total / mois
                </td>
                {months.map((mo) => {
                  const h = monthHead(mo);
                  return (
                    <td key={mo} className={`sticky bottom-0 z-20 border-t-2 bg-[var(--surface-2)] px-1 py-2 text-center text-[11px] font-bold tabular-nums text-[#181818] ${h.isJan ? "border-l-2 border-l-[var(--border-soft)]" : "border-l border-[var(--border-soft)]"}`} data-testid={`histo-monthtotal-${mo}`}>
                      {nf(monthTotals[mo])}
                    </td>
                  );
                })}
                <td className="sticky bottom-0 right-0 z-30 border-t-2 border-l-2 border-[var(--border-soft)] bg-[var(--shariti-orange)] px-2 py-2 text-center font-bold tabular-nums text-white shadow-[-6px_0_8px_-6px_rgba(24,24,24,0.16)]" data-testid="histo-grandtotal">
                  {nf(data.grand_total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}




/* =============================== TERRAIN DU JOUR ===============================
   Vue « qui est prévu aujourd'hui » : présence AUTOMATIQUE (salariés pointés) et appel
   en 1 clic (tel:). AUCUN statut manuel. Ajout du numéro à la volée s'il manque. */
const PRESENCE_META = {
  present: { label: "Présent", cls: "border-[rgba(31,157,85,0.35)] bg-[rgba(31,157,85,0.10)] text-[#0F5A2F]", icon: CheckCircle2 },
  prevu: { label: "Prévu", cls: "border-[var(--border-soft)] bg-[var(--surface-2)] text-muted-ink", icon: CalendarClock },
  a_appeler: { label: "À appeler", cls: "border-[rgba(178,106,0,0.35)] bg-[rgba(238,114,46,0.12)] text-[#B26A00]", icon: PhoneCall },
};

function PresenceBadge({ presence }) {
  const M = PRESENCE_META[presence] || PRESENCE_META.prevu;
  const Icon = M.icon;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${M.cls}`} data-testid={`presence-badge-${presence}`}>
      <Icon className="h-3 w-3" />{M.label}
    </span>
  );
}

function TerrainPersonRow({ p, onPhoneSaved, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState(p.phone || "");
  const [saving, setSaving] = useState(false);
  const testKey = p.code_rd || p.rd_id;

  const save = async () => {
    if (!phone.trim()) { toast.error("Saisissez un numéro"); return; }
    setSaving(true);
    try {
      await api.put(`/rds/${p.rd_id}`, { phone: phone.trim() });
      toast.success("Numéro enregistré");
      setEditing(false);
      onPhoneSaved && onPhoneSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  const popLabel = p.population === "independant"
    ? "Indépendant"
    : (p.readonly_reason === "pap" ? "Porte-à-porte" : "Salarié");

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 text-sm" data-testid={`terrain-person-${testKey}`}>
      <RecruiterAvatar name={p.rd_name} src={rdPhotoSrc(p)} size={36} testid={`terrain-avatar-${testKey}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="break-words font-semibold text-ink">{p.rd_name}</span>
          <span className="rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-semibold text-muted-ink">{popLabel}</span>
          <PresenceBadge presence={p.presence} />
        </div>
        <div className="mt-0.5 text-[11px] text-muted-ink">
          {p.creneau_debut && p.creneau_fin ? `${p.creneau_debut} – ${p.creneau_fin}` : "Horaire non défini"}
          {p.presence === "present" && p.arrivee_pointee ? ` · pointé à ${p.arrivee_pointee}` : ""}
        </div>
      </div>
      {readOnly ? (
        p.has_phone ? (
          <a href={`tel:+${p.phone_e164}`} data-testid={`terrain-call-${testKey}`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[rgba(31,157,85,0.10)] px-2.5 py-1.5 text-xs font-bold text-[#0F5A2F] transition-colors hover:bg-[rgba(31,157,85,0.18)]">
            <Phone className="h-3.5 w-3.5" />{p.phone}
          </a>
        ) : (
          <span className="text-[11px] italic text-muted-ink" data-testid={`terrain-nophone-${testKey}`}>Numéro non renseigné</span>
        )
      ) : editing ? (
        <div className="flex items-center gap-1.5">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 12 34 56 78"
            className="h-8 w-[150px]" data-testid={`terrain-phone-input-${testKey}`}
            onKeyDown={(e) => e.key === "Enter" && save()} autoFocus />
          <Button size="sm" className="h-8 bg-[var(--shariti-orange)] px-2 hover:bg-[#d9631f]" disabled={saving} onClick={save} data-testid={`terrain-phone-save-${testKey}`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "OK"}
          </Button>
          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setEditing(false); setPhone(p.phone || ""); }}>Annuler</Button>
        </div>
      ) : p.has_phone ? (
        <div className="flex items-center gap-1.5">
          <a href={`tel:+${p.phone_e164}`} data-testid={`terrain-call-${testKey}`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[rgba(31,157,85,0.10)] px-2.5 py-1.5 text-xs font-bold text-[#0F5A2F] transition-colors hover:bg-[rgba(31,157,85,0.18)]">
            <Phone className="h-3.5 w-3.5" />{p.phone}
          </a>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-ink" title="Modifier le numéro"
            onClick={() => setEditing(true)} data-testid={`terrain-phone-edit-${testKey}`}><Pencil className="h-3.5 w-3.5" /></Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="h-8 gap-1.5 border-dashed"
          onClick={() => setEditing(true)} data-testid={`terrain-phone-add-${testKey}`}><Plus className="h-3.5 w-3.5" />Ajouter un numéro</Button>
      )}
    </div>
  );
}

export function TerrainJourTab({ readOnly = false } = {}) {
  const [mode, setMode] = useState("jour");
  const [date, setDate] = useState(todayISO());
  const [dateOpen, setDateOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/pointage/terrain-jour?date=${date}`);
      setData(r.data);
    } catch { toast.error("Chargement du terrain impossible"); }
    finally { setLoading(false); }
  }, [date]);
  useEffect(() => { if (mode === "jour") load(); }, [load, mode]);

  const shiftDay = (delta) => {
    const d = new Date(date + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const s = data?.summary || {};
  const spots = data?.spots || [];

  return (
    <div className="space-y-5" data-testid="terrain-jour-tab">
      <div className="inline-flex w-full items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1 sm:w-auto" data-testid="terrain-mode-toggle">
        {[["jour", "Jour", CalendarClock], ["semaine", "Semaine", CalendarRange]].map(([m, lbl, Ic]) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`terrain-mode-${m}`}
            className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors sm:flex-none ${mode === m ? "bg-[var(--surface-2)] text-[var(--shariti-orange)]" : "text-muted-ink hover:text-ink"}`}>
            <Ic className="h-4 w-4" />{lbl}
          </button>
        ))}
      </div>
      {mode === "semaine" ? (
        <TerrainSemaineView />
      ) : (
      <>
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex w-full items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1 sm:w-auto">
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => shiftDay(-1)} data-testid="terrain-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="h-9 min-w-0 flex-1 justify-center gap-2 px-2 font-semibold capitalize sm:flex-none" data-testid="terrain-date-input">
                <CalendarClock className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                <span className="truncate">{frenchDateFull(date)}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" locale={fr} weekStartsOn={1}
                selected={date ? new Date(date + "T00:00:00") : undefined}
                defaultMonth={date ? new Date(date + "T00:00:00") : undefined}
                onSelect={(d) => { if (d) { const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; setDate(iso); setDateOpen(false); } }} />
            </PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => shiftDay(1)} data-testid="terrain-next-day"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" className="h-9 shrink-0 px-2 text-xs font-semibold" onClick={() => setDate(todayISO())} data-testid="terrain-today">Auj.</Button>
        </div>
        <Button variant="outline" onClick={load} className="h-9 w-full justify-center sm:w-9 sm:px-0" data-testid="terrain-refresh"><RefreshCw className="h-4 w-4" /><span className="ml-1.5 sm:hidden">Actualiser</span></Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Prévus" value={s.total ?? "—"} testid="terrain-kpi-total" />
        <Kpi label="Présents (pointés)" value={s.present ?? "—"} tone="green" testid="terrain-kpi-present" />
        <Kpi label="À appeler" value={s.a_appeler ?? "—"} tone="orange" testid="terrain-kpi-appeler" />
        <Kpi label="Sans numéro" value={s.sans_numero ?? "—"} tone="red" testid="terrain-kpi-sans-numero" />
      </div>

      <p className="text-[12px] text-muted-ink">Présence <b>automatique</b> : un salarié apparaît « Présent » dès qu'il a <b>pointé</b> son arrivée. Les indépendants et le porte-à-porte ne pointent pas — appelez-les pour confirmer leur venue.</p>

      {loading ? (
        <div className="grid gap-4 lg:grid-cols-2">{[0, 1].map((i) => <Skeleton key={i} className="h-48 rounded-[var(--radius)]" />)}</div>
      ) : spots.length === 0 ? (
        <EmptyState icon={CalendarClock} title="Aucun collaborateur prévu ce jour" hint="Choisissez une autre date ou composez l'équipe depuis l'onglet Journée." testid="terrain-empty" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="terrain-spot-grid">
          {spots.map((g, i) => (
            <div key={g.spot_id || `x${i}`} className="rounded-[var(--radius)] border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]" data-testid={`terrain-spot-${g.spot_id || i}`}>
              <div className="flex items-start gap-2 border-b border-[var(--border-soft)] p-3">
                <Store className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                <div className="min-w-0 flex-1">
                  <div className="break-words font-bold text-ink">{g.spot_name}</div>
                  {g.address && <div className="mt-0.5 flex items-start gap-1 break-words text-[11px] text-muted-ink"><MapPin className="mt-0.5 h-3 w-3 shrink-0" />{g.address}</div>}
                </div>
                <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-bold text-muted-ink" title="Collaborateurs prévus">{g.count}</span>
              </div>
              <div className="divide-y divide-[var(--border-soft)]">
                {g.people.map((p) => <TerrainPersonRow key={`${p.rd_id}-${p.creneau_debut}`} p={p} onPhoneSaved={load} readOnly={readOnly} />)}
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}



/* ---------------------------------------------------- Terrain de la semaine */
const PRESENCE_DOT = {
  present: { c: "rgba(31,157,85,0.95)", t: "Présent" },
  prevu: { c: "rgba(24,24,24,0.35)", t: "Prévu" },
  a_appeler: { c: "rgba(224,138,0,0.95)", t: "À appeler" },
};
const MOIS_COURT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

function PresenceDot({ presence }) {
  const m = PRESENCE_DOT[presence] || PRESENCE_DOT.prevu;
  return <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: m.c }} title={m.t} aria-label={m.t} />;
}

// Lundi (ISO) de la semaine contenant `iso`, et décalage de N jours.
const mondayOf = (iso) => {
  const d = new Date(iso + "T12:00:00");
  const dow = (d.getDay() + 6) % 7;   // 0 = lundi
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
};
const addDaysISO = (iso, n) => {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

// Vue SEMAINE de « Terrain du jour » : préparer les équipes sur toute la semaine
// (Lun→Dim) sans consulter jour par jour. Regroupement au choix : par EMPLACEMENT
// (idéal pour composer une équipe par magasin) ou par COLLABORATEUR (sa semaine d'un
// coup d'œil). Grille scrollable horizontalement, 1re colonne figée.
function TerrainSemaineView() {
  const [weekStart, setWeekStart] = useState(mondayOf(todayISO()));
  const [groupBy, setGroupBy] = useState("spot");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/pointage/terrain-semaine?start=${weekStart}`);
      setData(r.data);
    } catch { toast.error("Chargement de la semaine impossible"); }
    finally { setLoading(false); }
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  const days = data?.days || [];
  const items = data?.items || [];
  const s = data?.summary || {};
  const weekLabel = days.length === 7
    ? `${days[0].label} ${days[0].day} ${MOIS_COURT[days[0].month - 1]} – ${days[6].label} ${days[6].day} ${MOIS_COURT[days[6].month - 1]}`
    : "";

  // Regroupement des affectations (par spot ou par personne).
  const map = new Map();
  for (const it of items) {
    const key = groupBy === "spot" ? (it.spot_id || "_sans") : (it.rd_id || "_?");
    if (!map.has(key)) {
      map.set(key, groupBy === "spot"
        ? { key, title: it.spot_name, sub: it.address, byDay: {} }
        : { key, title: it.rd_name, sub: it.code_rd, byDay: {} });
    }
    const g = map.get(key);
    (g.byDay[it.date] = g.byDay[it.date] || []).push(it);
  }
  const groups = [...map.values()].sort((a, b) => (a.title || "").localeCompare(b.title || "", "fr"));

  return (
    <div className="space-y-4" data-testid="terrain-semaine-view">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex w-full items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1 sm:w-auto">
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => setWeekStart(addDaysISO(weekStart, -7))} data-testid="terrain-week-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-0 flex-1 px-2 text-center text-sm font-semibold sm:flex-none" data-testid="terrain-week-label">{weekLabel || "…"}</span>
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => setWeekStart(addDaysISO(weekStart, 7))} data-testid="terrain-week-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" className="h-9 shrink-0 px-2 text-xs font-semibold" onClick={() => setWeekStart(mondayOf(todayISO()))} data-testid="terrain-week-current">Cette semaine</Button>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1" data-testid="terrain-groupby">
            {[["spot", "Par emplacement", Store], ["person", "Par personne", UserRound]].map(([g, l, Ic]) => (
              <button key={g} onClick={() => setGroupBy(g)} data-testid={`terrain-groupby-${g}`}
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${groupBy === g ? "bg-[var(--surface-2)] text-[var(--shariti-orange)]" : "text-muted-ink hover:text-ink"}`}>
                <Ic className="h-3.5 w-3.5" />{l}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={load} className="h-9 w-9 shrink-0 px-0" data-testid="terrain-week-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Créneaux (semaine)" value={s.total ?? "—"} testid="terrain-week-kpi-total" />
        <Kpi label="Présents (pointés)" value={s.present ?? "—"} tone="green" testid="terrain-week-kpi-present" />
        <Kpi label="À appeler" value={s.a_appeler ?? "—"} tone="orange" testid="terrain-week-kpi-appeler" />
        <Kpi label={groupBy === "spot" ? "Emplacements" : "Collaborateurs"}
          value={groupBy === "spot" ? (data?.n_spots ?? "—") : (data?.n_people ?? "—")} testid="terrain-week-kpi-count" />
      </div>

      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-ink">
        <span>Vue semaine pour préparer les équipes.</span>
        <span className="inline-flex items-center gap-1"><PresenceDot presence="present" /> présent (pointé)</span>
        <span className="inline-flex items-center gap-1"><PresenceDot presence="prevu" /> prévu</span>
        <span className="inline-flex items-center gap-1"><PresenceDot presence="a_appeler" /> à appeler</span>
      </p>

      {loading ? (
        <Skeleton className="h-64 w-full rounded-[var(--radius)]" />
      ) : groups.length === 0 ? (
        <EmptyState icon={CalendarRange} title="Aucun créneau cette semaine" hint="Naviguez vers une autre semaine avec les flèches." testid="terrain-week-empty" />
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius)] border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]" data-testid="terrain-week-grid">
          <table className="w-full min-w-[900px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-[var(--border-soft)]">
                <th className="sticky left-0 z-10 bg-white px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-muted-ink">{groupBy === "spot" ? "Emplacement" : "Collaborateur"}</th>
                {days.map((d) => (
                  <th key={d.date} className={`px-2 py-2 text-center text-xs font-bold ${d.weekend ? "text-muted-ink" : "text-ink"} ${d.is_today ? "text-[var(--shariti-orange)]" : ""}`}>
                    <div className="capitalize">{d.label}</div>
                    <div className="text-[11px] font-semibold">{d.day}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.key} className="border-b border-[var(--border-soft)] align-top last:border-0" data-testid={`terrain-week-row-${g.key}`}>
                  <th className="sticky left-0 z-10 bg-white px-3 py-2 text-left align-top">
                    <div className="flex items-start gap-1.5">
                      {groupBy === "spot"
                        ? <Store className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
                        : <UserRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />}
                      <div className="min-w-0 max-w-[220px]">
                        <div className="break-words font-semibold text-ink">{g.title}</div>
                        {g.sub && <div className="mt-0.5 break-words text-[11px] text-muted-ink">{g.sub}</div>}
                      </div>
                    </div>
                  </th>
                  {days.map((d) => {
                    const cell = g.byDay[d.date] || [];
                    return (
                      <td key={d.date} className={`min-w-[108px] px-1.5 py-1.5 align-top ${d.weekend ? "bg-[var(--surface-2)]/30" : ""} ${d.is_today ? "bg-[rgba(238,114,46,0.06)]" : ""}`}>
                        {cell.length === 0 ? (
                          <span className="block text-center text-[13px] text-[rgba(24,24,24,0.18)]">–</span>
                        ) : (
                          <div className="space-y-1">
                            {cell.map((it, i) => (
                              <div key={i} className="flex items-center gap-1 rounded-md bg-[var(--surface-2)]/60 px-1.5 py-1" title={groupBy === "spot" ? it.rd_name : it.spot_name}>
                                <PresenceDot presence={it.presence} />
                                <span className="truncate text-[12px] text-ink">{groupBy === "spot" ? it.rd_name : it.spot_name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ Journée */
function JourneeTab() {
  const [date, setDate] = useState(todayISO());
  const [dateOpen, setDateOpen] = useState(false);
  const [population, setPopulation] = useState("tous");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waText, setWaText] = useState("");
  const [waOpen, setWaOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [retroOpen, setRetroOpen] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [remindBusy, setRemindBusy] = useState(null); // 'arrivee' | 'depart' | null
  const [correctRow, setCorrectRow] = useState(null);
  const [annulRow, setAnnulRow] = useState(null);
  const [reopenRow, setReopenRow] = useState(null);
  const [originalRow, setOriginalRow] = useState(null);
  const [reactiverRow, setReactiverRow] = useState(null);
  const [formationOpen, setFormationOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/pointage/journee?date=${date}&population=${population}`);
      setData(r.data);
    } catch { toast.error("Chargement de la journée impossible"); }
    finally { setLoading(false); }
  }, [date, population]);
  useEffect(() => { load(); }, [load]);

  const shiftDay = (delta) => {
    const d = new Date(date + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const openWa = async () => {
    try {
      const r = await api.get(`/pointage/message-jour?date=${date}`);
      setWaText(r.data.whatsapp_text); setWaOpen(true);
    } catch { toast.error("Génération du message impossible"); }
  };

  const sendReminderTest = async (kind) => {
    setRemindBusy(kind);
    try {
      const { data: r } = await api.post(`/pointage/rappel-${kind}/test`);
      if (r.sent > 0) toast.success(`${r.sent} e-mail(s) de test envoyé(s)`, { description: `Destinataire : ${r.test_email}` });
      else toast.error("Aucun e-mail envoyé", { description: r.skipped === "email_non_configure" ? "SMTP non configuré" : "Vérifiez la configuration e-mail" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setRemindBusy(null); }
  };

  const s = data?.summary || {};
  const p = s.prod || {};
  return (
    <div className="space-y-5" data-testid="journee-tab">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex w-full items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1 sm:w-auto">
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => shiftDay(-1)} data-testid="journee-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="h-9 min-w-0 flex-1 justify-center gap-2 px-2 font-semibold capitalize sm:flex-none" data-testid="journee-date-input">
                <CalendarClock className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                <span className="truncate">{frenchDateFull(date)}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                locale={fr}
                weekStartsOn={1}
                selected={date ? new Date(date + "T00:00:00") : undefined}
                defaultMonth={date ? new Date(date + "T00:00:00") : undefined}
                onSelect={(d) => {
                  if (d) {
                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    setDate(iso);
                    setDateOpen(false);
                  }
                }}
                data-testid="journee-date-calendar"
              />
            </PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => shiftDay(1)} data-testid="journee-next-day"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" className="h-9 shrink-0 px-2 text-xs font-semibold" onClick={() => setDate(todayISO())} data-testid="journee-today">Auj.</Button>
        </div>
        <div className="grid w-full grid-cols-2 items-stretch gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
          <Select value={population} onValueChange={setPopulation}>
            <SelectTrigger className="col-span-2 h-10 w-full min-w-0 sm:col-span-1 sm:h-9 sm:w-[215px]" data-testid="journee-population-filter">
              <Users className="mr-1.5 h-4 w-4 shrink-0 text-muted-ink" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tous" data-testid="journee-pop-tous">Toutes populations</SelectItem>
              <SelectItem value="salarie" data-testid="journee-pop-salarie">Salariés</SelectItem>
              <SelectItem value="independant" data-testid="journee-pop-independant">Indépendants</SelectItem>
              <SelectItem value="pap" data-testid="journee-pop-pap">Porte-à-porte</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={openWa} className="h-10 w-full justify-center sm:h-9 sm:w-auto" data-testid="journee-wa-btn"><MessageSquare className="mr-1.5 h-4 w-4 shrink-0" /><span className="sm:hidden">Message</span><span className="hidden sm:inline">Message du jour</span></Button>
          <Button variant="outline" onClick={() => setRetroOpen(true)} className="h-10 w-full justify-center border-[#5D3FD3] text-[#5D3FD3] hover:bg-[rgba(93,63,211,0.08)] sm:h-9 sm:w-auto" data-testid="journee-retro-btn"><CalendarPlus className="mr-1.5 h-4 w-4 shrink-0" /><span className="sm:hidden">Rétroactif</span><span className="hidden sm:inline">Saisie rétroactive</span></Button>
          <Button variant="outline" onClick={() => setFormationOpen(true)} className="h-10 w-full justify-center border-[#0F6E6E] text-[#0F6E6E] hover:bg-[rgba(15,110,110,0.08)] sm:h-9 sm:w-auto" data-testid="journee-formation-btn"><GraduationCap className="mr-1.5 h-4 w-4 shrink-0" /><span className="sm:hidden">Formation</span><span className="hidden sm:inline">Heures de formation</span></Button>
          <Button variant="outline" onClick={() => setRemindOpen(true)} className="h-10 w-full justify-center sm:h-9 sm:w-auto" data-testid="journee-rappels-btn"><Mail className="mr-1.5 h-4 w-4 shrink-0" /><span className="sm:hidden">Rappels</span><span className="hidden sm:inline">Rappels e-mail</span></Button>
          <Dialog open={remindOpen} onOpenChange={setRemindOpen}>
            <DialogContent className="max-w-md" data-testid="rappels-dialog">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-[var(--shariti-orange)]" />Rappels de pointage par e-mail</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="space-y-1.5 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
                  <div className="flex items-center justify-between gap-2"><span className="text-muted-ink">Expéditeur</span><span className="font-semibold text-ink" data-testid="rappels-sender">admin@shariti.com</span></div>
                  <div className="flex items-center justify-between gap-2"><span className="text-muted-ink">Vous en copie (BCC)</span><span className="font-semibold text-ink" data-testid="rappels-copy">sharitigroup@gmail.com</span></div>
                  <div className="flex items-center justify-between gap-2"><span className="text-muted-ink">Exclus des rappels</span><span className="font-semibold text-ink">Indépendants &amp; Porte-à-porte</span></div>
                </div>
                <p className="text-[12px] text-muted-ink">Envoie <b>2 e-mails de démonstration</b> à <b>sharitigroup@gmail.com</b>. Aucun collaborateur réel n'est notifié, aucun marquage n'est posé (test répétable).</p>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" disabled={!!remindBusy} onClick={() => sendReminderTest("arrivee")} data-testid="rappels-test-arrivee">{remindBusy === "arrivee" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Tester « arrivée »"}</Button>
                  <Button variant="outline" className="flex-1" disabled={!!remindBusy} onClick={() => sendReminderTest("depart")} data-testid="rappels-test-depart">{remindBusy === "depart" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Tester « départ »"}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
            <DialogTrigger asChild>
              <Button className="h-10 w-full justify-center bg-[var(--shariti-orange)] font-semibold hover:bg-[#d9631f] sm:h-9 sm:w-auto" data-testid="journee-assign-btn"><Plus className="mr-1.5 h-4 w-4 shrink-0" /><span className="sm:hidden">Affecter</span><span className="hidden sm:inline">Affecter un collaborateur</span></Button>
            </DialogTrigger>
            <AssignDialog date={date} onDone={() => { setAssignOpen(false); load(); }} />
          </Dialog>
          <Button variant="outline" onClick={load} className="col-span-2 h-10 w-full justify-center sm:h-9 sm:w-9 sm:px-0" data-testid="journee-refresh"><RefreshCw className="h-4 w-4" /><span className="ml-1.5 sm:hidden">Actualiser</span></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-8">
        <Kpi label="Affectés" value={s.affectes ?? "—"} testid="kpi-affectes" />
        <Kpi label="Pointés" value={s.pointes ?? "—"} tone="green" testid="kpi-pointes" />
        <Kpi label="Complets" value={s.complets ?? "—"} tone="green" testid="kpi-complets" />
        <Kpi label="Retards" value={s.retards ?? "—"} tone="red" testid="kpi-retards" />
        <Kpi label="Non dépointés" value={s.non_depointes ?? "—"} tone="orange" testid="kpi-non-depointes" />
        <Kpi label="Except. en attente" value={s.exceptionnels_en_attente ?? "—"} tone="orange" testid="kpi-exceptionnels" />
        <Kpi label="Hors pointage" value={s.hors_pointage ?? "—"} testid="kpi-hors-pointage" />
        <Kpi label="Heures retenues" value={`${fmtH(s.total_heures)} h`} tone="orange" testid="kpi-total-heures" />
      </div>

      {/* [DEMANDE 08/2026] PRODUCTION DU JOUR — répartition des bulletins produits
          (salariés vs indépendants) + taux de productivité des salariés (BS/h).
          Toujours calculée sur l'ENSEMBLE du jour, indépendamment du filtre population
          (c'est une répartition par nature). Cohérent avec l'onglet Productivité. */}
      <div data-testid="journee-production-section">
        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
          <BarChart3 className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
          Production du jour · bulletins
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Kpi label="Total bulletins" value={loading ? "…" : (p.bs_total ?? 0).toLocaleString("fr-FR")} testid="kpi-bs-total" />
          <Kpi label="BS salariés" value={loading ? "…" : (p.bs_salaries ?? 0).toLocaleString("fr-FR")} tone="green" testid="kpi-bs-salaries" />
          <Kpi label="BS indépendants" value={loading ? "…" : (p.bs_independants ?? 0).toLocaleString("fr-FR")} tone="violet" testid="kpi-bs-independants" />
          <Kpi label="Productivité salariés (BS/h)" value={loading ? "…" : fmtH(p.bs_h)} tone="orange" testid="kpi-bs-h" />
        </div>
        <p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-muted-ink" data-testid="journee-production-note">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
          <span>Répartition sur l'ensemble du jour (indépendante du filtre population). <b className="text-ink">BS/h salariés</b> = bulletins comptés ÷ heures <b className="text-ink">terrain</b> (hors formation, même règle que l'onglet Productivité) ; « — » si aucune heure terrain retenue.</span>
        </p>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
      ) : !data?.rows?.length ? (
        <EmptyState icon={CalendarClock} title="Aucune affectation ce jour" hint="Cliquez sur « Affecter un collaborateur » pour composer l'équipe du jour." testid="journee-empty" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]">
          <table className="w-full min-w-[720px] text-sm" data-testid="journee-table">
            <thead>
              <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-2)] text-[10px] uppercase tracking-wide text-muted-ink">
                <th className="px-3 py-2.5 text-left font-bold">Collaborateur</th>
                <th className="px-3 py-2.5 text-left font-bold">Spot</th>
                <th className="px-3 py-2.5 text-left font-bold">Créneau</th>
                <th className="px-3 py-2.5 text-left font-bold">Arrivée / Départ</th>
                <th className="px-3 py-2.5 text-right font-bold">Heures</th>
                <th className="px-3 py-2.5 text-left font-bold">Statut</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.affectation_id} className="border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--surface-2)]" data-testid={`journee-row-${r.code_rd}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-ink">{r.rd_name}
                      {r.population === "independant" && <span className="ml-1 rounded bg-[rgba(93,63,211,0.12)] px-1 text-[9px] font-bold uppercase text-[#5D3FD3]" data-testid={`journee-indep-${r.code_rd}`}>Indé</span>}
                      {r.readonly_reason === "pap" && <span className="ml-1 rounded bg-[rgba(47,111,237,0.12)] px-1 text-[9px] font-bold uppercase text-[#2F6FED]" data-testid={`journee-pap-${r.code_rd}`}>Porte-à-porte</span>}
                      {r.saisie_retroactive && <span className="ml-1 rounded bg-[rgba(93,63,211,0.12)] px-1 text-[9px] font-bold uppercase text-[#5D3FD3]" data-testid={`journee-retro-badge-${r.code_rd}`}>Rétroactif</span>}
                      {r.corrige && <span className="ml-1 rounded bg-[rgba(185,137,0,0.16)] px-1 text-[9px] font-bold uppercase text-[#8a6600]" data-testid={`journee-corrige-badge-${r.code_rd}`}>Corrigé</span>}
                      {r.annule && <span className="ml-1 rounded bg-[var(--surface-2)] px-1 text-[9px] font-bold uppercase text-muted-ink" data-testid={`journee-annule-badge-${r.code_rd}`}>Annulé</span>}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      <CodeBadge code={r.code_rd} />
                      {/* [DEMANDE 08/2026] Nombre de bulletins COMPTÉS produits par ce
                          collaborateur ce jour — détail « personne par personne ». */}
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${(r.bs_jour ?? 0) > 0 ? "bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]" : "bg-[var(--surface-2)] text-muted-ink"}`}
                        title="Bulletins comptés produits ce jour (source Reporting, hors invalides/annulés)"
                        data-testid={`journee-bs-jour-${r.code_rd}`}>
                        {(r.bs_jour ?? 0).toLocaleString("fr-FR")} BS
                      </span>
                      {(r.heures_formation ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold text-[#0F6E6E] bg-[rgba(15,110,110,0.12)]"
                          title="Heures de formation : payées mais exclues du taux BS/h"
                          data-testid={`journee-formation-${r.code_rd}`}>
                          <GraduationCap className="h-3 w-3" />{fmtH(r.heures_formation)} formation
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="max-w-[220px] truncate font-medium text-ink">{r.spot_name}</div>
                    <PositionTag row={r} />
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-muted-ink">{r.creneau_debut}–{r.creneau_fin}</td>
                  <td className={`px-3 py-2.5 tabular-nums ${r.deduit ? "text-[#2F6FED] font-semibold" : "text-ink"}`}>
                    {r.is_readonly_pointage ? <span className="text-muted-ink">—</span> : (
                      <>
                        {r.arrivee_retenue || "—"} → {r.depart_retenu || "—"}
                        {r.flags?.retard && <span className="ml-1 rounded bg-[rgba(214,69,69,0.12)] px-1 text-[10px] font-bold text-[#9B2226]">retard</span>}
                        {r.flags?.non_depointe && <span className="ml-1 rounded bg-[rgba(238,114,46,0.14)] px-1 text-[10px] font-bold text-[#8a4b1f]">non dépointé</span>}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {r.is_readonly_pointage && !r.pap_hours_editable
                      ? <span className="text-muted-ink">—</span>
                      : <HeuresCell row={r} onSaved={load} pap={r.pap_hours_editable} />}
                  </td>
                  <td className="px-3 py-2.5 align-top"><StatutBadge statut={r.statut} /><StatutMeta row={r} /></td>
                  <td className="px-3 py-2.5 text-right">
                    {r.is_readonly_pointage ? (
                      r.pap_hours_editable ? (
                        <span className="inline-flex items-center gap-1 text-[11px] italic text-[#2F6FED]" data-testid={`journee-pap-note-${r.code_rd}`} title="Porte-à-porte : heures saisies manuellement (responsable ou admin) — modifiez-les dans la colonne Heures">
                          <Info className="h-3 w-3" />Heures manuelles
                        </span>
                      ) : (
                        <span className="text-[11px] italic text-muted-ink" data-testid={`journee-readonly-note-${r.code_rd}`} title="Indépendant : non soumis au pointage">
                          Non soumis au pointage
                        </span>
                      )
                    ) : (
                      <JourneeRowActions row={r} onCorrect={() => setCorrectRow(r)} onAnnuler={() => setAnnulRow(r)} onReopen={() => setReopenRow(r)} onOriginal={() => setOriginalRow(r)} onReactiver={() => setReactiverRow(r)} onDeleted={load} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog message WhatsApp */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto overflow-x-hidden" data-testid="wa-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[var(--shariti-orange)]" />Message équipe du jour</DialogTitle></DialogHeader>
          <Textarea value={waText} readOnly className="min-h-[280px] font-mono text-xs" data-testid="wa-textarea" />
          <DialogFooter>
            <Button className="bg-[var(--shariti-orange)] font-semibold hover:bg-[#d9631f]"
              onClick={() => { navigator.clipboard?.writeText(waText); toast.success("Message copié — collez-le dans le groupe WhatsApp."); }}
              data-testid="wa-copy-btn"><Copy className="mr-1.5 h-4 w-4" />Copier le message</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RetroactiveDialog open={retroOpen} date={date} onClose={() => setRetroOpen(false)} onDone={() => { setRetroOpen(false); load(); }} />
      <FormationGroupeeDialog open={formationOpen} date={date} rows={data?.rows || []} onClose={() => setFormationOpen(false)} onDone={() => { setFormationOpen(false); load(); }} />
      <CorrectionDialog row={correctRow} onClose={() => setCorrectRow(null)} onDone={() => { setCorrectRow(null); load(); }} />
      <AnnulerDialog row={annulRow} onClose={() => setAnnulRow(null)} onDone={() => { setAnnulRow(null); load(); }} />
      <ReopenDialog row={reopenRow} onClose={() => setReopenRow(null)} onDone={() => { setReopenRow(null); load(); }} />
      <OriginalDialog row={originalRow} onClose={() => setOriginalRow(null)} />
      <ReactiverDialog row={reactiverRow} onClose={() => setReactiverRow(null)} onDone={() => { setReactiverRow(null); load(); }} />
    </div>
  );
}

// Cellule HEURES éditable en ligne : clic → saisie directe du total d'heures
// (Entrée pour valider, Échap pour annuler). Crée le pointage si la ligne n'en
// avait pas (« absence à trancher »). Les heures partent au reporting.
function HeuresCell({ row, onSaved, pap = false }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const start = () => {
    if (row.annule) return;
    setVal(row.heures_retenues != null ? Number(row.heures_retenues).toFixed(2).replace(".", ",") : "");
    setEditing(true);
  };
  const save = async () => {
    const raw = (val || "").trim();
    if (!raw) { setEditing(false); return; }
    const h = parseFloat(raw.replace(",", "."));
    if (isNaN(h) || h < 0 || h > 24) { toast.error("Heures invalides (0 à 24, ex. 4,97)."); return; }
    const ident = row.pointage_id
      ? { pointage_id: row.pointage_id }
      : { affectation_id: row.affectation_id, booking_id: row.booking_id, rd_id: row.rd_id,
          creneau_debut: row.creneau_debut, creneau_fin: row.creneau_fin };
    setBusy(true);
    try {
      await api.post(`/pointage/pointages/saisie-heures`, { ...ident, heures: h });
      toast.success("Heures enregistrées.");
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="flex items-center justify-end gap-1">
        <Input autoFocus inputMode="decimal" value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          onBlur={() => !busy && save()}
          className="h-8 w-[74px] text-right" data-testid={`heures-input-${row.code_rd}`} />
        <Button size="icon" variant="ghost" className="h-7 w-7 text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.10)]"
          onMouseDown={(e) => e.preventDefault()} onClick={save} disabled={busy} data-testid={`heures-save-${row.code_rd}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
        </Button>
      </div>
    );
  }
  return (
    <button type="button" onClick={start} disabled={row.annule}
      className={`group inline-flex items-center justify-end gap-1 rounded px-1.5 py-1 font-bold tabular-nums transition-colors hover:bg-[rgba(238,114,46,0.10)] ${row.deduit ? "text-[#2F6FED]" : "text-ink"} ${row.annule ? "cursor-default opacity-60" : ""}`}
      title={row.annule ? "" : (pap ? "Porte-à-porte : saisir / corriger les heures manuellement" : "Cliquer pour saisir les heures")} data-testid={`heures-cell-${row.code_rd}`}>
      {row.heures_retenues == null && pap
        ? <span className="text-[#8a4b1f]">Saisir</span>
        : fmtH(row.heures_retenues)}
      {!row.annule && <Pencil className={`h-3 w-3 transition-opacity ${pap ? "opacity-60" : "opacity-0 group-hover:opacity-60"}`} />}
    </button>
  );
}

// Actions d'une ligne Journée : Corriger / Annuler (traçable) / Voir l'original.
// La suppression sèche n'est proposée que pour une affectation SANS pointage réel.
function JourneeRowActions({ row, onCorrect, onAnnuler, onReopen, onOriginal, onDeleted, onReactiver }) {
  const hasPointage = !!(row.pointage_id && (row.arrivee_pointee || row.depart_pointe || row.saisie_retroactive));
  const showOriginal = !!(row.corrige || (row.original && Object.keys(row.original || {}).length > 0));
  // Réouverture possible si un départ a été pointé (erreur fréquente : départ cliqué trop tôt).
  const canReopen = !!(row.depart_pointe || row.depart_corrigee);
  // Ligne ANNULÉE (trace d'audit conservée) : on ne supprime jamais physiquement,
  // mais on doit pouvoir la RÉACTIVER (annulation erronée → remettre les heures en compte).
  if (row.annule) return (
    <div className="flex items-center justify-end gap-0.5">
      {showOriginal && (
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-ink hover:bg-[var(--surface-2)]" title="Voir l'original (brut immuable)" onClick={onOriginal} data-testid={`journee-original-${row.code_rd}`}><History className="h-4 w-4" /></Button>
      )}
      <Button variant="ghost" size="sm" className="h-8 px-2 text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.10)]" onClick={onReactiver} title="Réactiver cette ligne annulée (remet les heures en compte)" data-testid={`journee-reactiver-${row.code_rd}`}><RotateCcw className="mr-1 h-3.5 w-3.5" />Réactiver</Button>
    </div>
  );
  // Ligne SANS pointage (« absence à trancher ») : on autorise la SAISIE directe des
  // heures (création du pointage) — plus seulement la suppression sèche.
  if (!hasPointage) return (
    <div className="flex items-center justify-end gap-0.5">
      <Button variant="ghost" size="sm" className="h-8 px-2 text-[#8a6600] hover:bg-[rgba(185,137,0,0.10)]" onClick={onCorrect} data-testid={`journee-saisir-${row.code_rd}`}><Pencil className="mr-1 h-3.5 w-3.5" />Saisir</Button>
      <DeleteAffBtn affId={row.affectation_id} onDone={onDeleted} />
    </div>
  );
  return (
    <div className="flex items-center justify-end gap-0.5">
      {showOriginal && (
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-ink hover:bg-[var(--surface-2)]" title="Voir l'original (brut immuable)" onClick={onOriginal} data-testid={`journee-original-${row.code_rd}`}><History className="h-4 w-4" /></Button>
      )}
      {!row.annule && canReopen && (
        <Button variant="ghost" size="icon" className="h-8 w-8 text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.10)]" title="Réinitialiser le départ (le collaborateur pourra repointer)" onClick={onReopen} data-testid={`journee-reopen-${row.code_rd}`}><RotateCcw className="h-4 w-4" /></Button>
      )}
      {!row.annule && (
        <Button variant="ghost" size="sm" className="h-8 px-2 text-[#8a6600] hover:bg-[rgba(185,137,0,0.10)]" onClick={onCorrect} data-testid={`journee-correct-${row.code_rd}`}><Pencil className="mr-1 h-3.5 w-3.5" />Corriger</Button>
      )}
      {!row.annule && (
        <Button variant="ghost" size="icon" className="h-8 w-8 text-[#9B2226] hover:bg-[rgba(214,69,69,0.10)]" title="Annuler (traçable)" onClick={onAnnuler} data-testid={`journee-annuler-${row.code_rd}`}><Ban className="h-4 w-4" /></Button>
      )}
    </div>
  );
}

function DeleteAffBtn({ affId, onDone }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try { await api.delete(`/pointage/affectations/${affId}`); toast.success("Affectation retirée"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#9B2226] hover:bg-[rgba(214,69,69,0.10)]" onClick={del} disabled={busy} data-testid={`del-aff-${affId}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
    </Button>
  );
}

function AssignDialog({ date, onDone }) {
  const [bookings, setBookings] = useState([]);
  const [bookingId, setBookingId] = useState("");
  const [rdQuery, setRdQuery] = useState("");
  const [rds, setRds] = useState([]);
  const [rdId, setRdId] = useState("");
  const [cd, setCd] = useState("10:00");
  const [cf, setCf] = useState("20:00");
  const [independant, setIndependant] = useState(false);
  const [busy, setBusy] = useState(false);
  // Plage par défaut GLOBALE (paramétrable) — préremplit les nouvelles affectations.
  const [defOpen, setDefOpen] = useState(false);
  const [defCd, setDefCd] = useState("10:00");
  const [defCf, setDefCf] = useState("20:00");
  const [savingDef, setSavingDef] = useState(false);

  useEffect(() => {
    api.get(`/pointage/bookings?date=${date}`).then((r) => setBookings(r.data)).catch(() => setBookings([]));
    api.get(`/pointage/affectation-defaults`).then((r) => {
      const d = r.data || {};
      setDefCd(d.creneau_debut || "10:00"); setDefCf(d.creneau_fin || "20:00");
      setCd(d.creneau_debut || "10:00"); setCf(d.creneau_fin || "20:00");
    }).catch(() => {});
  }, [date]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/pointage/rds?actifs_terrain=true${rdQuery ? `&q=${encodeURIComponent(rdQuery)}` : ""}`)
        .then((r) => setRds(r.data)).catch(() => setRds([]));
    }, 250);
    return () => clearTimeout(t);
  }, [rdQuery]);

  const saveDefaults = async () => {
    setSavingDef(true);
    try {
      await api.put(`/pointage/affectation-defaults`, { creneau_debut: defCd, creneau_fin: defCf });
      setCd(defCd); setCf(defCf); setDefOpen(false);
      toast.success("Plage par défaut enregistrée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSavingDef(false); }
  };

  const submit = async () => {
    if (!bookingId || !rdId) { toast.error("Choisissez un spot et un collaborateur"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/pointage/affectations`, {
        booking_id: bookingId, rd_id: rdId, creneau_debut: cd, creneau_fin: cf,
        population: independant ? "independant" : "salarie",
      });
      toast.success(independant ? "Indépendant affecté (hors pointage)" : "Collaborateur affecté");
      if (r.data.warning) toast.warning(r.data.warning);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Affectation impossible"); }
    finally { setBusy(false); }
  };

  // Pré-coche « Indépendant » si le collaborateur sélectionné est déjà marqué indé.
  const selectedRd = rds.find((x) => x.id === rdId);
  useEffect(() => {
    if (selectedRd) setIndependant(selectedRd.population === "independant");
  }, [rdId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto overflow-x-hidden" data-testid="assign-dialog">
      <DialogHeader><DialogTitle className="break-words pr-6">Affecter un collaborateur — {frenchDateLong(date)}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Journée-spot</label>
          <Select value={bookingId} onValueChange={setBookingId}>
                <SelectTrigger className="mt-1 w-full [&>span]:line-clamp-1 [&>span]:text-left" data-testid="assign-booking-select"><SelectValue placeholder="Choisir un spot booké ce jour…" /></SelectTrigger>
            <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]">
              {bookings.length === 0 && <div className="px-3 py-2 text-sm text-muted-ink">Aucun spot booké ce jour.</div>}
              {bookings.map((b) => (
                <SelectItem
                  key={b.booking_id}
                  value={b.booking_id}
                  textValue={b.spot_name}
                  data-testid={`assign-booking-${b.booking_id}`}
                  className="[&>span:last-child]:flex [&>span:last-child]:min-w-0 [&>span:last-child]:flex-1 [&>span:last-child]:items-baseline [&>span:last-child]:gap-1.5"
                >
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{b.spot_name}{b.association_acronym ? ` — ${b.association_acronym}` : ""}</span>
                  <span className="shrink-0 whitespace-nowrap text-[11px] font-semibold text-muted-ink">· {b.affectations_count} affecté(s)</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Collaborateur (salariés &amp; indépendants)</label>
          <Input placeholder="Rechercher par nom ou code…" value={rdQuery} onChange={(e) => setRdQuery(e.target.value)} className="mt-1" data-testid="assign-rd-search" />
          <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-[var(--border-soft)]">
            {rds.length === 0 && <div className="px-3 py-2 text-sm text-muted-ink">Aucun collaborateur affectable. Synchronisez PREREC.</div>}
            {rds.map((rd) => (
              <button key={rd.id} onClick={() => setRdId(rd.id)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--surface-2)] ${rdId === rd.id ? "bg-[rgba(238,114,46,0.10)]" : ""}`}
                data-testid={`assign-rd-${rd.code}`}>
                <span className="min-w-0 flex-1 truncate font-semibold text-ink">{rd.name}
                  {rd.population === "independant" && <span className="ml-1 rounded bg-[rgba(93,63,211,0.12)] px-1 text-[9px] font-bold uppercase text-[#5D3FD3]" data-testid={`assign-indep-${rd.code}`}>Indé</span>}</span>
                <CodeBadge code={rd.code} />
              </button>
            ))}
          </div>
          <label className="mt-2 flex cursor-pointer items-start gap-2 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5" data-testid="assign-independant-row">
            <Checkbox checked={independant} onCheckedChange={(v) => setIndependant(!!v)} className="mt-0.5" data-testid="assign-independant-checkbox" />
            <span className="text-[12px] leading-snug text-ink">
              <span className="font-semibold">Indépendant — hors pointage</span>
              <span className="block text-[11px] text-muted-ink">Non soumis au pointage : aucun rappel d'arrivée/départ, apparaît en lecture seule dans la Journée. La fiche du collaborateur sera mise à jour.</span>
            </span>
          </label>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Plage horaire</label>
            <button type="button" onClick={() => setDefOpen((o) => !o)}
              className="text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline" data-testid="assign-defaults-toggle">
              Plage par défaut
            </button>
          </div>
          <div className="mt-1 grid grid-cols-2 gap-3">
            <div><span className="text-[10px] font-semibold text-muted-ink">Début</span><Input type="time" value={cd} onChange={(e) => setCd(e.target.value)} className="mt-0.5" data-testid="assign-cd" /></div>
            <div><span className="text-[10px] font-semibold text-muted-ink">Fin</span><Input type="time" value={cf} onChange={(e) => setCf(e.target.value)} className="mt-0.5" data-testid="assign-cf" /></div>
          </div>
          {defOpen && (
            <div className="mt-2 rounded-lg border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5" data-testid="assign-defaults-panel">
              <div className="mb-1.5 text-[11px] font-semibold text-ink">Plage par défaut (globale) — appliquée aux nouvelles affectations</div>
              <div className="grid grid-cols-2 gap-3">
                <Input type="time" value={defCd} onChange={(e) => setDefCd(e.target.value)} data-testid="assign-def-cd" />
                <Input type="time" value={defCf} onChange={(e) => setDefCf(e.target.value)} data-testid="assign-def-cf" />
              </div>
              <Button size="sm" onClick={saveDefaults} disabled={savingDef}
                className="mt-2 h-8 bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]" data-testid="assign-def-save">
                {savingDef ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer la plage par défaut"}
              </Button>
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button className="w-full bg-[var(--shariti-orange)] font-semibold hover:bg-[#d9631f] sm:w-auto" onClick={submit} disabled={busy} data-testid="assign-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Affecter"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/* --------------------------------------------------------------- Anomalies */
// ============================================================================
// ONGLET « HEURES » — matrice mensuelle : collaborateurs × jours + cumul
// ============================================================================

// Dialogue d'édition d'une CASE (collaborateur × jour). Charge le détail du jour
// puis corrige les heures via /pointages/saisie-heures → met à jour heures_retenues
// (source unique remontée à la paie/prépaie) : la correction se répercute partout.
function HeuresCellDialog({ cell, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vals, setVals] = useState({});
  const [savingId, setSavingId] = useState("");
  const [manualVal, setManualVal] = useState("");   // saisie directe hors planning
  const [savingManual, setSavingManual] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const open = !!cell;

  useEffect(() => {
    if (!cell) return;
    let cancel = false;
    (async () => {
      setLoading(true); setData(null);
      try {
        const { data: r } = await api.get(`/pointage/heures-cellule`, { params: { rd_id: cell.rd_id, jour: cell.jour } });
        if (cancel) return;
        setData(r);
        const init = {};
        (r.entries || []).forEach((e) => { init[e.pointage_id] = String(e.heures_retenues ?? "").replace(".", ","); });
        setVals(init);
        setManualVal("");
      } catch { if (!cancel) toast.error("Impossible de charger le détail du jour."); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [cell]);

  const save = async (entry) => {
    const raw = String(vals[entry.pointage_id] ?? "").trim().replace(",", ".");
    if (raw === "") { toast.error("Saisissez un nombre d'heures."); return; }
    const h = Number(raw);
    if (!Number.isFinite(h) || h < 0 || h > 24) { toast.error("Heures invalides (0 à 24)."); return; }
    setSavingId(entry.pointage_id);
    try {
      await api.post(`/pointage/pointages/saisie-heures`, {
        pointage_id: entry.pointage_id, heures: h, motif: "Correction depuis la matrice Heures",
      });
      toast.success("Heures corrigées — répercutées sur la paie et le pointage.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Correction impossible.");
    } finally { setSavingId(""); }
  };

  // Saisie DIRECTE d'heures quand la personne n'était PAS inscrite au planning ce
  // jour-là (aucune ligne de pointage) : crée une saisie « hors planning » qui
  // compte immédiatement dans la paie / les totaux.
  const saveManual = async () => {
    const raw = String(manualVal ?? "").trim().replace(",", ".");
    if (raw === "") { toast.error("Saisissez un nombre d'heures."); return; }
    const h = Number(raw);
    if (!Number.isFinite(h) || h < 0 || h > 24) { toast.error("Heures invalides (0 à 24)."); return; }
    setSavingManual(true);
    try {
      await api.post(`/pointage/pointages/saisie-manuelle`, {
        rd_id: data.rd_id, jour: data.jour, heures: h,
        motif: "Saisie directe hors planning (matrice Heures)",
      });
      toast.success("Heures ajoutées — répercutées sur la paie et les totaux.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Saisie impossible.");
    } finally { setSavingManual(false); }
  };

  // Suppression d'une saisie MANUELLE hors planning (sécurisée côté backend :
  // n'affecte jamais un vrai pointage terrain).
  const deleteManual = async (entry) => {
    setDeletingId(entry.pointage_id);
    try {
      await api.delete(`/pointage/pointages/${entry.pointage_id}/manuelle`);
      toast.success("Saisie manuelle supprimée.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    } finally { setDeletingId(""); }
  };

  const frLong = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }); } catch { return iso; } };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="heures-cell-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4 text-[var(--shariti-orange)]" /> Corriger les heures</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="space-y-2 py-4"><Skeleton className="h-5 w-2/3" /><Skeleton className="h-14 w-full" /></div>
        ) : !data ? null : (
          <div className="space-y-3">
            <div className="rounded-lg bg-[var(--surface-2)] px-3 py-2 text-sm">
              <div className="break-words font-bold text-[#181818]" data-testid="cell-dialog-name">{data.rd_name}{data.code_rd && <span className="font-normal text-muted-ink"> · {data.code_rd}</span>}</div>
              <div className="capitalize text-muted-ink">{frLong(data.jour)}</div>
            </div>
            {data.is_independant ? (
              <div className="flex items-start gap-2 rounded-lg bg-[rgba(238,114,46,0.08)] p-3 text-sm text-[#8a3a10]" data-testid="cell-dialog-indep"><Info className="mt-0.5 h-4 w-4 shrink-0" /> Les indépendants ne sont pas soumis au pointage.</div>
            ) : data.frozen ? (
              <div className="flex items-start gap-2 rounded-lg bg-[rgba(214,69,69,0.1)] p-3 text-sm text-[#b23434]" data-testid="cell-dialog-frozen"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Mois transmis en paie (gelé) : correction bloquée.</div>
            ) : data.future ? (
              <div className="flex items-start gap-2 rounded-lg bg-[var(--surface-2)] p-3 text-sm text-muted-ink" data-testid="cell-dialog-future"><Info className="mt-0.5 h-4 w-4 shrink-0" /> Jour futur : aucune heure à corriger.</div>
            ) : (data.entries || []).length === 0 ? (
              <div className="space-y-2" data-testid="cell-dialog-empty">
                <div className="flex items-start gap-2 rounded-lg bg-[var(--surface-2)] p-3 text-[13px] text-muted-ink">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Personne <b>non inscrite au planning</b> ce jour-là : aucun pointage possible. Saisissez directement le total d'heures travaillées ci-dessous.</span>
                </div>
                <div className="rounded-xl border border-[var(--border-soft)] p-3">
                  <div className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-[#181818]"><Pencil className="h-3.5 w-3.5 shrink-0 text-muted-ink" />Saisie directe (hors planning)</div>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input inputMode="decimal" value={manualVal} onChange={(ev) => setManualVal(ev.target.value)}
                        className="h-10 pr-7 font-bold tabular-nums" placeholder="ex. 7" data-testid="cell-manual-input" />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-ink">h</span>
                    </div>
                    <Button onClick={saveManual} disabled={savingManual}
                      className="h-10 bg-[var(--shariti-orange)] text-white hover:opacity-90" data-testid="cell-manual-save">
                      {savingManual ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      <span className="ml-1">Enregistrer</span>
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {data.entries.map((e) => (
                  <div key={e.pointage_id} className="rounded-xl border border-[var(--border-soft)] p-3" data-testid={`cell-entry-${e.pointage_id}`}>
                    <div className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-[#181818]"><MapPin className="h-3.5 w-3.5 shrink-0 text-muted-ink" /><span className="break-words">{e.spot_name}</span></div>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Input inputMode="decimal" value={vals[e.pointage_id] ?? ""} onChange={(ev) => setVals((p) => ({ ...p, [e.pointage_id]: ev.target.value }))}
                          className="h-10 pr-7 font-bold tabular-nums" placeholder="ex. 7" data-testid={`cell-input-${e.pointage_id}`} />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-ink">h</span>
                      </div>
                      <Button onClick={() => save(e)} disabled={savingId === e.pointage_id}
                        className="h-10 bg-[var(--shariti-orange)] text-white hover:opacity-90" data-testid={`cell-save-${e.pointage_id}`}>
                        {savingId === e.pointage_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        <span className="ml-1">Enregistrer</span>
                      </Button>
                    </div>
                    {!e.counts && <div className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-[#b23434]"><AlertTriangle className="h-3 w-3 shrink-0" /> Ligne non comptée ({e.annule ? "annulée" : e.statut}) — la saisie la réactivera.</div>}
                    {e.is_manuel_sans_spot && (
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1 rounded-md bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-semibold text-muted-ink"><Info className="h-3 w-3 shrink-0" /> Saisie manuelle hors planning</span>
                        <Button variant="ghost" size="sm" onClick={() => deleteManual(e)} disabled={deletingId === e.pointage_id}
                          className="h-8 px-2 text-[#b23434] hover:bg-[rgba(214,69,69,0.1)]" data-testid={`cell-manual-delete-${e.pointage_id}`}>
                          {deletingId === e.pointage_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          <span className="ml-1">Supprimer</span>
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] leading-relaxed text-muted-ink">La correction met à jour les <b>heures retenues</b> (celles remontées à la paie/prépaie) et se répercute partout : pointage, totaux et exports.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cell-close">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Carte MOBILE d'un collaborateur : total du mois + écart, totaux par semaine, et
// détail par jour dépliable (chaque jour éditable). Supprime le scroll horizontal.
function HeuresMobileCard({ c, weeks, days, fmtH, fmtDiff, onEditDay }) {
  const { seuils } = useContext(SeuilsContext);
  const [open, setOpen] = useState(false);
  const d = fmtDiff(c.diff);
  const activeWeeks = weeks.filter((w) => c.per_week[w.key]);
  const daysWithHours = days.filter((dd) => c.per_day[dd.date]);
  const prodFmt = (v) => (v === null || v === undefined ? "" : Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const prodOf = (bs, h) => (h && h > 0 ? Math.round(((bs || 0) / h) * 100) / 100 : null);
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid={`heures-card-${c.rd_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="break-words font-bold text-[#181818]">{c.nom}</div>
          {c.code_rd && <div className="text-[11px] text-muted-ink">{c.code_rd} · {c.jours_travailles} j</div>}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-2xl font-extrabold tabular-nums text-[var(--shariti-orange)]" data-testid={`heures-card-total-${c.rd_id}`}>{fmtH(c.total) || "0"}<span className="text-sm font-bold"> h</span></div>
          {(c.bs_total || 0) > 0 && (
            <div className="text-[11px] font-bold text-[#5D3FD3]" data-testid={`heures-card-bs-${c.rd_id}`}>{c.bs_total} BS{c.bs_par_heure != null && <span className={bshTone(c.bs_par_heure, c.kind, seuils)}> · {prodFmt(c.bs_par_heure)}/h</span>}</div>
          )}
          {d && <div className={`flex items-center justify-end gap-0.5 text-[11px] font-bold ${d.cls}`}><d.Icon className="h-3 w-3 shrink-0" />{d.sign}{d.txt} h</div>}
        </div>
      </div>
      {activeWeeks.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`heures-card-weeks-${c.rd_id}`}>
          {activeWeeks.map((w) => {
            const bs = c.bs_per_week?.[w.key] || 0;
            const prod = prodOf(bs, c.per_week[w.key]);
            return (
              <div key={w.key} className="flex items-center justify-between gap-2 rounded-lg bg-[var(--surface-1)] px-2.5 py-1.5">
                <div className="min-w-0"><div className="text-[11px] font-bold text-[#181818]">{w.label}</div><div className="text-[10px] text-muted-ink">{frenchWeekRange(w)}</div></div>
                <div className="shrink-0 text-right">
                  <div className="font-bold tabular-nums text-[#181818]">{fmtH(c.per_week[w.key])} h</div>
                  {bs > 0 && <div className="text-[10px] font-bold text-[#5D3FD3]">{bs} BS{prod != null && <span className={bshTone(prod, c.kind, seuils)}> · {prodFmt(prod)}/h</span>}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <button onClick={() => setOpen(!open)} className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg border border-[var(--border-soft)] py-1.5 text-[12px] font-semibold text-muted-ink transition-colors hover:bg-[var(--surface-2)]" data-testid={`heures-card-toggle-${c.rd_id}`}>
        {open ? "Masquer le détail" : "Voir & corriger le détail par jour"}
        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-1" data-testid={`heures-card-days-${c.rd_id}`}>
          {daysWithHours.length === 0 ? (
            <div className="py-2 text-center text-[12px] text-muted-ink">Aucune heure sur la période.</div>
          ) : daysWithHours.map((dd) => {
            const bs = c.bs_per_day?.[dd.date] || 0;
            return (
              <button key={dd.date} onClick={() => onEditDay(dd.date)}
                className="flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-sm transition-colors hover:bg-[var(--surface-2)]"
                data-testid={`heures-card-day-${c.rd_id}-${dd.date}`}>
                <span className="capitalize text-muted-ink">{new Date(dd.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</span>
                <span className="flex items-center gap-2 font-semibold text-[#181818]">
                  {bs > 0 && <span className="text-[11px] font-bold text-[#5D3FD3]">{bs} BS</span>}
                  {fmtH(c.per_day[dd.date])} h <Pencil className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HeuresMatrixTab() {
  const { seuils } = useContext(SeuilsContext);
  const [mois, setMois] = useState(monthISO());
  const [pop, setPop] = useState("salarie");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("jour");   // "jour" | "semaine" (desktop)
  const [editCell, setEditCell] = useState(null);      // { rd_id, jour, rd_name }
  const [focusWeekKey, setFocusWeekKey] = useState(null); // null = tout le mois ; sinon clé de semaine ISO

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/pointage/heures-matrix?mois=${mois}&population=${pop}`);
      setData(res);
    } catch (e) {
      toast.error("Impossible de charger les heures du mois");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [mois, pop]);

  useEffect(() => { load(); }, [load]);
  // Réinitialise la semaine ciblée quand on change de mois ou de population.
  useEffect(() => { setFocusWeekKey(null); }, [mois, pop]);

  const shiftMonth = (delta) => {
    const [y, m] = mois.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMois(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const monthLabel = (() => {
    const [y, m] = mois.split("-").map(Number);
    const s = new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();

  const prevMonthLabel = (() => {
    if (!data?.prev_mois) return "";
    const [y, m] = data.prev_mois.split("-").map(Number);
    const s = new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();

  const fmtH = (v) => (v ? Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 2 }) : "");
  // Productivité (BS/h) : 2 décimales, style FR (ex. « 0,45 »). Renvoie "" si non calculable.
  const fmtProd = (v) => (v === null || v === undefined ? "" : Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  // Productivité d'une cellule = BS validés / heures (null si 0 h).
  const prodOf = (bs, h) => (h && h > 0 ? Math.round(((bs || 0) / h) * 100) / 100 : null);

  // Écart mois précédent (M vs M-1) : signe + couleur sémantique + icône.
  const fmtDiff = (v) => {
    if (v === null || v === undefined) return null;
    const r = Math.round(v * 100) / 100;
    if (r === 0) return { txt: "±0", cls: "text-muted-ink", Icon: Minus, sign: "" };
    if (r > 0) return { txt: fmtH(r), cls: "text-emerald-600", Icon: ArrowUp, sign: "+" };
    return { txt: fmtH(Math.abs(r)), cls: "text-rose-600", Icon: ArrowDown, sign: "−" };
  };
  const dayHead = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return { wd: d.toLocaleDateString("fr-FR", { weekday: "short" }).replace(".", ""), num: d.getDate() };
  };

  const days = data?.days || [];
  const weeks = data?.weeks || [];
  const collabs = data?.collaborateurs || [];
  const dayTotals = data?.day_totals || {};
  const weekTotals = data?.week_totals || {};
  const dayBsTotals = data?.day_bs_totals || {};
  const weekBsTotals = data?.week_bs_totals || {};

  // ————— Navigation SEMAINE (◀ ▶) : parcourir les semaines du mois —————
  // focusWeekKey === null → tout le mois ; sinon on n'affiche que la semaine ciblée.
  const focusWeek = focusWeekKey ? weeks.find((w) => w.key === focusWeekKey) : null;
  const weekSteps = [null, ...weeks.map((w) => w.key)];
  const stepIdx = Math.max(0, weekSteps.indexOf(focusWeek ? focusWeek.key : null));
  const gotoStep = (delta) => setFocusWeekKey(weekSteps[Math.min(weekSteps.length - 1, Math.max(0, stepIdx + delta))]);
  const visibleDays = focusWeek ? days.filter((d) => (focusWeek.day_dates || []).includes(d.date)) : days;
  const visibleWeeks = focusWeek ? [focusWeek] : weeks;

  const exportCsv = () => {
    if (!data) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["Collaborateur", "Code", ...days.map((d) => d.date), "Total (h)", "Mois préc. (h)", "Écart (h)"];
    const lines = [head.map(esc).join(";")];
    collabs.forEach((c) => {
      lines.push([c.nom, c.code_rd || "", ...days.map((d) => String(c.per_day[d.date] || "").replace(".", ",")),
        String(c.total).replace(".", ","), String(c.prev_total ?? "").replace(".", ","), String(c.diff ?? "").replace(".", ",")].map(esc).join(";"));
    });
    lines.push(["Total / jour", "", ...days.map((d) => String(dayTotals[d.date] || "").replace(".", ",")),
      String(data.grand_total).replace(".", ","), String(data.prev_grand_total ?? "").replace(".", ","), String(data.grand_diff ?? "").replace(".", ",")].map(esc).join(";"));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `heures_${mois}_${pop}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const popLabel = { salarie: "Salariés", independant: "Indépendants", tous: "Tous" }[pop] || "Salariés";

  return (
    <div className="space-y-4" data-testid="heures-matrix-tab">
      {/* Barre d'outils : navigation mois + population + export */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1">
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => shiftMonth(-1)} data-testid="heures-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[150px] px-2 text-center text-sm font-bold capitalize" data-testid="heures-month-label">{monthLabel}</span>
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => shiftMonth(1)} data-testid="heures-next-month"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" className="h-9 shrink-0 px-2 text-xs font-semibold" onClick={() => setMois(monthISO())} data-testid="heures-this-month">Ce mois</Button>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden items-center rounded-lg border border-[var(--border-soft)] bg-white p-0.5 lg:flex" data-testid="heures-viewmode">
            <button onClick={() => setViewMode("jour")} className={`rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${viewMode === "jour" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`} data-testid="heures-view-jour">Par jour</button>
            <button onClick={() => setViewMode("semaine")} className={`rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${viewMode === "semaine" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`} data-testid="heures-view-semaine">Par semaine</button>
          </div>
          <Select value={pop} onValueChange={setPop}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="heures-population-filter">
              <Users className="mr-1.5 h-4 w-4 shrink-0 text-muted-ink" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="salarie" data-testid="heures-pop-salarie">Salariés</SelectItem>
              <SelectItem value="independant" data-testid="heures-pop-independant">Indépendants</SelectItem>
              <SelectItem value="tous" data-testid="heures-pop-tous">Tous</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="h-9" onClick={exportCsv} disabled={!collabs.length} data-testid="heures-export-csv"><FileText className="mr-1.5 h-4 w-4" />Exporter</Button>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={load} data-testid="heures-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Résumé */}
      {!loading && data && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="heures-summary">
          <Badge variant="secondary" className="border border-[var(--border-soft)] bg-white font-semibold">{data.nb_collaborateurs} {popLabel.toLowerCase()}</Badge>
          <Badge className="bg-[var(--shariti-orange)] font-bold text-white">{fmtH(data.grand_total) || "0"} h retenues</Badge>
          <Badge variant="secondary" className="border border-[var(--border-soft)] bg-white font-bold text-[#5D3FD3]" title="Bulletins comptés (tous sauf invalides et annulés) sur la période" data-testid="heures-grand-bs">
            {(data.grand_bs_total || 0).toLocaleString("fr-FR")} BS comptés
          </Badge>
          {data.grand_bs_par_heure != null && (
            <Badge variant="secondary" className={`border border-[var(--border-soft)] bg-white font-bold ${bshTone(data.grand_bs_par_heure, "magasin", seuils)}`} title="Productivité = BS comptés / heures retenues (barème magasin)" data-testid="heures-grand-prod">
              {fmtProd(data.grand_bs_par_heure)} BS/h
            </Badge>
          )}
          <span className="text-muted-ink">sur {monthLabel}</span>
          {(() => {
            const d = fmtDiff(data.grand_diff);
            if (!d) return null;
            const { txt, cls, Icon, sign } = d;
            return (
              <Badge variant="secondary" className={`inline-flex items-center gap-1 border border-[var(--border-soft)] bg-white font-bold ${cls}`}
                title={`Écart global vs ${prevMonthLabel} (${fmtH(data.prev_grand_total) || "0"} h)`} data-testid="heures-grand-diff">
                <Icon className="h-3.5 w-3.5 shrink-0" />{sign}{txt} h vs {prevMonthLabel}
              </Badge>
            );
          })()}
        </div>
      )}

      {/* Navigation SEMAINE : ◀ ▶ pour parcourir les semaines (au lieu de tout afficher d'un bloc) */}
      {!loading && data && collabs.length > 0 && weeks.length > 0 && (
        <div className="flex items-center justify-center gap-1.5 rounded-xl border border-[var(--border-soft)] bg-white p-1.5 shadow-[var(--shadow-sm)]" data-testid="heures-week-nav">
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => gotoStep(-1)} disabled={stepIdx <= 0} data-testid="heures-week-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="min-w-[176px] px-2 text-center" data-testid="heures-week-label">
            {focusWeek ? (
              <><div className="text-sm font-bold leading-tight text-[#181818]">{focusWeek.label}</div><div className="text-[11px] text-muted-ink">{frenchWeekRange(focusWeek)}</div></>
            ) : (
              <div className="text-sm font-bold text-[#181818]">Tout le mois</div>
            )}
          </div>
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => gotoStep(1)} disabled={stepIdx >= weekSteps.length - 1} data-testid="heures-week-next"><ChevronRight className="h-4 w-4" /></Button>
          {focusWeek && <Button variant="ghost" className="h-9 shrink-0 px-2 text-xs font-semibold text-[var(--shariti-orange)]" onClick={() => setFocusWeekKey(null)} data-testid="heures-week-all">Tout le mois</Button>}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-11 w-full rounded-lg" />)}</div>
      ) : !collabs.length ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="heures-empty">
          <Hourglass className="h-8 w-8 text-muted-ink" />
          <p className="font-semibold text-[#181818]">Aucune heure retenue ce mois</p>
          <p className="text-sm text-muted-ink">Changez de mois ou de population pour voir d'autres données.</p>
        </div>
      ) : (
        <>
          {/* ————— Vue MOBILE (< md) : cartes empilées, sans scroll horizontal ————— */}
          <div className="space-y-3 md:hidden" data-testid="heures-mobile-list">
            {collabs.map((c) => (
              <HeuresMobileCard key={c.rd_id} c={c} weeks={visibleWeeks} days={visibleDays} fmtH={fmtH} fmtDiff={fmtDiff}
                onEditDay={(jour) => setEditCell({ rd_id: c.rd_id, jour })} />
            ))}
            <div className="flex items-center justify-between rounded-2xl bg-[var(--shariti-orange)] px-4 py-3 text-white shadow-[var(--shadow-sm)]" data-testid="heures-mobile-grandtotal">
              <span className="text-sm font-bold uppercase tracking-wide">Total du mois</span>
              <span className="text-lg font-extrabold tabular-nums">{fmtH(data.grand_total) || "0"} h</span>
            </div>
          </div>

          {/* ————— Vue BUREAU (≥ md) : « Par semaine » (condensé) ou « Par jour » (détaillé) ————— */}
          {viewMode === "semaine" ? (
            <div className="relative hidden max-h-[68vh] overflow-auto rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)] no-scrollbar md:block" data-testid="heures-week-table-wrap">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-30 min-w-[200px] border-b border-r border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-muted-ink shadow-[6px_0_8px_-6px_rgba(24,24,24,0.16)]">Collaborateur</th>
                    {visibleWeeks.map((w) => (
                      <th key={w.key} className="border-b border-l border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-center text-[11px] font-bold leading-tight text-[#181818]">
                        <div className="uppercase tracking-wide">{w.label}</div>
                        <div className="text-[10px] font-medium normal-case text-muted-ink">{frenchWeekRange(w)}</div>
                      </th>
                    ))}
                    <th className="sticky right-[86px] top-0 z-30 w-[80px] min-w-[80px] border-b border-l-2 border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-[var(--shariti-orange)] shadow-[-6px_0_8px_-6px_rgba(24,24,24,0.16)]">Total</th>
                    <th className="sticky right-0 top-0 z-30 w-[86px] min-w-[86px] border-b border-l border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-muted-ink">Écart</th>
                  </tr>
                </thead>
                <tbody>
                  {collabs.map((c) => (
                    <tr key={c.rd_id} className="group" data-testid={`heures-week-row-${c.rd_id}`}>
                      <td className="sticky left-0 z-10 min-w-[200px] border-b border-r border-[var(--border-soft)] bg-white px-3 py-2 shadow-[6px_0_8px_-6px_rgba(24,24,24,0.16)] group-hover:bg-[var(--surface-2)]">
                        <div className="break-words font-semibold text-[#181818]">{c.nom}</div>
                        {c.code_rd && <div className="text-[11px] text-muted-ink">{c.code_rd} · {c.jours_travailles} j</div>}
                      </td>
                      {visibleWeeks.map((w) => {
                        const v = c.per_week[w.key];
                        const bs = c.bs_per_week?.[w.key] || 0;
                        const prod = prodOf(bs, v);
                        return (
                          <td key={w.key} className={`border-b border-l border-[var(--border-soft)] px-3 py-2 text-center tabular-nums group-hover:bg-[var(--surface-2)] ${v ? "font-semibold text-[#181818]" : "text-[rgba(24,24,24,0.18)]"}`} data-testid={`heures-week-cell-${c.rd_id}-${w.key}`}>
                            <div className="flex flex-col items-center leading-tight">
                              <span>{v ? `${fmtH(v)} h` : "·"}</span>
                              {bs > 0 && (
                                <span className="mt-0.5 text-[10px] font-bold text-[#5D3FD3]" data-testid={`heures-week-bs-${c.rd_id}-${w.key}`}>
                                  {bs} BS{prod != null && <span className={bshTone(prod, c.kind, seuils)}> · {fmtProd(prod)}/h</span>}
                                </span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td className="sticky right-[86px] z-10 w-[80px] min-w-[80px] border-b border-l-2 border-[var(--border-soft)] bg-[#FDEEE4] px-2 py-2 text-center font-bold tabular-nums text-[var(--shariti-orange)] shadow-[-6px_0_8px_-6px_rgba(24,24,24,0.16)] group-hover:bg-[#FBE0D0]" data-testid={`heures-week-total-${c.rd_id}`}>
                        <div className="flex flex-col items-center leading-tight">
                          <span>{fmtH(c.total) || "0"}</span>
                          {(c.bs_total || 0) > 0 && (
                            <span className="mt-0.5 text-[10px] font-bold text-[#5D3FD3]" title="Bulletins comptés · productivité BS/h" data-testid={`heures-week-total-bs-${c.rd_id}`}>
                              {c.bs_total} BS{c.bs_par_heure != null && <span className={bshTone(c.bs_par_heure, c.kind, seuils)}> · {fmtProd(c.bs_par_heure)}/h</span>}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="sticky right-0 z-10 w-[86px] min-w-[86px] border-b border-l border-[var(--border-soft)] bg-white px-2 py-2 text-center tabular-nums group-hover:bg-[var(--surface-2)]">
                        {(() => {
                          const dd = fmtDiff(c.diff);
                          if (!dd) return <span className="text-[rgba(24,24,24,0.25)]">·</span>;
                          const { txt, cls, Icon, sign } = dd;
                          return <span className={`inline-flex items-center justify-center gap-0.5 whitespace-nowrap font-semibold ${cls}`} title={`${prevMonthLabel} : ${fmtH(c.prev_total) || "0"} h`}><Icon className="h-3 w-3 shrink-0" />{sign}{txt}</span>;
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="sticky bottom-0 left-0 z-30 border-t-2 border-r border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink shadow-[6px_0_8px_-6px_rgba(24,24,24,0.16)]">Total / semaine</td>
                    {visibleWeeks.map((w) => (
                      <td key={w.key} className="sticky bottom-0 z-20 border-t-2 border-l border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-center text-[11px] font-bold tabular-nums text-[#181818]" data-testid={`heures-weektotal-${w.key}`}>
                        <div className="flex flex-col items-center leading-tight">
                          <span>{fmtH(weekTotals[w.key])}</span>
                          {(weekBsTotals[w.key] || 0) > 0 && <span className="mt-0.5 text-[10px] font-bold text-[#5D3FD3]">{weekBsTotals[w.key]} BS{prodOf(weekBsTotals[w.key], weekTotals[w.key]) != null && <span className={bshTone(prodOf(weekBsTotals[w.key], weekTotals[w.key]), "magasin", seuils)}> · {fmtProd(prodOf(weekBsTotals[w.key], weekTotals[w.key]))}/h</span>}</span>}
                        </div>
                      </td>
                    ))}
                    <td className="sticky bottom-0 right-[86px] z-30 border-t-2 border-l-2 border-[var(--border-soft)] bg-[var(--shariti-orange)] px-2 py-2 text-center font-bold tabular-nums text-white shadow-[-6px_0_8px_-6px_rgba(24,24,24,0.16)]" data-testid="heures-week-grandtotal">
                      <div className="flex flex-col items-center leading-tight">
                        <span>{fmtH(data.grand_total) || "0"}</span>
                        {(data.grand_bs_total || 0) > 0 && <span className="mt-0.5 text-[10px] font-bold text-white/90">{(data.grand_bs_total || 0).toLocaleString("fr-FR")} BS{data.grand_bs_par_heure != null ? ` · ${fmtProd(data.grand_bs_par_heure)}/h` : ""}</span>}
                      </div>
                    </td>
                    <td className="sticky bottom-0 right-0 z-30 border-t-2 border-l border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-2 text-center font-bold tabular-nums">
                      {(() => {
                        const dd = fmtDiff(data.grand_diff);
                        if (!dd) return <span className="text-muted-ink">·</span>;
                        const { txt, cls, Icon, sign } = dd;
                        return <span className={`inline-flex items-center justify-center gap-0.5 whitespace-nowrap ${cls}`}><Icon className="h-3 w-3 shrink-0" />{sign}{txt}</span>;
                      })()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
          <div className="relative hidden max-h-[68vh] overflow-auto rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)] no-scrollbar md:block" data-testid="heures-table-wrap">
          <table className="border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 min-w-[132px] sm:min-w-[170px] border-b border-r border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-muted-ink shadow-[6px_0_8px_-6px_rgba(24,24,24,0.16)]">Collaborateur</th>
                {visibleDays.map((d) => {
                  const h = dayHead(d.date);
                  return (
                    <th key={d.date} title={frenchDateFull(d.date)}
                      className={`sticky top-0 z-20 min-w-[46px] border-b border-l border-[var(--border-soft)] px-1 py-1.5 text-center text-[10px] font-semibold leading-tight ${d.is_today ? "bg-[#FBE0D0] text-[var(--shariti-orange)]" : d.weekend ? "bg-[var(--surface-2)] text-muted-ink" : "bg-[var(--surface-2)] text-[#181818]"}`}>
                      <div className="capitalize">{h.wd}</div>
                      <div className="text-[13px] font-bold">{h.num}</div>
                    </th>
                  );
                })}
                <th className="sticky right-[86px] top-0 z-30 w-[74px] min-w-[74px] border-b border-l-2 border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-[var(--shariti-orange)] shadow-[-6px_0_8px_-6px_rgba(24,24,24,0.16)]">Total</th>
                <th title={prevMonthLabel ? `Écart vs ${prevMonthLabel}` : "Écart mois précédent"} className="sticky right-0 top-0 z-30 w-[86px] min-w-[86px] border-b border-l border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-muted-ink" data-testid="heures-diff-head">Écart</th>
              </tr>
            </thead>
            <tbody>
              {collabs.map((c) => (
                <tr key={c.rd_id} className="group" data-testid={`heures-row-${c.rd_id}`}>
                  <td className="sticky left-0 z-10 min-w-[132px] sm:min-w-[170px] border-b border-r border-[var(--border-soft)] bg-white px-3 py-2 shadow-[6px_0_8px_-6px_rgba(24,24,24,0.16)] group-hover:bg-[var(--surface-2)]">
                    <div className="truncate font-semibold text-[#181818]" title={c.nom}>{c.nom}</div>
                    {c.code_rd && <div className="text-[11px] text-muted-ink">{c.code_rd} · {c.jours_travailles} j</div>}
                  </td>
                  {visibleDays.map((d) => {
                    const v = c.per_day[d.date];
                    const bs = c.bs_per_day?.[d.date] || 0;
                    const prod = prodOf(bs, v);
                    return (
                      <td key={d.date}
                        onClick={() => setEditCell({ rd_id: c.rd_id, jour: d.date })}
                        title={`${frenchDateFull(d.date)}${bs ? ` — ${bs} BS validé(s)${prod != null ? ` · ${fmtProd(prod)} BS/h` : ""}` : ""} · Cliquer pour corriger les heures`}
                        className={`group/cell relative cursor-pointer border-b border-l border-[var(--border-soft)] px-1 py-2 text-center tabular-nums transition-colors hover:bg-[rgba(238,114,46,0.12)] hover:ring-1 hover:ring-inset hover:ring-[var(--shariti-orange)] ${d.is_today ? "bg-[rgba(238,114,46,0.06)]" : d.weekend ? "bg-[rgba(24,24,24,0.02)]" : ""} ${v ? "font-semibold text-[#181818]" : "text-[rgba(24,24,24,0.18)]"} group-hover:bg-[var(--surface-2)]`}
                        data-testid={`heures-cell-${c.rd_id}-${d.date}`}>
                        <div className="flex flex-col items-center leading-none">
                          <span className="inline-flex items-center justify-center gap-0.5">
                            {v ? fmtH(v) : "·"}
                            <Pencil className="h-2.5 w-2.5 shrink-0 text-[var(--shariti-orange)] opacity-0 transition-opacity group-hover/cell:opacity-100" />
                          </span>
                          {bs > 0 && <span className="mt-0.5 text-[9px] font-bold text-[#5D3FD3]" data-testid={`heures-bs-${c.rd_id}-${d.date}`}>{bs} BS</span>}
                          {(c.form_per_day?.[d.date] || 0) > 0 && (
                            <span className="mt-0.5 inline-flex items-center gap-0.5 rounded bg-[rgba(15,110,110,0.14)] px-1 text-[8px] font-bold text-[#0F6E6E]"
                              title={`${fmtH(c.form_per_day[d.date])} de formation ce jour (payées, hors taux BS/h)`}
                              data-testid={`heures-form-${c.rd_id}-${d.date}`}>
                              <GraduationCap className="h-2.5 w-2.5" />{fmtH(c.form_per_day[d.date])}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  <td className="sticky right-[86px] z-10 w-[74px] min-w-[74px] border-b border-l-2 border-[var(--border-soft)] bg-[#FDEEE4] px-2 py-2 text-center font-bold tabular-nums text-[var(--shariti-orange)] shadow-[-6px_0_8px_-6px_rgba(24,24,24,0.16)] group-hover:bg-[#FBE0D0]" data-testid={`heures-total-${c.rd_id}`}>
                    <div className="flex flex-col items-center leading-tight">
                      <span>{fmtH(c.total) || "0"}</span>
                      {(c.bs_total || 0) > 0 && (
                        <span className="mt-0.5 text-[9px] font-bold text-[#5D3FD3]" title="Bulletins comptés · productivité BS/h" data-testid={`heures-total-bs-${c.rd_id}`}>
                          {c.bs_total} BS{c.bs_par_heure != null && <span className={bshTone(c.bs_par_heure, c.kind, seuils)}> · {fmtProd(c.bs_par_heure)}/h</span>}
                        </span>
                      )}
                      {(c.total_formation || 0) > 0 && (
                        <span className="mt-0.5 text-[8px] font-bold text-[#0F6E6E]" title="dont heures de formation (payées, exclues du taux BS/h)" data-testid={`heures-total-form-${c.rd_id}`}>
                          dont {fmtH(c.total_formation)} form.
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="sticky right-0 z-10 w-[86px] min-w-[86px] border-b border-l border-[var(--border-soft)] bg-white px-2 py-2 text-center tabular-nums group-hover:bg-[var(--surface-2)]" data-testid={`heures-diff-${c.rd_id}`}>
                    {(() => {
                      const d = fmtDiff(c.diff);
                      if (!d) return <span className="text-[rgba(24,24,24,0.25)]">·</span>;
                      const { txt, cls, Icon, sign } = d;
                      return (
                        <span className={`inline-flex items-center justify-center gap-0.5 whitespace-nowrap font-semibold ${cls}`}
                          title={`${prevMonthLabel} : ${fmtH(c.prev_total) || "0"} h`}>
                          <Icon className="h-3 w-3 shrink-0" />{sign}{txt}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="sticky bottom-0 left-0 z-30 border-t-2 border-r border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink shadow-[6px_0_8px_-6px_rgba(24,24,24,0.16)]">Total / jour</td>
                {visibleDays.map((d) => (
                  <td key={d.date} className={`sticky bottom-0 z-20 border-t-2 border-l border-[var(--border-soft)] px-1 py-2 text-center text-[11px] font-bold tabular-nums ${d.is_today ? "bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]" : "bg-[var(--surface-2)] text-[#181818]"}`} data-testid={`heures-daytotal-${d.date}`}>
                    <div className="flex flex-col items-center leading-none">
                      <span>{fmtH(dayTotals[d.date])}</span>
                      {(dayBsTotals[d.date] || 0) > 0 && <span className="mt-0.5 text-[9px] font-bold text-[#5D3FD3]">{dayBsTotals[d.date]} BS{prodOf(dayBsTotals[d.date], dayTotals[d.date]) != null && <span className={bshTone(prodOf(dayBsTotals[d.date], dayTotals[d.date]), "magasin", seuils)}> · {fmtProd(prodOf(dayBsTotals[d.date], dayTotals[d.date]))}/h</span>}</span>}
                    </div>
                  </td>
                ))}
                <td className="sticky bottom-0 right-[86px] z-30 border-t-2 border-l-2 border-[var(--border-soft)] bg-[var(--shariti-orange)] px-2 py-2 text-center font-bold tabular-nums text-white shadow-[-6px_0_8px_-6px_rgba(24,24,24,0.16)]" data-testid="heures-grandtotal">
                  <div className="flex flex-col items-center leading-tight">
                    <span>{fmtH(data.grand_total) || "0"}</span>
                    {(data.grand_bs_total || 0) > 0 && <span className="mt-0.5 text-[9px] font-bold text-white/90">{(data.grand_bs_total || 0).toLocaleString("fr-FR")} BS{data.grand_bs_par_heure != null ? ` · ${fmtProd(data.grand_bs_par_heure)}/h` : ""}</span>}
                  </div>
                </td>
                <td className="sticky bottom-0 right-0 z-30 border-t-2 border-l border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-2 text-center font-bold tabular-nums" data-testid="heures-grandtotal-diff">
                  {(() => {
                    const d = fmtDiff(data.grand_diff);
                    if (!d) return <span className="text-muted-ink">·</span>;
                    const { txt, cls, Icon, sign } = d;
                    return <span className={`inline-flex items-center justify-center gap-0.5 whitespace-nowrap ${cls}`}><Icon className="h-3 w-3 shrink-0" />{sign}{txt}</span>;
                  })()}
                </td>
              </tr>
            </tfoot>
          </table>
          </div>
          )}
        </>
      )}
      <HeuresCellDialog cell={editCell} onClose={() => setEditCell(null)} onSaved={load} />
    </div>
  );
}

function AnomaliesTab() {
  const [mois, setMois] = useState(monthISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);   // pointage row being corrected

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/pointage/anomalies?mois=${mois}`); setData(r.data); }
    catch { toast.error("Chargement des anomalies impossible"); }
    finally { setLoading(false); }
  }, [mois]);
  useEffect(() => { load(); }, [load]);

  const confirmRow = async (id) => {
    try { await api.post(`/pointage/pointages/${id}/confirm`); toast.success("Valeur confirmée"); load(); }
    catch { toast.error("Confirmation impossible"); }
  };

  const approveExc = async (id) => {
    try {
      const r = await api.post(`/pointage/pointages/${id}/exceptionnel/approve`);
      toast.success(`Pointage exceptionnel validé — ${fmtH(r.data.heures_retenues)} h comptées.`);
      load();
    } catch { toast.error("Validation impossible"); }
  };

  const rejectExc = async (id) => {
    try { await api.post(`/pointage/pointages/${id}/exceptionnel/reject`); toast.success("Pointage exceptionnel refusé — aucune heure comptée."); load(); }
    catch { toast.error("Rejet impossible"); }
  };

  const nbExc = (data?.rows || []).filter((r) => r.statut === "exceptionnel_en_attente").length;

  return (
    <div className="space-y-5" data-testid="anomalies-tab">
      <div className="flex items-center justify-between gap-3">
        <Input type="month" value={mois} onChange={(e) => setMois(e.target.value)} className="h-10 w-[180px] font-semibold" data-testid="anomalies-month" />
        <Button variant="ghost" size="icon" onClick={load} data-testid="anomalies-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {nbExc > 0 && (
        <div className="flex items-start gap-2 rounded-2xl border border-[rgba(185,137,0,0.30)] bg-[rgba(185,137,0,0.07)] px-4 py-3 text-sm text-[#8a6600]" data-testid="anomalies-exc-banner">
          <Hourglass className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <span className="font-bold">{nbExc} pointage(s) exceptionnel(s) en attente de validation.</span>{" "}
            Ces heures ne sont pas comptées tant que vous ne les avez pas approuvées. Approuver = heures comptées ; Refuser = 0 h retenue.
          </span>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
      ) : !data?.rows?.length ? (
        <EmptyState icon={CheckCircle2} title="Aucune anomalie" hint="Tous les pointages du mois sont complets et cohérents." testid="anomalies-empty" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]">
          <table className="w-full min-w-[860px] text-sm" data-testid="anomalies-table">
            <thead>
              <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-2)] text-[10px] uppercase tracking-wide text-muted-ink">
                <th className="px-3 py-2.5 text-left font-bold">Date</th>
                <th className="px-3 py-2.5 text-left font-bold">Collaborateur</th>
                <th className="px-3 py-2.5 text-left font-bold">Créneau</th>
                <th className="px-3 py-2.5 text-left font-bold">Pointé (réel)</th>
                <th className="px-3 py-2.5 text-left font-bold">Proposé (retenu)</th>
                <th className="px-3 py-2.5 text-right font-bold">Heures</th>
                <th className="px-3 py-2.5 text-left font-bold">Statut</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const isExc = r.statut === "exceptionnel_en_attente";
                return (
                <tr key={r.pointage_id} className={`border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--surface-2)] ${isExc ? "bg-[rgba(185,137,0,0.04)]" : ""}`} data-testid={`anomalie-row-${r.code_rd}`}>
                  <td className="px-3 py-2.5 tabular-nums">{r.date}</td>
                  <td className="px-3 py-2.5"><div className="font-semibold text-ink">{r.rd_name}</div><CodeBadge code={r.code_rd} /></td>
                  <td className="px-3 py-2.5 tabular-nums text-muted-ink">{r.creneau_debut}–{r.creneau_fin}</td>
                  <td className="px-3 py-2.5 tabular-nums text-muted-ink">
                    {r.arrivee_pointee || "—"} → {r.depart_pointe || "—"}
                    {isExc && (
                      <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] font-semibold text-[#8a6600]" data-testid={`anomalie-motif-${r.code_rd}`}>
                        <Navigation className="h-3 w-3" />
                        {MOTIF_LABEL[r.exceptionnel_motif] || "Exceptionnel"}
                        {r.gps_distance_m != null && <span className="text-muted-ink">· {r.gps_distance_m} m du spot</span>}
                        {r.gps_precision_m != null && <span className="text-muted-ink">· ± {r.gps_precision_m} m</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-semibold tabular-nums text-[#2F6FED]">{r.arrivee_retenue || "—"} → {r.depart_retenu || "—"}</td>
                  <td className="px-3 py-2.5 text-right font-bold tabular-nums text-[#2F6FED]">{isExc ? "—" : fmtH(r.heures_retenues)}</td>
                  <td className="px-3 py-2.5"><StatutBadge statut={r.statut} /></td>
                  <td className="px-3 py-2.5">
                    <div className="flex justify-end gap-1">
                      {isExc ? (
                        <>
                          <Button variant="ghost" size="sm" className="h-8 text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.10)]" onClick={() => approveExc(r.pointage_id)} data-testid={`anomalie-approve-${r.code_rd}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Approuver</Button>
                          <Button variant="ghost" size="sm" className="h-8 text-[#9B2226] hover:bg-[rgba(214,69,69,0.10)]" onClick={() => rejectExc(r.pointage_id)} data-testid={`anomalie-reject-${r.code_rd}`}><XCircle className="mr-1 h-3.5 w-3.5" />Rejeter</Button>
                        </>
                      ) : (
                        <>
                          {r.statut === "deduit_a_confirmer" && (
                            <Button variant="ghost" size="sm" className="h-8 text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.10)]" onClick={() => confirmRow(r.pointage_id)} data-testid={`anomalie-confirm-${r.code_rd}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Confirmer</Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-8" onClick={() => setEdit(r)} data-testid={`anomalie-edit-${r.code_rd}`}><Pencil className="mr-1 h-3.5 w-3.5" />Corriger</Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CorrectionDialog row={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />
    </div>
  );
}

function CorrectionDialog({ row, onClose, onDone }) {
  const [mode, setMode] = useState("bornes");   // "bornes" | "heures"
  const [arr, setArr] = useState("");
  const [dep, setDep] = useState("");
  const [heuresStr, setHeuresStr] = useState("");
  const [formationStr, setFormationStr] = useState("");   // dont formation (h), part exclue du BS/h
  const [motif, setMotif] = useState("");
  const [spotId, setSpotId] = useState("");        // [Pb3] spot corrigé
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (row) {
      setArr(row.arrivee_corrigee || row.arrivee_pointee || row.arrivee_retenue || "");
      setDep(row.depart_corrigee || row.depart_pointe || row.depart_retenu || "");
      setHeuresStr(row.heures_manuelle && row.heures_retenues != null
        ? Number(row.heures_retenues).toFixed(2).replace(".", ",") : "");
      setFormationStr(row.heures_formation ? Number(row.heures_formation).toFixed(2).replace(".", ",") : "");
      setMotif("");
      setSpotId(row.spot_id || "");
      setMode(row.heures_manuelle ? "heures" : "bornes");
      // Spots planifiés le même jour (pour réaffecter au bon spot).
      api.get(`/pointage/bookings?date=${row.date}`).then((r) => setBookings(r.data || [])).catch(() => setBookings([]));
    }
  }, [row]);
  if (!row) return null;
  const hasPointage = !!row.pointage_id;
  const brutArr = (row.original && row.original.arrivee_pointee) || row.arrivee_pointee || "—";
  const brutDep = (row.original && row.original.depart_pointe) || row.depart_pointe || "—";
  const hInit = row.original && row.original.heures_retenues;
  const hasBrut = !!(row.arrivee_pointee || row.depart_pointe || (row.original && Object.keys(row.original || {}).length));
  const submit = async () => {
    // Identifiants : pointage existant OU (affectation + collaborateur) pour créer.
    const ident = hasPointage
      ? { pointage_id: row.pointage_id }
      : { affectation_id: row.affectation_id, booking_id: row.booking_id, rd_id: row.rd_id,
          creneau_debut: row.creneau_debut, creneau_fin: row.creneau_fin };
    const payload = { ...ident, motif: motif.trim() || undefined };
    if (spotId && spotId !== row.spot_id) payload.spot_id = spotId;
    // Part FORMATION (h) : comptée dans le total payé mais EXCLUE du taux BS/h.
    let formationH = 0;
    if ((formationStr || "").trim()) {
      formationH = parseFloat((formationStr || "").replace(",", "."));
      if (isNaN(formationH) || formationH < 0) { toast.error("Formation : saisissez un nombre d'heures valide (ex. 4)."); return; }
    }
    if (mode === "heures") {
      const h = parseFloat((heuresStr || "").replace(",", "."));
      if (isNaN(h)) { toast.error("Saisissez un nombre d'heures (ex. 4,97)."); return; }
      if (h < 0 || h > 24) { toast.error("Le total d'heures doit être entre 0 et 24."); return; }
      if (formationH > h) { toast.error("La formation ne peut pas dépasser le total d'heures."); return; }
      payload.heures = h;
    } else {
      if (!arr && !dep) { toast.error("Renseignez au moins une borne (arrivée ou départ)."); return; }
      const computed = hoursFromBornes(arr, dep);
      if (computed != null && formationH > computed + 0.001) { toast.error("La formation ne peut pas dépasser la durée pointée."); return; }
      payload.arrivee = arr || null;
      payload.depart = dep || null;
    }
    payload.heures_formation = formationH;
    setBusy(true);
    try {
      const r = await api.post(`/pointage/pointages/saisie-heures`, payload);
      if (r.data?.warning) toast.warning(r.data.warning);
      const sc = r.data?.spot_change;
      if (sc) {
        toast.success(`Spot réaffecté à « ${sc.nouveau_spot} »`
          + (sc.doublons_supprimes ? ` — ${sc.doublons_supprimes} doublon(s) supprimé(s)` : ""));
      } else {
        toast.success(hasPointage ? "Heures enregistrées — le brut éventuel reste conservé."
          : "Heures saisies pour ce collaborateur.");
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };
  // Durée (en heures décimales) entre deux bornes « HH:MM ». null si invalide ou si le
  // départ n'est pas postérieur à l'arrivée (pas de durée exploitable).
  const hoursFromBornes = (a, d) => {
    if (!a || !d) return null;
    const [ah, am] = String(a).split(":").map(Number);
    const [dh, dm] = String(d).split(":").map(Number);
    if ([ah, am, dh, dm].some((n) => Number.isNaN(n))) return null;
    const mins = (dh * 60 + dm) - (ah * 60 + am);
    if (mins <= 0) return null;
    return mins / 60;
  };
  // Au passage sur « Total d'heures » : on PRÉ-REMPLIT automatiquement le nombre d'heures
  // que représente le pointage brut (Arrivée→Départ), pour éviter tout calcul manuel dans
  // une autre application. On ne remplit que si le champ est vide (jamais d'écrasement d'une
  // saisie volontaire). L'admin/CDP reste libre d'ajuster ou non la valeur avant paie.
  const switchMode = (value) => {
    if (value === "heures" && !String(heuresStr).trim()) {
      const computed = hoursFromBornes(arr, dep);
      if (computed != null) setHeuresStr(computed.toFixed(2).replace(".", ","));
    }
    setMode(value);
  };
  const ToggleBtn = ({ value, label }) => (
    <button type="button" onClick={() => switchMode(value)} data-testid={`correction-mode-${value}`}
      className={`min-w-0 flex-1 truncate rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${mode === value
        ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.10)] text-[#8a4b1f]"
        : "border-[var(--border-soft)] text-muted-ink hover:bg-[var(--surface-2)]"}`}>
      {label}
    </button>
  );
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="grid max-h-[90vh] w-[calc(100vw-2rem)] max-w-md gap-3 overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="correction-dialog">
        <DialogHeader className="min-w-0"><DialogTitle className="flex items-center gap-2 pr-6"><Pencil className="h-5 w-5 shrink-0 text-[#8a6600]" /><span className="min-w-0 [overflow-wrap:anywhere]">{hasPointage ? "Corriger le pointage" : "Saisir les heures"} — {row.rd_name}</span></DialogTitle></DialogHeader>
        {hasBrut ? (
          <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5 text-xs" data-testid="correction-original">
            <div className="mb-1 flex items-center gap-1.5 font-semibold text-ink"><History className="h-3.5 w-3.5" />Pointage brut (immuable)</div>
            <span className="text-muted-ink">Arrivée <b className="text-ink">{brutArr}</b> · Départ <b className="text-ink">{brutDep}</b>{hInit != null && <> · <b className="text-ink">{Number(hInit).toFixed(2).replace(".", ",")} h</b> initiales</>}</span>
          </div>
        ) : (
          <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5 text-xs text-muted-ink" data-testid="correction-no-pointage">
            Aucun pointage enregistré ce jour. Saisissez directement les heures réellement travaillées.
          </div>
        )}
        <p className="text-sm text-muted-ink">Créneau {row.creneau_debut}–{row.creneau_fin}. La valeur saisie devient la référence de paie{hasBrut ? " ; le brut reste consultable et intact" : ""}.</p>
        {/* Explicabilité : QUE S'EST-IL PASSÉ + D'OÙ VIENNENT LES HEURES (qui / quand) */}
        {row.explication && (
          <div className="rounded-lg border border-[rgba(214,69,69,0.28)] bg-[rgba(214,69,69,0.06)] p-2.5 text-xs" data-testid="correction-explication">
            <div className="mb-1 flex items-center gap-1.5 font-semibold text-[#9B2226]"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />Que s'est-il passé ?</div>
            <span className="text-ink [overflow-wrap:anywhere]">{row.explication}</span>
          </div>
        )}
        {row.provenance && row.provenance.label && (
          <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5 text-xs" data-testid="correction-provenance">
            <div className="mb-1 flex items-center gap-1.5 font-semibold text-ink"><Info className="h-3.5 w-3.5 shrink-0" />D'où viennent ces heures ?</div>
            <span className="text-muted-ink [overflow-wrap:anywhere]">
              {row.provenance.label}
              {row.provenance.by ? <> · par <b className="text-ink">{row.provenance.by}</b></> : null}
              {frDateTimeShort(row.provenance.at) ? <> · le <b className="text-ink">{frDateTimeShort(row.provenance.at)}</b></> : null}
              {row.provenance.motif ? <><br />Motif : <i>{row.provenance.motif}</i></> : null}
              {row.provenance.confirme_collaborateur ? <><br />Confirmé par le collaborateur.</> : null}
            </span>
          </div>
        )}
        {/* [Pb3] Réaffectation au bon spot (cas changement de spot de dernière minute) */}
        <div data-testid="correction-spot-block">
          <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Spot <span className="font-normal normal-case text-muted-ink">(réaffecter si erroné)</span></label>
          <Select value={spotId || row.spot_id || ""} onValueChange={setSpotId}>
            <SelectTrigger className="mt-1 w-full min-w-0 [&>span]:line-clamp-1 [&>span]:text-left" data-testid="correction-spot-select"><SelectValue placeholder="Choisir le bon spot du jour…" /></SelectTrigger>
            <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]">
              {row.spot_id && !bookings.some((b) => b.spot_id === row.spot_id) && (
                <SelectItem value={row.spot_id} data-testid="correction-spot-current">{row.spot_name || "Spot actuel"} (actuel)</SelectItem>
              )}
              {bookings.map((b) => (
                <SelectItem key={b.spot_id} value={b.spot_id} data-testid={`correction-spot-opt-${b.spot_id}`}>
                  {b.spot_name}{b.association_acronym ? ` — ${b.association_acronym}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {spotId && spotId !== row.spot_id && (
            <p className="mt-1 flex items-start gap-1 text-[11px] text-[#8a4b1f]" data-testid="correction-spot-warning">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              Le pointage sera déplacé vers ce spot. Un éventuel doublon vide (sans pointage) sur ce spot sera annulé automatiquement.
            </p>
          )}
        </div>
        <div className="flex gap-2" data-testid="correction-mode-toggle">
          <ToggleBtn value="bornes" label="Arrivée / Départ" />
          <ToggleBtn value="heures" label="Total d'heures" />
        </div>
        {mode === "bornes" ? (
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Arrivée</label><Input type="time" value={arr} onChange={(e) => setArr(e.target.value)} className="mt-1" data-testid="correction-arr" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Départ</label><Input type="time" value={dep} onChange={(e) => setDep(e.target.value)} className="mt-1" data-testid="correction-dep" /></div>
          </div>
        ) : (
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Nombre d'heures</label>
            <Input inputMode="decimal" value={heuresStr} onChange={(e) => setHeuresStr(e.target.value)} placeholder="Ex. 4,97" className="mt-1" data-testid="correction-heures" />
            {hoursFromBornes(arr, dep) != null ? (
              <p className="mt-1 text-[11px] text-muted-ink">
                Calculé depuis le pointage <b className="text-ink">{arr} → {dep}</b> ={" "}
                <button type="button"
                  onClick={() => setHeuresStr(hoursFromBornes(arr, dep).toFixed(2).replace(".", ","))}
                  data-testid="correction-heures-autofill"
                  className="font-bold text-[var(--shariti-orange)] underline decoration-dotted underline-offset-2 hover:text-[#d9631f]">
                  {hoursFromBornes(arr, dep).toFixed(2).replace(".", ",")} h
                </button>. Ajustez la valeur si besoin avant d'enregistrer.
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-muted-ink">Total travaillé, deux décimales (ex. 4,97). L'arrivée/départ ne sont pas requis.</p>
            )}
          </div>
        )}
        <div data-testid="correction-formation-block">
          <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">
            Dont formation <span className="font-normal normal-case text-muted-ink">(h, facultatif)</span>
          </label>
          <Input inputMode="decimal" value={formationStr} onChange={(e) => setFormationStr(e.target.value)}
            placeholder="Ex. 4" className="mt-1" data-testid="correction-formation" />
          <p className="mt-1 flex items-start gap-1 text-[11px] text-muted-ink">
            <Info className="mt-0.5 h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
            Heures de formation comprises dans le total ci-dessus. Elles restent payées mais sont <b className="text-ink">exclues du taux BS/h</b> (terrain uniquement).
          </p>
        </div>
        <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Motif <span className="font-normal normal-case text-muted-ink">(facultatif)</span></label>
          <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. oubli de dépointage confirmé par le CDP" className="mt-1 min-h-[56px]" data-testid="correction-motif" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="correction-cancel">Fermer</Button>
          <Button className="bg-[var(--shariti-orange)] font-semibold hover:bg-[#d9631f]" onClick={submit} disabled={busy} data-testid="correction-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AnnulerDialog({ row, onClose, onDone }) {
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (row) setMotif(""); }, [row]);
  if (!row) return null;
  const submit = async () => {
    if (!motif.trim()) { toast.error("Le motif est obligatoire pour annuler."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/pointage/pointages/${row.pointage_id}/annuler`, { motif });
      if (r.data?.warning) toast.warning(r.data.warning);
      toast.success("Pointage annulé (tracé) — aucune suppression physique.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Annulation impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="annuler-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban className="h-5 w-5 text-[#9B2226]" />Annuler le pointage — {row.rd_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-ink">La ligne sera marquée <b>annulée</b> (0 h en paie). Aucune suppression physique : le pointage brut reste en base et reste consultable.</p>
        <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Motif <span className="text-[#9B2226]">*</span></label>
          <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. doublon / pointage erroné" className="mt-1 min-h-[64px]" data-testid="annuler-motif" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="annuler-cancel">Fermer</Button>
          <Button className="bg-[#9B2226] font-semibold text-white hover:bg-[#7f1c1f]" onClick={submit} disabled={busy} data-testid="annuler-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer l'annulation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Réactivation d'une ligne ANNULÉE (annulation erronée) : lève l'annulation tracée
// pour que les heures RECOMPTENT, et permet au passage de corriger le créneau
// provisionnel s'il était faux (ex. 16:00–20:00 → 10:00–14:00).
function ReactiverDialog({ row, onClose, onDone }) {
  const [cd, setCd] = useState("");
  const [cf, setCf] = useState("");
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (row) { setCd(row.creneau_debut || ""); setCf(row.creneau_fin || ""); setMotif(""); }
  }, [row]);
  if (!row) return null;
  const creneauChanged = (!!cd && cd !== row.creneau_debut) || (!!cf && cf !== row.creneau_fin);
  const submit = async () => {
    setBusy(true);
    try {
      // 1) Corrige le créneau provisionnel si l'admin l'a modifié (répercuté sur le pointage).
      if (creneauChanged) {
        await api.put(`/pointage/affectations/${row.affectation_id}`, { creneau_debut: cd, creneau_fin: cf });
      }
      // 2) Lève l'annulation → les heures recomptent pour la paie/prépaie.
      const r = await api.post(`/pointage/pointages/${row.pointage_id}/reactiver`, { motif: motif.trim() || undefined });
      if (r.data?.warning) toast.warning(r.data.warning);
      toast.success("Ligne réactivée — les heures sont de nouveau comptées. Ajustez-les via « Corriger » si besoin.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Réactivation impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reactiver-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 shrink-0 text-[#0F5A2F]" /><span className="min-w-0 [overflow-wrap:anywhere]">Réactiver la ligne — {row.rd_name}</span></DialogTitle></DialogHeader>
        <p className="text-sm text-muted-ink">Cette ligne avait été <b>annulée</b>. La réactiver la remet en compte pour la paie/prépaie. La trace de l'annulation reste conservée dans l'historique (audit).</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Arrivée prévue</label><Input type="time" value={cd} onChange={(e) => setCd(e.target.value)} className="mt-1" data-testid="reactiver-cd" /></div>
          <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Départ prévu</label><Input type="time" value={cf} onChange={(e) => setCf(e.target.value)} className="mt-1" data-testid="reactiver-cf" /></div>
        </div>
        <p className="flex items-start gap-1 text-[11px] text-muted-ink"><Info className="mt-0.5 h-3 w-3 shrink-0" />Corrigez le créneau si l'horaire provisionnel était erroné (ex. 16:00–20:00 → 10:00–14:00).</p>
        <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Motif <span className="font-normal normal-case text-muted-ink">(facultatif)</span></label>
          <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. annulation par erreur — collaboratrice réellement présente" className="mt-1 min-h-[56px]" data-testid="reactiver-motif" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reactiver-cancel">Fermer</Button>
          <Button className="bg-[#0F5A2F] font-semibold text-white hover:bg-[#0c4a27]" onClick={submit} disabled={busy} data-testid="reactiver-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Réactiver la ligne"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Réouverture d'un pointage : réinitialise le départ (ou toute la journée) pour
// que le collaborateur puisse RE-POINTER (cas du départ cliqué par erreur).
function ReopenDialog({ row, onClose, onDone }) {
  const [scope, setScope] = useState("depart");
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (row) { setScope("depart"); setMotif(""); } }, [row]);
  if (!row) return null;
  const submit = async () => {
    if (!motif.trim()) { toast.error("Le motif est obligatoire."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/pointage/pointages/${row.pointage_id}/reouvrir`, { scope, motif });
      if (r.data?.warning) toast.warning(r.data.warning);
      toast.success(r.data?.message || "Pointage réinitialisé.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Réinitialisation impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reopen-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-[#0F5A2F]" />Réinitialiser le pointage — {row.rd_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-ink">
          Permet au collaborateur de <b>pointer à nouveau</b> (ex. départ cliqué par erreur alors qu'il est encore sur le terrain).
          Le pointage brut initial reste conservé et consultable.
        </p>
        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Que réinitialiser ?</label>
          <div className="grid grid-cols-1 gap-1.5">
            <button type="button" onClick={() => setScope("depart")} data-testid="reopen-scope-depart"
              className={`rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors ${scope === "depart" ? "border-[#0F5A2F] bg-[rgba(31,157,85,0.08)] text-[#0F5A2F]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
              <b>Le départ seulement</b> — la journée repasse « en mission », l'arrivée est conservée (recommandé)
            </button>
            <button type="button" onClick={() => setScope("jour")} data-testid="reopen-scope-jour"
              className={`rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors ${scope === "jour" ? "border-[#0F5A2F] bg-[rgba(31,157,85,0.08)] text-[#0F5A2F]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
              <b>Toute la journée</b> — efface arrivée + départ (le collaborateur repointe tout)
            </button>
          </div>
        </div>
        <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Motif <span className="text-[#9B2226]">*</span></label>
          <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. départ cliqué par erreur, collaborateur encore sur le terrain" className="mt-1 min-h-[64px]" data-testid="reopen-motif" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reopen-cancel">Fermer</Button>
          <Button className="bg-[#0F5A2F] font-semibold text-white hover:bg-[#0b4425]" onClick={submit} disabled={busy} data-testid="reopen-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Réinitialiser"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function OriginalDialog({ row, onClose }) {
  if (!row) return null;
  const o = row.original || {};
  const gps = o.gps_arrivee || row.gps_arrivee || null;
  const hInit = o.heures_retenues;
  return (
    <Dialog open={!!row} onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="original-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" />Pointage original — {row.rd_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-ink">Valeurs brutes conservées (immuables). La ligne affiche désormais la valeur corrigée.</p>
        <div className="space-y-1.5 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-sm">
          <div className="flex justify-between"><span className="text-muted-ink">Arrivée pointée (brut)</span><b className="text-ink">{o.arrivee_pointee || row.arrivee_pointee || "—"}</b></div>
          <div className="flex justify-between"><span className="text-muted-ink">Départ pointé (brut)</span><b className="text-ink">{o.depart_pointe || row.depart_pointe || "—"}</b></div>
          <div className="flex justify-between"><span className="text-muted-ink">Heures retenues initiales</span><b className="text-ink">{hInit != null ? `${Number(hInit).toFixed(2).replace(".", ",")} h` : "—"}</b></div>
          <div className="flex justify-between"><span className="text-muted-ink">Statut initial</span><b className="text-ink">{o.statut || "—"}</b></div>
          <div className="flex justify-between"><span className="text-muted-ink">Position</span><b className="text-ink">{gps ? `GPS vérifiée · ${gps.distance_m ?? 0} m` : "saisie manuelle"}</b></div>
        </div>
        {(row.corrections || []).length > 0 && (
          <div className="text-xs text-muted-ink" data-testid="original-corrections">
            <div className="mb-1 font-semibold text-ink">Historique</div>
            {(row.corrections || []).map((c, i) => (
              <div key={i} className="py-0.5">• {c.champ} : {String(c.ancienne ?? "—")} → <b className="text-ink">{String(c.nouvelle ?? "—")}</b>{c.motif ? ` — ${c.motif}` : ""} <span className="opacity-70">({c.by})</span></div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="original-close">Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Saisie GROUPÉE d'heures de formation : un jour, plusieurs collaborateurs, en un geste.
// Les heures restent payées (total) mais sont EXCLUES du taux BS/h.
function FormationGroupeeDialog({ open, date, rows = [], onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [jour, setJour] = useState(date);
  const [heuresStr, setHeuresStr] = useState("");
  const [motif, setMotif] = useState("");
  const [query, setQuery] = useState("");
  const [rds, setRds] = useState([]);
  const [selected, setSelected] = useState({});   // id -> nom (map pour garder les chips)
  const [busy, setBusy] = useState(false);

  // Collaborateurs du jour (hors indépendants) proposés d'emblée.
  const dayPeople = (rows || []).filter(
    (r) => r.rd_id && r.population !== "independant" && r.readonly_reason !== "independant"
  ).map((r) => ({ id: r.rd_id, name: r.rd_name, code: r.code_rd }));
  const dedupDay = Array.from(new Map(dayPeople.map((p) => [p.id, p])).values());

  useEffect(() => {
    if (open) { setJour(date > today ? today : date); setHeuresStr(""); setMotif(""); setQuery(""); setSelected({}); }
  }, [open, date, today]);
  useEffect(() => {
    if (!open || !query.trim()) { setRds([]); return; }
    const t = setTimeout(() => {
      api.get(`/pointage/rds?actifs_terrain=true&q=${encodeURIComponent(query.trim())}`)
        .then((r) => setRds((r.data || []).filter((rd) => rd.population !== "independant"))).catch(() => setRds([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, query]);

  const toggle = (id, name) => setSelected((s) => {
    const n = { ...s };
    if (n[id]) delete n[id]; else n[id] = name;
    return n;
  });
  const selectAllDay = () => setSelected((s) => {
    const n = { ...s };
    dedupDay.forEach((p) => { n[p.id] = p.name; });
    return n;
  });
  const clearSel = () => setSelected({});

  const ids = Object.keys(selected);
  const submit = async () => {
    const h = parseFloat((heuresStr || "").replace(",", "."));
    if (isNaN(h) || h < 0 || h > 24) { toast.error("Saisissez un nombre d'heures valide (0 à 24, ex. 4)."); return; }
    if (ids.length === 0) { toast.error("Sélectionnez au moins un collaborateur."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/pointage/pointages/formation-groupee`, { jour, rd_ids: ids, heures: h, motif: motif.trim() || undefined });
      if (r.data?.warning) toast.warning(r.data.warning);
      const up = (r.data?.updated || []).length, cr = (r.data?.created || []).length, sk = (r.data?.skipped || []).length;
      toast.success(`Formation enregistrée — ${Number(h).toFixed(2).replace(".", ",")} h`, {
        description: `${up} mis à jour · ${cr} créé(s)${sk ? ` · ${sk} ignoré(s)` : ""}`,
      });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto overflow-x-hidden" data-testid="formation-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-[#0F6E6E]" />Heures de formation (groupé)</DialogTitle></DialogHeader>
        <div className="flex items-start gap-2 rounded-lg border border-[rgba(15,110,110,0.25)] bg-[rgba(15,110,110,0.06)] p-2.5 text-xs text-[#0F6E6E]">
          <Info className="mt-0.5 h-4 w-4 shrink-0" /><span>Marquez en une fois des heures de <b>formation</b> pour plusieurs collaborateurs un même jour. Elles sont <b>payées</b> (comptées dans le total) mais <b>exclues du taux BS/h</b>. Les heures terrain déjà pointées sont conservées (total = terrain + formation). Mettre <b>0</b> retire la formation.</span>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Jour</label>
              <Input type="date" max={today} value={jour} onChange={(e) => setJour(e.target.value)} className="mt-1" data-testid="formation-date" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Heures de formation</label>
              <Input inputMode="decimal" value={heuresStr} onChange={(e) => setHeuresStr(e.target.value)} placeholder="Ex. 4" className="mt-1" data-testid="formation-hours" /></div>
          </div>

          {dedupDay.length > 0 && (
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Collaborateurs du jour</label>
                <button type="button" onClick={selectAllDay} className="text-[11px] font-semibold text-[#0F6E6E] hover:underline" data-testid="formation-select-all">Tout sélectionner</button>
              </div>
              <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-[var(--border-soft)]">
                {dedupDay.map((p) => (
                  <button key={p.id} type="button" onClick={() => toggle(p.id, p.name)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--surface-2)] ${selected[p.id] ? "bg-[rgba(15,110,110,0.10)]" : ""}`}
                    data-testid={`formation-day-${p.code || p.id}`}>
                    <span className="min-w-0 flex-1 truncate font-semibold text-ink">{p.name}{p.code ? <span className="font-normal text-muted-ink"> · {p.code}</span> : null}</span>
                    {selected[p.id] ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[#0F6E6E]" /> : <span className="h-4 w-4 shrink-0 rounded-full border border-[var(--border-soft)]" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Ajouter d'autres collaborateurs</label>
            <Input placeholder="Rechercher par nom ou code…" value={query} onChange={(e) => setQuery(e.target.value)} className="mt-1" data-testid="formation-search" />
            {query.trim() && (
              <div className="mt-2 max-h-36 overflow-y-auto rounded-lg border border-[var(--border-soft)]">
                {rds.length === 0 && <div className="px-3 py-2 text-sm text-muted-ink">Aucun collaborateur.</div>}
                {rds.map((rd) => (
                  <button key={rd.id} type="button" onClick={() => toggle(rd.id, rd.name)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--surface-2)] ${selected[rd.id] ? "bg-[rgba(15,110,110,0.10)]" : ""}`}
                    data-testid={`formation-rd-${rd.code}`}>
                    <span className="min-w-0 flex-1 truncate font-semibold text-ink">{rd.name}{rd.code ? <span className="font-normal text-muted-ink"> · {rd.code}</span> : null}</span>
                    {selected[rd.id] ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[#0F6E6E]" /> : <span className="h-4 w-4 shrink-0 rounded-full border border-[var(--border-soft)]" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {ids.length > 0 && (
            <div className="rounded-lg bg-[var(--surface-2)] p-2" data-testid="formation-selected">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">{ids.length} sélectionné(s)</span>
                <button type="button" onClick={clearSel} className="text-[11px] font-semibold text-[#9B2226] hover:underline" data-testid="formation-clear">Tout retirer</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {ids.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-ink">
                    {selected[id]}
                    <button type="button" onClick={() => toggle(id, selected[id])} className="text-muted-ink hover:text-[#9B2226]"><XCircle className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Motif <span className="font-normal normal-case text-muted-ink">(facultatif)</span></label>
            <Input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. formation produit" className="mt-1" data-testid="formation-motif" /></div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="formation-cancel">Annuler</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#0F6E6E] text-white hover:opacity-90" data-testid="formation-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><GraduationCap className="mr-1.5 h-4 w-4" />Enregistrer la formation</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function RetroactiveDialog({ open, date, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [retroDate, setRetroDate] = useState(date);
  const [bookings, setBookings] = useState([]);
  const [bookingId, setBookingId] = useState("");
  const [rdQuery, setRdQuery] = useState("");
  const [rds, setRds] = useState([]);
  const [rdId, setRdId] = useState("");
  const [cd, setCd] = useState("10:00");
  const [cf, setCf] = useState("20:00");
  const [arr, setArr] = useState("10:00");
  const [dep, setDep] = useState("");
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setRetroDate(date > today ? today : date); setBookingId(""); setRdId(""); setRdQuery(""); setArr("10:00"); setDep(""); setMotif(""); } }, [open, date, today]);
  useEffect(() => {
    if (!open || !retroDate) return;
    api.get(`/pointage/bookings?date=${retroDate}`).then((r) => setBookings(r.data)).catch(() => setBookings([]));
  }, [open, retroDate]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get(`/pointage/rds?actifs_terrain=true${rdQuery ? `&q=${encodeURIComponent(rdQuery)}` : ""}`).then((r) => setRds(r.data)).catch(() => setRds([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, rdQuery]);

  const submit = async () => {
    if (!bookingId) { toast.error("Choisissez un spot de la journée."); return; }
    if (!rdId) { toast.error("Choisissez un collaborateur."); return; }
    if (!arr && !dep) { toast.error("Renseignez au moins une borne (arrivée ou départ)."); return; }
    if (!motif.trim()) { toast.error("Le motif est obligatoire."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/pointage/pointages/retroactif`, { booking_id: bookingId, rd_id: rdId, arrivee: arr || null, depart: dep || null, creneau_debut: cd, creneau_fin: cf, motif });
      if (r.data?.warning) toast.warning(r.data.warning);
      const h = r.data?.heures_retenues;
      toast.success(`Saisie rétroactive enregistrée${h != null ? ` — ${Number(h).toFixed(2).replace(".", ",")} h retenues` : ""}.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Saisie impossible"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto overflow-x-hidden" data-testid="retro-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-[#5D3FD3]" />Saisie rétroactive d'heures</DialogTitle></DialogHeader>
        <div className="flex items-start gap-2 rounded-lg border border-[rgba(93,63,211,0.25)] bg-[rgba(93,63,211,0.06)] p-2.5 text-xs text-[#5D3FD3]">
          <Info className="mt-0.5 h-4 w-4 shrink-0" /><span>Heures travaillées <b>avant le câblage</b>. Calcul par le moteur (arrivée = max(saisie, début), fin ≤ fin de créneau, plafond/jour). Statut <b>« Saisie admin »</b>, position « saisie manuelle », jour futur interdit.</span>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Date (passée)</label>
              <Input type="date" max={today} value={retroDate} onChange={(e) => { setRetroDate(e.target.value); setBookingId(""); }} className="mt-1" data-testid="retro-date" /></div>
            <div className="flex items-end pb-1 text-xs text-muted-ink">{retroDate ? frenchDateLong(retroDate) : ""}</div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Spot de la journée</label>
            <Select value={bookingId} onValueChange={setBookingId}>
              <SelectTrigger className="mt-1 w-full [&>span]:line-clamp-1 [&>span]:text-left" data-testid="retro-booking-select"><SelectValue placeholder="Choisir un spot booké ce jour-là…" /></SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]">
                {bookings.length === 0 && <div className="px-3 py-2 text-sm text-muted-ink">Aucun spot booké ce jour-là.</div>}
                {bookings.map((b) => (
                  <SelectItem key={b.booking_id} value={b.booking_id} textValue={b.spot_name} data-testid={`retro-booking-${b.booking_id}`}
                    className="[&>span:last-child]:flex [&>span:last-child]:min-w-0 [&>span:last-child]:flex-1 [&>span:last-child]:items-baseline [&>span:last-child]:gap-1.5">
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{b.spot_name}{b.association_acronym ? ` — ${b.association_acronym}` : ""}</span>
                    <span className="shrink-0 whitespace-nowrap text-[11px] font-semibold text-muted-ink">· {b.affectations_count} affecté(s)</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Collaborateur (salariés &amp; indépendants)</label>
            <Input placeholder="Rechercher par nom ou code…" value={rdQuery} onChange={(e) => setRdQuery(e.target.value)} className="mt-1" data-testid="retro-rd-search" />
            <div className="mt-2 max-h-36 overflow-y-auto rounded-lg border border-[var(--border-soft)]">
              {rds.length === 0 && <div className="px-3 py-2 text-sm text-muted-ink">Aucun collaborateur.</div>}
              {rds.map((rd) => (
                <button key={rd.id} onClick={() => setRdId(rd.id)}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--surface-2)] ${rdId === rd.id ? "bg-[rgba(93,63,211,0.10)]" : ""}`}
                  data-testid={`retro-rd-${rd.code}`}>
                  <span className="min-w-0 flex-1 truncate font-semibold text-ink">{rd.name}
                    {rd.population === "independant" && <span className="ml-1 rounded bg-[rgba(93,63,211,0.12)] px-1 text-[9px] font-bold uppercase text-[#5D3FD3]">Indé</span>}</span>
                  <CodeBadge code={rd.code} />
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Début créneau</label><Input type="time" value={cd} onChange={(e) => setCd(e.target.value)} className="mt-1" data-testid="retro-cd" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Fin créneau</label><Input type="time" value={cf} onChange={(e) => setCf(e.target.value)} className="mt-1" data-testid="retro-cf" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Arrivée</label><Input type="time" value={arr} onChange={(e) => setArr(e.target.value)} className="mt-1" data-testid="retro-arr" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Départ</label><Input type="time" value={dep} onChange={(e) => setDep(e.target.value)} className="mt-1" data-testid="retro-dep" /></div>
          </div>
          <div><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Motif <span className="text-[#9B2226]">*</span></label>
            <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. heures travaillées avant la mise en place du pointage" className="mt-1 min-h-[64px]" data-testid="retro-motif" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="retro-cancel">Fermer</Button>
          <Button className="bg-[#5D3FD3] font-semibold text-white hover:bg-[#4b31ab]" onClick={submit} disabled={busy} data-testid="retro-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer la saisie"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------------------------------------- Confirmations */
function ResponsablesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [designOpen, setDesignOpen] = useState(false);
  const [membersFor, setMembersFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/pointage/responsables`); setData(r.data); }
    catch { toast.error("Chargement des responsables impossible"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (row, enabled) => {
    setBusyId(row.id);
    try {
      await api.post(`/pointage/responsables/${row.id}/habilitation`, { enabled });
      toast.success(enabled
        ? `${row.nom} habilité(e) à la validation des heures`
        : `Habilitation retirée à ${row.nom}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    } finally { setBusyId(null); }
  };

  const copy = async (lien) => {
    try { await navigator.clipboard.writeText(lien); toast.success("Lien copié"); }
    catch { toast.error("Copie impossible"); }
  };

  const setAccess = async (row, mode) => {
    setBusyId(row.id);
    try {
      await api.post(`/pointage/responsables/${row.id}/access-mode`, { access_mode: mode });
      toast.success(mode === "active" ? `Accès complet rétabli pour ${row.nom}`
        : mode === "readonly" ? `${row.nom} passé(e) en lecture seule`
        : `Accès désactivé pour ${row.nom}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Modification de l'accès impossible");
    } finally { setBusyId(null); }
  };

  const s = data || {};
  return (
    <div className="space-y-5" data-testid="responsables-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="max-w-xl text-sm text-muted-ink">
          <p className="font-semibold text-ink">Chefs d'équipe & validation des heures</p>
          <p className="mt-0.5">Désignez un collaborateur comme <b>chef d'équipe</b>, rattachez-lui ses membres, puis
            habilitez-le : il obtient un lien personnel pour voir (et donner un avis sur) les heures de son équipe —
            <b> ainsi que les siennes</b>. L'avis est tracé et ne modifie jamais les heures.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setDesignOpen(true)} data-testid="designer-chef-btn"
            className="gap-2 bg-[var(--shariti-orange)] font-semibold text-white hover:bg-[#d9631f]">
            <UserPlus className="h-4 w-4" />Désigner un chef d'équipe
          </Button>
          <Button variant="outline" onClick={load} data-testid="responsables-refresh" className="gap-2"><RefreshCw className="h-4 w-4" />Actualiser</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Kpi label="Responsables" value={s.total ?? "—"} testid="resp-kpi-total" />
        <Kpi label="Habilités" value={s.habilites ?? "—"} tone="green" testid="resp-kpi-habilites" />
        <Kpi label="Non habilités" value={s.total != null ? s.total - (s.habilites || 0) : "—"} testid="resp-kpi-non" />
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
      ) : !data?.rows?.length ? (
        <EmptyState icon={UserCheck} title="Aucun chef d'équipe" hint="Cliquez sur « Désigner un chef d'équipe » pour promouvoir un collaborateur." testid="responsables-empty" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]">
          <table className="w-full text-sm" data-testid="responsables-table">
            <thead>
              <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-2)] text-[10px] uppercase tracking-wide text-muted-ink">
                <th className="px-3 py-2.5 text-left font-bold">Responsable</th>
                <th className="px-3 py-2.5 text-center font-bold">Équipe</th>
                <th className="px-3 py-2.5 text-center font-bold">Validation des heures</th>
                <th className="px-3 py-2.5 text-left font-bold">Lien personnel</th>
                <th className="px-3 py-2.5 text-left font-bold">Accès à l'espace</th>
                <th className="px-3 py-2.5 text-left font-bold">Dernière action</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--surface-2)]" data-testid={`resp-row-${r.id}`}>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-ink">{r.nom}</span>
                      {r.code_rd
                        ? <span className="rounded bg-[rgba(238,114,46,0.12)] px-1.5 py-0.5 font-mono text-[10px] font-bold text-[var(--shariti-orange)]" data-testid={`resp-code-${r.id}`}>{r.code_rd}</span>
                        : <button onClick={() => setMembersFor(r)} className="inline-flex items-center gap-1 rounded-full border border-[rgba(217,119,6,0.4)] bg-[rgba(245,158,11,0.10)] px-2 py-0.5 text-[10px] font-bold text-[#B45309] transition-colors hover:bg-[rgba(245,158,11,0.18)]" data-testid={`resp-lier-${r.id}`}><Link2 className="h-3 w-3" />Ma perf : à relier</button>}
                    </div>
                    {r.email && <div className="text-[11px] text-muted-ink">{r.email}</div>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <span className="tabular-nums font-semibold text-ink">{r.equipe_taille}</span>
                      <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-[11px]" onClick={() => setMembersFor(r)} data-testid={`resp-membres-${r.id}`}><Users className="h-3.5 w-3.5" />Membres</Button>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <Switch checked={r.heures_validation_enabled} disabled={busyId === r.id}
                        onCheckedChange={(v) => toggle(r, v)} data-testid={`resp-toggle-${r.id}`} />
                      <span className={`text-[11px] font-bold ${r.heures_validation_enabled ? "text-[#0F5A2F]" : "text-muted-ink"}`} data-testid={`resp-state-${r.id}`}>
                        {r.heures_validation_enabled ? "Habilité" : "Non habilité"}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {r.lien ? (
                      <div className="flex items-center gap-1.5">
                        <code className="max-w-[180px] truncate rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[11px]" data-testid={`resp-link-${r.id}`}>{r.lien}</code>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copy(r.lien)} data-testid={`resp-copy-${r.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-ink">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {r.heures_validation_enabled ? (
                      <Select value={r.access_mode || "active"} onValueChange={(m) => setAccess(r, m)} disabled={busyId === r.id}>
                        <SelectTrigger className="h-8 w-[150px] text-[12px]" data-testid={`resp-access-select-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active" data-testid={`resp-access-active-${r.id}`}>Actif</SelectItem>
                          <SelectItem value="readonly" data-testid={`resp-access-readonly-${r.id}`}>Lecture seule</SelectItem>
                          <SelectItem value="disabled" data-testid={`resp-access-disabled-${r.id}`}>Désactivé</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-[11px] text-muted-ink">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-[11px] text-muted-ink" data-testid={`resp-trace-${r.id}`}>
                    {r.habilitation_at ? (
                      <>
                        <div className="font-semibold text-ink">{frenchDateLong(r.habilitation_at.slice(0, 10))}</div>
                        <div>par {r.habilitation_by || "—"}</div>
                      </>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="flex items-center gap-1.5 text-[11px] text-muted-ink">
        <Info className="h-3.5 w-3.5" />
        Le CDP gère les spots et n'a aucun rôle sur les heures. Seul un responsable habilité voit les heures de son équipe (RBAC).
      </p>

      <DesignerChefDialog open={designOpen} onClose={() => setDesignOpen(false)} onDone={() => { setDesignOpen(false); load(); }} />
      {membersFor && <MembresDialog chef={membersFor} onClose={() => setMembersFor(null)} onChanged={load} />}
    </div>
  );
}


function DesignerChefDialog({ open, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => { if (open) { setQ(""); setResults([]); } }, [open]);
  useEffect(() => {
    if (!open) return;
    let active = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/pointage/rds?actifs_terrain=true&include_pauses=true${q ? `&q=${encodeURIComponent(q)}` : ""}`);
        if (active) setResults((r.data || []).filter((x) => !x.archived_at && !x.is_demo).slice(0, 40));
      } catch { if (active) setResults([]); }
      finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, open]);

  const designer = async (rd) => {
    setBusy(rd.id);
    try {
      const r = await api.post(`/pointage/responsables/designer`, { rd_id: rd.id });
      toast.success(r.data.reused ? `${r.data.nom} était déjà chef — réutilisé (lien préservé).` : `${r.data.nom} désigné chef d'équipe.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Désignation impossible"); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-hidden" data-testid="designer-chef-dialog">
        <DialogHeader><DialogTitle>Désigner un chef d'équipe</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-ink">Choisissez le collaborateur à promouvoir. Vous pourrez ensuite lui rattacher ses membres puis l'habiliter (lien /equipe).</p>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
          <Input autoFocus placeholder="Rechercher un collaborateur (nom ou matricule)…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 pl-9" data-testid="designer-search" />
        </div>
        <div className="max-h-[45vh] space-y-1 overflow-y-auto" data-testid="designer-results">
          {loading ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full rounded-xl" />)
            : results.length === 0 ? <div className="py-6 text-center text-sm text-muted-ink">Aucun collaborateur trouvé.</div>
              : results.map((rd) => (
                <div key={rd.id} className="flex items-center justify-between gap-2 rounded-xl border border-[var(--border-soft)] px-3 py-2 hover:bg-[var(--surface-2)]" data-testid={`designer-result-${rd.code}`}>
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-ink">{rd.name}</div>
                    <div className="font-mono text-[11px] text-muted-ink">{rd.code}</div>
                  </div>
                  <Button size="sm" className="h-8 gap-1" onClick={() => designer(rd)} disabled={busy === rd.id} data-testid={`designer-pick-${rd.code}`}>
                    {busy === rd.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserCheck className="h-4 w-4" />Désigner</>}
                  </Button>
                </div>
              ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function MembresDialog({ chef, onClose, onChanged }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [selfQ, setSelfQ] = useState("");
  const [selfResults, setSelfResults] = useState([]);
  const [selfSearching, setSelfSearching] = useState(false);
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/pointage/responsables/${chef.id}/membres`); setInfo(r.data); }
    catch { toast.error("Chargement des membres impossible"); }
    finally { setLoading(false); }
  }, [chef.id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      if (!q) { setResults([]); return; }
      setSearching(true);
      try {
        const r = await api.get(`/pointage/rds?actifs_terrain=true&include_pauses=true&q=${encodeURIComponent(q)}`);
        const memberIds = new Set((info?.membres || []).map((m) => m.rd_id));
        const selfId = info?.self?.rd_id;
        if (active) setResults((r.data || []).filter((x) => !x.archived_at && !x.is_demo && !memberIds.has(x.id) && x.id !== selfId).slice(0, 25));
      } catch { if (active) setResults([]); }
      finally { if (active) setSearching(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, info]);

  const setMembre = async (rd_id, action, nom) => {
    setBusy(rd_id);
    try {
      await api.post(`/pointage/responsables/${chef.id}/membres`, { rd_id, action });
      toast.success(action === "add" ? `${nom} rattaché à l'équipe.` : `${nom} détaché.`);
      setQ("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
    finally { setBusy(null); }
  };

  const retirer = async () => {
    if (!window.confirm(`Retirer ${chef.nom} de la liste des chefs d'équipe ? Son lien /equipe sera désactivé et ses membres détachés.`)) return;
    setRemoving(true);
    try {
      const r = await api.delete(`/pointage/responsables/${chef.id}`);
      toast.success(`${chef.nom} retiré (${r.data.detached} membre(s) détaché(s)).`);
      onChanged?.();
      onClose();
    } catch { toast.error("Retrait impossible"); }
    finally { setRemoving(false); }
  };

  // Relier / détacher la FICHE PERSO du responsable (active sa « Ma perf »).
  const lier = async (rd_id, nom) => {
    setLinking(true);
    try {
      await api.post(`/pointage/responsables/${chef.id}/lier`, { rd_id });
      toast.success(rd_id ? `Fiche perso reliée${nom ? ` : ${nom}` : ""}.` : "Fiche perso détachée.");
      setSelfQ(""); setSelfResults([]);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
    finally { setLinking(false); }
  };

  // Recherche d'une fiche collaborateur à relier (par nom OU code recruteur AT0…).
  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      if (!selfQ) { setSelfResults([]); return; }
      setSelfSearching(true);
      try {
        const r = await api.get(`/pointage/rds?actifs_terrain=true&include_pauses=true&q=${encodeURIComponent(selfQ)}`);
        if (active) setSelfResults((r.data || []).filter((x) => !x.archived_at && !x.is_demo).slice(0, 20));
      } catch { if (active) setSelfResults([]); }
      finally { if (active) setSelfSearching(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [selfQ]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto" data-testid="membres-dialog">
        <DialogHeader><DialogTitle>Équipe de {chef.nom}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}</div>
        ) : (
          <div className="space-y-4">
            {/* FICHE PERSO (Ma perf) du responsable — relier / détacher */}
            <div data-testid="membres-selflink">
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-ink">Fiche perso (Ma perf)</div>
              {info?.self ? (
                <div className="flex items-center justify-between gap-2 rounded-xl border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.06)] px-3 py-2 text-[13px]" data-testid="membres-self">
                  <div className="min-w-0">
                    <div>
                      <span className="font-semibold text-ink">{info.self.name}</span>
                      <span className="ml-1 font-mono text-[11px] text-muted-ink">{info.self.code}</span>
                    </div>
                    <div className="text-[11px] text-muted-ink">Perf individuelle active (niveau, série, badges) + ses propres heures.</div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 shrink-0 gap-1 px-2 text-[11px] text-[#9B2226] hover:bg-[rgba(214,69,69,0.10)]" onClick={() => lier(null)} disabled={linking} data-testid="membres-self-detach">
                    {linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Trash2 className="h-3.5 w-3.5" />Détacher</>}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 rounded-xl border border-dashed border-[rgba(217,119,6,0.4)] bg-[rgba(245,158,11,0.06)] p-3" data-testid="membres-self-empty">
                  <p className="text-[12px] text-ink">Non relié à sa fiche collaborateur → sa « Ma perf » est vide. Reliez-le pour activer sa perf individuelle. Le rapprochement automatique (nom / code recruteur unique) se fait sinon tout seul.</p>
                  {(info?.suggestions || []).length > 0 && (
                    <div className="space-y-1" data-testid="membres-suggestions">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">Suggestions</div>
                      {info.suggestions.map((s) => (
                        <div key={s.rd_id} className="flex items-center justify-between gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5" data-testid={`membres-sugg-${s.code}`}>
                          <div className="min-w-0 text-[13px]">
                            <span className="font-semibold text-ink">{s.name}</span>
                            <span className="ml-1 font-mono text-[11px] text-muted-ink">{s.code}</span>
                            {s.best && <span className="ml-1.5 rounded-full bg-[rgba(31,157,85,0.14)] px-1.5 py-0.5 text-[9px] font-bold text-[#0F5A2F]">suggéré</span>}
                          </div>
                          <Button size="sm" className="h-7 shrink-0 gap-1 px-2 text-[11px]" onClick={() => lier(s.rd_id, s.name)} disabled={linking} data-testid={`membres-sugg-link-${s.code}`}>
                            {linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Link2 className="h-3.5 w-3.5" />Relier</>}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
                    <Input placeholder="Rechercher par nom ou code recruteur (AT0…)…" value={selfQ} onChange={(e) => setSelfQ(e.target.value)} className="h-9 pl-9 text-[13px]" data-testid="membres-self-search" />
                  </div>
                  {selfQ && (
                    <div className="max-h-40 space-y-1 overflow-y-auto" data-testid="membres-self-results">
                      {selfSearching ? [0, 1].map((i) => <Skeleton key={i} className="h-9 w-full rounded-lg" />)
                        : selfResults.length === 0 ? <div className="py-2 text-center text-[12px] text-muted-ink">Aucune fiche trouvée.</div>
                          : selfResults.map((rd) => (
                            <div key={rd.id} className="flex items-center justify-between gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5" data-testid={`membres-self-result-${rd.code}`}>
                              <div className="min-w-0 text-[13px]"><span className="font-semibold text-ink">{rd.name}</span> <span className="font-mono text-[11px] text-muted-ink">{rd.code}</span></div>
                              <Button size="sm" variant="outline" className="h-7 shrink-0 gap-1 px-2 text-[11px]" onClick={() => lier(rd.id, rd.name)} disabled={linking} data-testid={`membres-self-link-${rd.code}`}><Link2 className="h-3.5 w-3.5" />Relier</Button>
                            </div>
                          ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-ink">Membres ({info?.member_count ?? 0})</div>
              <div className="space-y-1" data-testid="membres-list">
                {(info?.membres || []).length === 0 ? <div className="py-3 text-center text-sm text-muted-ink">Aucun membre rattaché.</div>
                  : info.membres.map((m) => (
                    <div key={m.rd_id} className="flex items-center justify-between gap-2 rounded-xl border border-[var(--border-soft)] px-3 py-2" data-testid={`membre-${m.code}`}>
                      <div className="min-w-0"><span className="font-semibold text-ink">{m.name}</span> <span className="font-mono text-[11px] text-muted-ink">{m.code}</span></div>
                      <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-[11px] text-[#9B2226] hover:bg-[rgba(214,69,69,0.10)]" onClick={() => setMembre(m.rd_id, "remove", m.name)} disabled={busy === m.rd_id} data-testid={`membre-remove-${m.code}`}>
                        {busy === m.rd_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Trash2 className="h-3.5 w-3.5" />Détacher</>}
                      </Button>
                    </div>
                  ))}
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-ink">Ajouter un membre</div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
                <Input placeholder="Rechercher (nom ou matricule)…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 pl-9" data-testid="membre-add-search" />
              </div>
              {q && (
                <div className="mt-1 max-h-48 space-y-1 overflow-y-auto" data-testid="membre-add-results">
                  {searching ? <Skeleton className="h-10 w-full rounded-xl" />
                    : results.length === 0 ? <div className="py-3 text-center text-[13px] text-muted-ink">Aucun collaborateur disponible.</div>
                      : results.map((rd) => (
                        <div key={rd.id} className="flex items-center justify-between gap-2 rounded-xl border border-[var(--border-soft)] px-3 py-2 hover:bg-[var(--surface-2)]" data-testid={`membre-add-${rd.code}`}>
                          <div className="min-w-0"><span className="font-semibold text-ink">{rd.name}</span> <span className="font-mono text-[11px] text-muted-ink">{rd.code}</span></div>
                          <Button size="sm" className="h-7 gap-1 px-2 text-[11px]" onClick={() => setMembre(rd.id, "add", rd.name)} disabled={busy === rd.id} data-testid={`membre-pick-${rd.code}`}>
                            {busy === rd.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" />Rattacher</>}
                          </Button>
                        </div>
                      ))}
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button variant="ghost" className="text-[#9B2226] hover:bg-[rgba(214,69,69,0.08)]" onClick={retirer} disabled={removing} data-testid="membres-retirer-chef">
            {removing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}Retirer ce chef
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="membres-close">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ConfirmationsTab() {
  const [mois, setMois] = useState(monthISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [releve, setReleve] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/pointage/confirmations?mois=${mois}`); setData(r.data); }
    catch { toast.error("Chargement des confirmations impossible"); }
    finally { setLoading(false); }
  }, [mois]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try { const r = await api.post(`/pointage/confirmations/generate`, { mois }); toast.success(`Relevés générés (${r.data.crees} nouveau(x))`); load(); }
    catch { toast.error("Génération impossible"); }
    finally { setBusy(false); }
  };

  const openReleve = async (rd_id) => {
    try { const r = await api.get(`/pointage/rd/${rd_id}/releve?mois=${mois}`); setReleve(r.data); }
    catch { toast.error("Relevé indisponible"); }
  };

  // Libellés alignés sur l'état consolidé « conf_etat » renvoyé par le backend.
  // Les heures saisies/corrigées par l'admin ne sont plus affichées « Non confirmé »
  // (trompeur) mais « Saisie admin » : le collaborateur n'a rien à confirmer.
  const CONF = {
    confirme: { label: "Confirmé", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" },
    conteste: { label: "Contesté", cls: "bg-[rgba(214,69,69,0.12)] text-[#9B2226]" },
    valide_admin: { label: "Saisie admin", cls: "bg-[rgba(93,63,211,0.12)] text-[#5D3FD3]" },
    obsolete: { label: "À reconfirmer", cls: "bg-[rgba(185,137,0,0.16)] text-[#8a6600]" },
    non_confirme: { label: "Non confirmé", cls: "bg-[var(--surface-2)] text-muted-ink" },
    rien: { label: "Rien à confirmer", cls: "bg-[var(--surface-2)] text-muted-ink" },
  };
  const RESP = {
    valide: { label: "Validé resp.", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]", Icon: ShieldCheck },
    signale: { label: "Signalé", cls: "bg-[rgba(214,69,69,0.10)] text-[#9B2226]", Icon: Flag },
    partiel: { label: "Partiel", cls: "bg-[rgba(185,137,0,0.14)] text-[#8a6600]", Icon: ShieldQuestion },
    en_attente: { label: "En attente", cls: "bg-[var(--surface-2)] text-muted-ink", Icon: Hourglass },
  };
  const s = data?.summary || {};

  return (
    <div className="space-y-5" data-testid="confirmations-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Input type="month" value={mois} onChange={(e) => setMois(e.target.value)} className="h-10 w-[180px] font-semibold" data-testid="confirmations-month" />
        <Button className="bg-[var(--shariti-orange)] font-semibold hover:bg-[#d9631f]" onClick={generate} disabled={busy} data-testid="confirmations-generate">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />}Générer les relevés
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Collaborateurs" value={s.collaborateurs ?? "—"} testid="conf-kpi-collab" />
        <Kpi label="Confirmés à jour" value={s.confirmes_a_jour ?? "—"} tone="green" testid="conf-kpi-confirmes" />
        <Kpi label="Saisie admin" value={s.valide_admin ?? "—"} tone="violet" testid="conf-kpi-valide-admin" />
        <Kpi label="À reconfirmer" value={s.obsoletes ?? "—"} tone="orange" testid="conf-kpi-obsoletes" />
        <Kpi label="Non confirmés" value={s.non_confirme ?? "—"} testid="conf-kpi-non-confirme" />
        <Kpi label="Contestés" value={s.contestes ?? "—"} tone="red" testid="conf-kpi-contestes" />
        <Kpi label="Validés resp." value={s.valides_resp ?? "—"} tone="green" testid="conf-kpi-resp" />
        <Kpi label="Total heures" value={`${fmtH(s.total_heures)} h`} tone="orange" testid="conf-kpi-total" />
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
      ) : !data?.rows?.length ? (
        <EmptyState icon={ClipboardCheck} title="Aucune heure ce mois-ci" hint="Les relevés apparaîtront dès que des pointages seront enregistrés." testid="confirmations-empty" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]">
          <table className="w-full text-sm" data-testid="confirmations-table">
            <thead>
              <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-2)] text-[10px] uppercase tracking-wide text-muted-ink">
                <th className="px-3 py-2.5 text-left font-bold">Collaborateur</th>
                <th className="px-3 py-2.5 text-right font-bold">Jours</th>
                <th className="px-3 py-2.5 text-right font-bold">Total heures</th>
                <th className="px-3 py-2.5 text-left font-bold">Confirmation collaborateur</th>
                <th className="px-3 py-2.5 text-left font-bold">Avis responsable</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const c = CONF[r.conf_etat] || CONF.non_confirme;
                const rp = RESP[r.resp_etat] || RESP.en_attente;
                const RpIcon = rp.Icon;
                const confirmed = r.statut_confirmation === "confirme";
                return (
                  <tr key={r.rd_id} className="border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--surface-2)]" data-testid={`conf-row-${r.code_rd}`}>
                    <td className="px-3 py-2.5"><div className="font-semibold text-ink">{r.rd_name}</div><CodeBadge code={r.code_rd} /></td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{r.jours}{r.anomalies ? <span className="ml-1 text-[10px] font-bold text-[#9B2226]" title="Anomalies">⚠{r.anomalies}</span> : null}</td>
                    <td className="px-3 py-2.5 text-right font-bold tabular-nums text-[var(--shariti-orange)]">{fmtH(r.total_heures)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge className={`${c.cls} hover:${c.cls} font-semibold`} data-testid={`conf-etat-${r.code_rd}`}>{c.label}</Badge>
                      </div>
                      {r.conf_etat === "valide_admin" && (
                        <div className="mt-0.5 text-[11px] text-muted-ink" data-testid={`conf-admin-hint-${r.code_rd}`}>
                          Heures saisies par l'admin — rien à confirmer par le collaborateur.
                        </div>
                      )}
                      {confirmed && (
                        <div className="mt-0.5 text-[11px] text-muted-ink" data-testid={`conf-couverture-${r.code_rd}`}>
                          Couvre {r.couverture_jours} jour{r.couverture_jours > 1 ? "s" : ""} · {fmtH(r.couverture_total)} h
                          {!r.a_jour && r.perimetre_count != null && <> → périmètre actuel {r.perimetre_count} j · {fmtH(r.perimetre_total)} h</>}
                        </div>
                      )}
                      {r.commentaire && <div className="mt-0.5 text-[11px] text-muted-ink">« {r.commentaire} »</div>}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge className={`${rp.cls} hover:${rp.cls} font-semibold`} data-testid={`conf-resp-${r.code_rd}`}><RpIcon className="mr-1 h-3 w-3" />{rp.label}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-right"><Button variant="ghost" size="sm" className="h-8" onClick={() => openReleve(r.rd_id)} data-testid={`conf-releve-${r.code_rd}`}><FileText className="mr-1 h-3.5 w-3.5" />Relevé</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!releve} onOpenChange={(o) => !o && setReleve(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto overflow-x-hidden" data-testid="releve-dialog">
          <DialogHeader><DialogTitle>Relevé — {releve?.rd_name} · {releve?.mois}</DialogTitle></DialogHeader>
          <div className="max-h-[380px] overflow-y-auto rounded-xl border border-[var(--border-soft)]">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[var(--border-soft)] bg-[var(--surface-2)] text-[10px] uppercase text-muted-ink"><th className="px-3 py-2 text-left">Jour</th><th className="px-3 py-2 text-left">Arr.→Dép.</th><th className="px-3 py-2 text-right">Brut</th><th className="px-3 py-2 text-right">Retenu</th></tr></thead>
              <tbody>
                {(releve?.jours || []).map((j) => (
                  <tr key={j.date} className="border-b border-[var(--border-soft)] last:border-0">
                    <td className="px-3 py-2 tabular-nums">{j.date}</td>
                    <td className="px-3 py-2 tabular-nums text-muted-ink">{j.arrivee || "—"}→{j.depart || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-ink">{fmtH(j.heures_brutes)}</td>
                    <td className={`px-3 py-2 text-right font-bold tabular-nums ${j.deduit ? "text-[#2F6FED]" : "text-ink"}`}>{fmtH(j.heures_retenues)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-[var(--surface-2)] px-4 py-3">
            <span className="text-sm font-bold uppercase tracking-wide text-muted-ink">Total du mois</span>
            <span className="text-xl font-extrabold tabular-nums text-[var(--shariti-orange)]">{fmtH(releve?.total_heures)} h</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------------------------------- Collaborateurs */
// Statut consolidé d'un collaborateur pour la vue pointage :
//  - Actif (lien de pointage en place) ; En pause / Archivé (info PREREC) ; Sans lien.
function collabStatut(rd) {
  if (rd.has_token) return { label: "Actif", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]", Icon: ShieldCheck };
  if (rd.statut_prerec === "paused") return { label: "En pause (PREREC)", cls: "bg-[rgba(185,137,0,0.16)] text-[#8a6600]", Icon: Hourglass };
  if (rd.statut_prerec === "archived") return { label: "Archivé", cls: "bg-[rgba(214,69,69,0.10)] text-[#9B2226]", Icon: XCircle };
  return { label: "Sans lien", cls: "bg-[var(--surface-2)] text-muted-ink", Icon: null };
}

const CollabStatutBadge = ({ rd, testid }) => {
  const st = collabStatut(rd);
  const I = st.Icon;
  return (
    <Badge className={`shrink-0 font-semibold ${st.cls} hover:${st.cls}`} data-testid={testid}>
      {I && <I className="mr-1 h-3 w-3" />}{st.label}
    </Badge>
  );
};

// Rappel discret du statut PREREC (source de vérité) quand il diffère d'« actif ».
const PrerecHint = ({ rd }) => (
  (rd.statut_prerec === "paused" || rd.statut_prerec === "archived") ? (
    <span className="ml-1 rounded bg-[var(--surface-2)] px-1 text-[10px] font-semibold text-muted-ink" data-testid={`collab-prerec-${rd.code}`}>
      PREREC : {rd.statut_prerec === "paused" ? "en pause" : "archivé"}
    </span>
  ) : null
);

const DIV_LABELS = {
  absent_de_prerec: "Absent de PREREC",
  telephone_different: "Téléphone différent",
  cree_depuis_prerec: "Créé depuis PREREC",
  matricule_manquant: "Matricule manquant",
  doublon_matricule: "Doublon de matricule",
};

// Badge d'un collaborateur ARCHIVÉ (fantôme). Indique, si connu, le matricule de
// remplacement (rebranchement PREREC via tombstone).
const ArchivedBadge = ({ rd, testid }) => (
  <div className="flex flex-col items-start gap-0.5" data-testid={testid}>
    <Badge className="w-fit bg-[rgba(214,69,69,0.10)] font-semibold text-[#9B2226] hover:bg-[rgba(214,69,69,0.10)]">
      <Archive className="mr-1 h-3 w-3" />{rd.archived_reason === "rebranche" ? "Rebranché" : "Archivé"}
    </Badge>
    {rd.reassigned_to_code && (
      <span className="text-[10px] font-semibold text-muted-ink">→ nouveau matricule {rd.reassigned_to_code}</span>
    )}
  </div>
);

// Badge « Indépendant » : non soumis au pointage (pas de lien, pas d'heures).
const IndepBadge = ({ testid }) => (
  <Badge variant="secondary" className="w-fit font-semibold" data-testid={testid}>
    <Info className="mr-1 h-3 w-3" />Indépendant
  </Badge>
);

// Panneau « Doublons de matricule » : détecte les fiches partageant un même code,
// permet de choisir la fiche à CONSERVER (recommandée = celle reconnue par PREREC)
// puis de FUSIONNER en sécurité (aperçu avant/après ; heures & liens transférés ;
// doublon archivé, jamais supprimé).
function DoublonsPanel({ onMerged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keepSel, setKeepSel] = useState({});   // { [code]: keepId }
  const [dlg, setDlg] = useState(null);          // { group, keepId, preview, busy }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/rds/doublons");
      const gs = r.data.groups || [];
      setGroups(gs);
      const sel = {};
      gs.forEach((g) => {
        const reco = g.records.find((x) => x.recommended_keep) || g.records[0];
        sel[g.code] = reco?.id;
      });
      setKeepSel(sel);
    } catch { /* silencieux : panneau simplement masqué */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || groups.length === 0) return null;

  const openMerge = async (group) => {
    const keepId = keepSel[group.code];
    const dropIds = group.records.filter((r) => r.id !== keepId).map((r) => r.id);
    if (!keepId || dropIds.length === 0) return;
    setDlg({ group, keepId, preview: null, busy: true });
    try {
      const r = await api.post("/rds/merge", { keep_id: keepId, drop_ids: dropIds, dry_run: true });
      setDlg((s) => s && ({ ...s, preview: r.data, busy: false }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Aperçu impossible"); setDlg(null); }
  };
  const confirmMerge = async () => {
    if (!dlg) return;
    const dropIds = dlg.group.records.filter((r) => r.id !== dlg.keepId).map((r) => r.id);
    setDlg((s) => ({ ...s, busy: true }));
    try {
      const r = await api.post("/rds/merge", { keep_id: dlg.keepId, drop_ids: dropIds });
      toast.success(`Fusion effectuée — ${r.data.total_transfere} élément(s) transféré(s), doublon archivé.`);
      setDlg(null);
      await load();
      onMerged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fusion impossible"); setDlg((s) => s && ({ ...s, busy: false })); }
  };

  const keepName = dlg ? (dlg.group.records.find((r) => r.id === dlg.keepId)?.name || "") : "";

  return (
    <div className="rounded-2xl border border-[rgba(185,137,0,0.4)] bg-[rgba(185,137,0,0.06)] p-5 shadow-[var(--shadow-sm)]" data-testid="doublons-panel">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-[#8a6600]" />
        <h3 className="text-sm font-bold text-ink">Doublons de matricule détectés</h3>
        <Badge className="bg-[rgba(185,137,0,0.16)] font-semibold text-[#8a6600] hover:bg-[rgba(185,137,0,0.16)]" data-testid="doublons-count">{groups.length}</Badge>
      </div>
      <p className="mt-1 text-[13px] text-muted-ink">
        Plusieurs fiches partagent le même matricule. Choisissez la fiche à <b>conserver</b> (recommandée = officielle PREREC),
        puis fusionnez : les heures, affectations et rattachements sont transférés, le doublon est archivé. Aucune perte.
      </p>
      <div className="mt-4 space-y-4">
        {groups.map((g) => (
          <div key={g.code} className="rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid={`doublon-group-${g.code}`}>
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-ink">Matricule {g.code} · {g.count} fiches</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {g.records.map((r) => {
                const selected = keepSel[g.code] === r.id;
                const c = r.counts || {};
                return (
                  <button key={r.id} type="button" onClick={() => setKeepSel((s) => ({ ...s, [g.code]: r.id }))}
                    data-testid={`doublon-pick-${g.code}-${r.id.slice(0, 8)}`}
                    className={`rounded-lg border p-2.5 text-left transition-colors ${selected
                      ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)] ring-1 ring-[var(--shariti-orange)]"
                      : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-sm font-bold text-ink">
                        <span className={`inline-block h-3.5 w-3.5 shrink-0 rounded-full border-2 ${selected ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)]" : "border-[var(--border-strong)]"}`} />
                        {r.name}
                      </span>
                      {selected && <span className="shrink-0 rounded bg-[rgba(31,157,85,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#0F5A2F]">À CONSERVER</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {r.recommended_keep && <span className="rounded bg-[rgba(185,137,0,0.16)] px-1.5 py-0.5 text-[10px] font-bold text-[#8a6600]">Recommandée</span>}
                      {r.synced_at && <span className="rounded bg-[rgba(47,111,237,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#31517d]">Officielle PREREC</span>}
                      {r.chef_equipe_nom && <span className="rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-semibold text-muted-ink">Chef : {r.chef_equipe_nom}</span>}
                      {r.archived_at && <span className="rounded bg-[rgba(214,69,69,0.10)] px-1.5 py-0.5 text-[10px] font-bold text-[#9B2226]">Archivée</span>}
                    </div>
                    <div className="mt-1.5 text-[11px] text-muted-ink">
                      <b className="text-ink">{fmtH(c.heures_total)} h</b> · {c.pointages || 0} pointages · {c.affectations || 0} affect. · {c.bulletins || 0} bulletins
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-2.5 flex justify-end">
              <Button size="sm" className="bg-[var(--shariti-orange)] font-semibold hover:bg-[#d9631f]" onClick={() => openMerge(g)} data-testid={`doublon-merge-${g.code}`}>
                <GitMerge className="mr-1 h-4 w-4" />Fusionner
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Dialog de confirmation avec aperçu (dry-run) */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md" data-testid="doublon-merge-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><GitMerge className="h-5 w-5 text-[#8a6600]" />Confirmer la fusion</DialogTitle></DialogHeader>
          {dlg?.busy && !dlg?.preview ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-ink"><Loader2 className="h-4 w-4 animate-spin" />Calcul de l'aperçu…</div>
          ) : dlg?.preview ? (
            <div className="space-y-3 text-sm">
              <p className="text-muted-ink">Fiche conservée : <b className="text-ink">{keepName}</b> (matricule {dlg.group.code}).</p>
              <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="doublon-merge-preview">
                <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Seront transférés vers la fiche conservée</div>
                {dlg.preview.total_a_transferer === 0 ? (
                  <div className="text-[13px] text-muted-ink">Aucune donnée à transférer (le doublon est vide).</div>
                ) : (
                  <ul className="space-y-0.5 text-[13px] text-ink">
                    {Object.entries(dlg.preview.a_transferer || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                      <li key={k}>• <b>{v}</b> {k.replace("heures_confirmations", "confirmations").replace("presence_overrides", "corrections présence").replace("chefs_equipe", "rattachement chef")}</li>
                    ))}
                  </ul>
                )}
              </div>
              <p className="text-[12px] text-muted-ink">Le(s) doublon(s) seront <b>archivés</b> (conservés pour audit), leur lien de pointage désactivé. Les heures et le rattachement chef restent intacts.</p>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)} data-testid="doublon-merge-cancel">Annuler</Button>
            <Button className="bg-[var(--shariti-orange)] font-semibold hover:bg-[#d9631f]" onClick={confirmMerge} disabled={!dlg?.preview || dlg?.busy} data-testid="doublon-merge-confirm">
              {dlg?.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer la fusion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Format court d'une date ISO -> "jj/mm" ou "jj/mm/aaaa".
const fmtDateFR = (iso, withYear = false) => {
  try { return new Date(iso).toLocaleDateString("fr-FR", withYear ? undefined : { day: "2-digit", month: "2-digit" }); }
  catch { return ""; }
};

// 4.b — Section « Rebranchements récents PREREC » : liste immuable (audit URSSAF)
// des matricules rebranchés/consommés. Masquée s'il n'y a rien.
function RebranchementsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/pointage/sync/rebranchements?jours=30")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => { /* silencieux : panneau masqué */ })
      .finally(() => setLoading(false));
  }, []);
  if (loading || items.length === 0) return null;

  const shown = expanded ? items : items.slice(0, 4);
  const ptsLabel = (rows) => {
    const p = (rows && rows.pointages) || 0;
    return `${p} pointage${p > 1 ? "s" : ""} migré${p > 1 ? "s" : ""}`;
  };

  return (
    <div className="rounded-2xl border border-[rgba(47,111,237,0.35)] bg-[rgba(47,111,237,0.05)] p-5 shadow-[var(--shadow-sm)]" data-testid="rebranchements-panel">
      <div className="flex flex-wrap items-center gap-2">
        <RotateCcw className="h-5 w-5 text-[#31517d]" />
        <h3 className="text-sm font-bold text-ink">Rebranchements PREREC</h3>
        <Badge className="bg-[rgba(47,111,237,0.14)] font-semibold text-[#31517d] hover:bg-[rgba(47,111,237,0.14)]" data-testid="rebranchements-count">{items.length}</Badge>
        <span className="text-xs text-muted-ink">· 30 derniers jours</span>
      </div>
      <p className="mt-1 text-[13px] text-muted-ink">
        Matricules rebranchés côté PREREC et appliqués automatiquement ici (pointages transférés, historique conservé).
      </p>
      <div className="mt-3 space-y-1.5" data-testid="rebranchements-list">
        {shown.map((it) => (
          <div key={it.id || it.key} data-testid={`rebranch-${it.burnt_code}`}
            className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg border border-[var(--border-soft)] bg-white px-3 py-2 text-[13px]">
            <span className="rounded bg-[var(--surface-2)] px-1.5 py-0.5 font-mono text-[11px] text-muted-ink">{it.burnt_code || "—"}</span>
            <ChevronRight className="h-3.5 w-3.5 text-muted-ink" />
            <span className="rounded bg-[rgba(31,157,85,0.12)] px-1.5 py-0.5 font-mono text-[11px] font-bold text-[#0F5A2F]">{it.reassigned_to_code || "—"}</span>
            <span className="font-semibold text-ink">{it.keep_name || "—"}</span>
            {it.kind === "fusion_manuelle" && (
              <span className="rounded bg-[rgba(93,63,211,0.12)] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#5D3FD3]">Manuel</span>
            )}
            <span className="text-muted-ink">· appliqué le {fmtDateFR(it.applied_at)}</span>
            <span className="text-muted-ink">· {ptsLabel(it.rows_migrated)}</span>
            {it.pointages_protected_frozen > 0 && (
              <span className="font-semibold text-[#8a6600]">· {it.pointages_protected_frozen} gelé(s) protégé(s)</span>
            )}
          </div>
        ))}
      </div>
      {items.length > 4 && (
        <button type="button" onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs font-semibold text-[#31517d] hover:underline" data-testid="rebranchements-toggle">
          {expanded ? "Réduire" : `Voir tous (${items.length})`}
        </button>
      )}
    </div>
  );
}

// 4.a — Bandeau discret sur la fiche : indique un rebranchement de matricule et
// ouvre l'historique complet (audit URSSAF). Masqué si aucun historique.
function MatriculeHistoryHint({ rd }) {
  const [open, setOpen] = useState(false);
  const hist = rd.matricule_history || [];
  if (!hist.length) return null;
  const last = hist[hist.length - 1];
  const srcLabel = (s) => (s === "prerec_tombstone" ? "PREREC" : s === "fusion_manuelle" ? "fusion manuelle" : (s || "—"));

  return (
    <>
      <div className="mt-1 flex flex-wrap items-center gap-1 rounded-md bg-[rgba(47,111,237,0.08)] px-2 py-1 text-[11px] text-[#31517d]" data-testid={`matricule-hist-${rd.code}`}>
        <Info className="h-3 w-3 shrink-0" />
        <span>Matricule rebranché depuis <b>{last.from}</b> le {fmtDateFR(last.at, true)}{last.by ? ` · par ${last.by}` : ""}</span>
        <button type="button" onClick={() => setOpen(true)} className="ml-1 font-semibold underline" data-testid={`matricule-hist-open-${rd.code}`}>Voir l'historique</button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`matricule-hist-dialog-${rd.code}`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-[#31517d]" />Historique des matricules — {rd.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {[...hist].reverse().map((h, i) => (
              <div key={i} className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5 text-[13px]">
                <div className="flex items-center gap-2 font-mono text-xs">
                  <span className="rounded bg-white px-1.5 py-0.5">{h.from}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-ink" />
                  <span className="rounded bg-[rgba(31,157,85,0.12)] px-1.5 py-0.5 font-bold text-[#0F5A2F]">{h.to}</span>
                </div>
                <div className="mt-1 text-muted-ink">Le {fmtDateFR(h.at, true)}{h.by ? ` · par ${h.by}` : ""} · source {srcLabel(h.source)}</div>
                {h.reason && <div className="mt-0.5 text-[12px] italic text-muted-ink">« {h.reason} »</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// 4.c — Fusion manuelle (version light) : choisir une fiche cible, aperçu simple,
// une seule confirmation. Réservé aux admins ; trace basique côté serveur.
function ForceMergeDialog({ source, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [target, setTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (source) { setQ(""); setResults([]); setTarget(null); setPreview(null); setBusy(false); } }, [source]);
  useEffect(() => {
    if (!source) return undefined;
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      try {
        const r = await api.get(`/pointage/rds?include_pauses=true&q=${encodeURIComponent(q)}`);
        setResults((r.data || []).filter((x) => x.id !== source.id).slice(0, 8));
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [q, source]);

  if (!source) return null;

  const pick = async (rd) => {
    setTarget(rd); setPreview(null); setBusy(true);
    try {
      const r = await api.post("/rds/force-merge", { source_id: source.id, target_id: rd.id, dry_run: true });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aperçu impossible"); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const r = await api.post("/rds/force-merge", { source_id: source.id, target_id: target.id });
      toast.success(`Fusion effectuée — ${r.data.total_migrated} élément(s) transféré(s) vers ${r.data.keep_name}.`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fusion impossible"); setBusy(false); }
  };
  const c = preview?.a_transferer || {};

  return (
    <Dialog open={!!source} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="force-merge-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><GitMerge className="h-5 w-5 text-[var(--shariti-orange)]" />Fusionner avec un autre collaborateur</DialogTitle></DialogHeader>
        <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5 text-sm">
          <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Fiche à fusionner (sera archivée)</div>
          <div className="font-semibold text-ink">{source.name} <span className="font-mono text-xs text-muted-ink">{source.code || "sans matricule"}</span></div>
        </div>
        {!target ? (
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Fiche cible (à conserver)</label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
              <Input autoFocus placeholder="Nom ou matricule…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 pl-8" data-testid="force-merge-search" />
            </div>
            <div className="mt-2 max-h-56 space-y-1 overflow-y-auto" data-testid="force-merge-results">
              {results.map((rd) => (
                <button key={rd.id} type="button" onClick={() => pick(rd)}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--border-soft)] px-2.5 py-2 text-left text-sm hover:bg-[var(--surface-2)]"
                  data-testid={`force-merge-pick-${rd.code}`}>
                  <span className="min-w-0 truncate font-semibold text-ink">{rd.name}</span>
                  <span className="shrink-0 font-mono text-[11px] text-muted-ink">{rd.code}</span>
                </button>
              ))}
              {q.trim() && results.length === 0 && <div className="px-1 py-2 text-[13px] text-muted-ink">Aucun résultat.</div>}
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-center gap-2 text-[13px]">
              <span className="font-mono text-xs text-muted-ink">{source.code || "—"}</span>
              <ChevronRight className="h-4 w-4 text-[var(--shariti-orange)]" />
              <span className="rounded bg-[rgba(31,157,85,0.12)] px-2 py-0.5 font-semibold text-[#0F5A2F]">{target.name} <span className="font-mono text-[11px]">{target.code}</span></span>
            </div>
            {busy && !preview ? (
              <div className="flex items-center gap-2 py-3 text-muted-ink"><Loader2 className="h-4 w-4 animate-spin" />Calcul de l'aperçu…</div>
            ) : preview ? (
              <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="force-merge-preview">
                <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Seront transférés vers {target.name}</div>
                <div className="text-[13px] text-ink">
                  <b>{c.pointages || 0}</b> pointages · <b>{c.affectations || 0}</b> affectations · <b>{c.bulletins || 0}</b> bulletins · <b>{fmtH(c.heures_total)}</b> h
                </div>
                <p className="mt-2 text-[12px] text-muted-ink">La fiche « {source.name} » sera <b>archivée</b> (conservée pour audit), son lien de pointage désactivé. Aucune perte.</p>
              </div>
            ) : null}
            <button type="button" onClick={() => { setTarget(null); setPreview(null); }} className="text-xs font-semibold text-muted-ink hover:underline" data-testid="force-merge-change-target">← Choisir une autre cible</button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="force-merge-cancel">Annuler</Button>
          <Button className="bg-[var(--shariti-orange)] font-semibold hover:bg-[#d9631f]" onClick={confirm} disabled={!target || !preview || busy} data-testid="force-merge-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer la fusion"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CollaborateursTab() {
  const [sync, setSync] = useState(null);
  const [rds, setRds] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [linkRow, setLinkRow] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busyBulk, setBusyBulk] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const [mergeSource, setMergeSource] = useState(null);

  const loadRds = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, ac] = await Promise.all([
        api.get("/pointage/sync/status").catch(() => ({ data: null })),
        api.get(`/pointage/rds?actifs_terrain=true&include_pauses=true${showArchived ? "&include_archived=true" : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
        api.get("/pointage/collaborateurs/archived-count").catch(() => ({ data: { count: 0 } })),
      ]);
      setSync(s.data); setRds(r.data); setArchivedCount(ac.data?.count ?? 0);
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  }, [q, showArchived]);
  useEffect(() => { loadRds(); }, [loadRds]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/pointage/sync");
      if (r.data.ok) {
        const extra = r.data.auto_archived ? ` — ${r.data.auto_archived} fantôme(s) archivé(s)` : "";
        toast.success(`Synchro PREREC : ${r.data.matched} rapprochés, ${r.data.created} créés${extra}.`);
      } else toast.warning(r.data.message || "Synchro indisponible (jeton non configuré).");
      loadRds();
    } catch { toast.error("Synchro impossible"); }
    finally { setSyncing(false); }
  };

  const openLink = async (rd) => {
    try { const r = await api.get(`/pointage/rd/${rd.id}/link`); setLinkRow({ ...rd, ...r.data }); }
    catch { toast.error("Lien indisponible"); }
  };

  // Divergences « fantômes » (absent_de_prerec) restant à traiter (non archivées).
  const pendingGhosts = (sync?.divergences || []).filter(
    (d) => d.type === "absent_de_prerec" && !d.archived && d.rd_id
  );

  const archiveOne = async (rd_id, code) => {
    setArchivingId(rd_id);
    try {
      await api.post(`/pointage/collaborateurs/${rd_id}/archive`, { reason: "absent_de_prerec" });
      toast.success(`${code || "Collaborateur"} archivé.`);
      loadRds();
    } catch { toast.error("Archivage impossible"); }
    finally { setArchivingId(null); }
  };

  const archiveAll = async () => {
    setBusyBulk(true);
    try {
      const ids = pendingGhosts.map((d) => d.rd_id);
      const r = await api.post("/pointage/collaborateurs/archive-divergences", { rd_ids: ids });
      toast.success(`${r.data.archived} collaborateur(s) archivé(s).`);
      setBulkOpen(false);
      loadRds();
    } catch { toast.error("Archivage groupé impossible"); }
    finally { setBusyBulk(false); }
  };

  const restoreOne = async (rd) => {
    try {
      await api.post(`/pointage/collaborateurs/${rd.id}/restore`);
      toast.success(`${rd.code || "Collaborateur"} restauré.`);
      loadRds();
    } catch { toast.error("Restauration impossible"); }
  };

  // [INCIDENT 05/08/2026 - Pb1] Bascule salarié <-> indépendant. Un indépendant
  // n'est jamais soumis au pointage ni aux rappels.
  const togglePopulation = async (rd) => {
    const next = rd.population === "independant" ? "salarie" : "independant";
    try {
      await api.post(`/pointage/collaborateurs/${rd.id}/population`, { population: next });
      toast.success(next === "independant"
        ? `${rd.name} marqué Indépendant (hors pointage)`
        : `${rd.name} repassé Salarié`);
      loadRds();
    } catch (e) { toast.error(e?.response?.data?.detail || "Modification impossible"); }
  };

  return (
    <div className="space-y-5" data-testid="collaborateurs-tab">
      {/* Doublons de matricule (fusion sécurisée) — s'affiche seulement s'il y en a */}
      <DoublonsPanel onMerged={loadRds} />
      {/* Rebranchements récents PREREC (audit) — s'affiche seulement s'il y en a */}
      <RebranchementsPanel />
      {/* Bandeau synchro PREREC */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid="sync-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-ink">Référentiel PREREC (source de vérité)</div>
            <div className="mt-0.5 text-sm text-muted-ink" data-testid="sync-status-text">
              {sync?.ok === true && `Dernière synchro OK — ${sync.matched} rapprochés, ${sync.created} créés, ${sync.updated} mis à jour.`}
              {sync?.ok === false && `⚠ ${sync.message || "Synchronisation indisponible."}`}
              {sync?.ok == null && "Aucune synchronisation exécutée pour l'instant."}
            </div>
          </div>
          <Button className="w-full shrink-0 bg-[var(--shariti-orange)] font-semibold hover:bg-[#d9631f] sm:w-auto" onClick={doSync} disabled={syncing} data-testid="sync-now-btn">
            {syncing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}Synchroniser maintenant
          </Button>
        </div>
        {sync?.divergences?.length > 0 && (
          <div className="mt-3" data-testid="divergences-block">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-ink">
                Divergences ({sync.divergences_count ?? sync.divergences.length})
                {(sync.divergences.filter((d) => d.type === "absent_de_prerec" && d.archived).length > 0) && (
                  <span className="ml-1 font-normal normal-case text-muted-ink">
                    · {sync.divergences.filter((d) => d.type === "absent_de_prerec" && d.archived).length} résolue(s) par archivage
                  </span>
                )}
              </div>
              {pendingGhosts.length > 0 && (
                <Button size="sm" variant="outline"
                  className="h-8 border-[rgba(214,69,69,0.4)] text-[#9B2226] hover:bg-[rgba(214,69,69,0.08)]"
                  onClick={() => setBulkOpen(true)} data-testid="divergences-archive-all">
                  <Archive className="mr-1.5 h-3.5 w-3.5" />Archiver toutes les divergences absent de PREREC ({pendingGhosts.length})
                </Button>
              )}
            </div>
            <div className="mt-1 max-h-44 space-y-1 overflow-y-auto text-[13px]" data-testid="sync-divergences">
              {sync.divergences.slice(0, 50).map((d, i) => {
                const isGhost = d.type === "absent_de_prerec";
                return (
                  <div key={i}
                    className="flex items-center justify-between gap-2 rounded px-1 py-0.5 hover:bg-[var(--surface-2)]"
                    data-testid={`divergence-row-${d.code || i}`}>
                    <div className="flex min-w-0 items-center gap-2 text-muted-ink">
                      <span className="shrink-0 rounded bg-[var(--surface-2)] px-1.5 py-0.5 font-mono text-[11px]">{d.code || "?"}</span>
                      <span className="truncate">{DIV_LABELS[d.type] || d.type}{d.name ? ` — ${d.name}` : ""}</span>
                    </div>
                    {isGhost && (d.archived ? (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[rgba(214,69,69,0.35)] bg-[rgba(214,69,69,0.08)] px-2 py-0.5 text-[11px] font-bold text-[#9B2226]"
                        data-testid={`divergence-archived-${d.code}`}>
                        <Archive className="h-3 w-3" />Archivé
                      </span>
                    ) : d.rd_id ? (
                      <Button size="sm" variant="ghost"
                        className="h-7 shrink-0 px-2 text-[11px] text-[#9B2226] hover:bg-[rgba(214,69,69,0.10)]"
                        onClick={() => archiveOne(d.rd_id, d.code)} disabled={archivingId === d.rd_id}
                        data-testid={`divergence-archive-${d.code}`}>
                        {archivingId === d.rd_id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Archive className="mr-1 h-3.5 w-3.5" />}
                        Archiver cette entrée
                      </Button>
                    ) : null)}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input placeholder="Rechercher un collaborateur…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 w-full max-w-xs" data-testid="collab-search" />
        <Button variant="ghost" size="icon" className="shrink-0" onClick={loadRds} data-testid="collab-refresh"><RefreshCw className="h-4 w-4" /></Button>
        <label className="ml-auto inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-3 py-2 text-sm font-semibold text-ink hover:bg-[var(--surface-2)]">
          <Checkbox checked={showArchived} onCheckedChange={(v) => setShowArchived(!!v)} data-testid="collab-show-archived" />
          Afficher les collaborateurs archivés ({archivedCount})
        </label>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}</div>
      ) : !rds.length ? (
        <EmptyState icon={Users} title="Aucun collaborateur" hint="Synchronisez PREREC pour récupérer le référentiel." testid="collab-empty" />
      ) : (
        <>
          {/* Mobile : cartes (le lien de pointage reste toujours accessible) */}
          <div className="space-y-2 sm:hidden" data-testid="collab-cards">
            {rds.map((rd) => (
              <div key={rd.id} className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid={`collab-card-${rd.code}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-ink">{rd.name}
                      {rd.population === "independant" && <span className="ml-1 rounded bg-[rgba(93,63,211,0.12)] px-1 text-[10px] font-bold uppercase text-[#5D3FD3]">Indé</span>}
                      {rd.is_demo && <span className="ml-1 rounded bg-[var(--surface-2)] px-1 text-[10px] text-muted-ink">démo</span>}</div>
                    <CodeBadge code={rd.code} />
                    <PrerecHint rd={rd} />
                    <MatriculeHistoryHint rd={rd} />
                  </div>
                  {rd.archived_at
                    ? <ArchivedBadge rd={rd} testid={`collab-archived-m-${rd.code}`} />
                    : rd.has_token
                      ? <Badge className="shrink-0 bg-[rgba(31,157,85,0.12)] font-semibold text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.12)]"><ShieldCheck className="mr-1 h-3 w-3" />Actif</Badge>
                      : rd.population === "independant"
                        ? <IndepBadge testid={`collab-indep-m-${rd.code}`} />
                        : <CollabStatutBadge rd={rd} testid={`collab-statut-m-${rd.code}`} />}
                </div>
                <div className="mt-1.5 text-[12px] text-muted-ink">Plafond/j : <span className="font-semibold text-ink">{rd.plafond_heures_jour != null ? `${fmtH(rd.plafond_heures_jour)} h` : "—"}</span></div>
                {rd.archived_at ? (
                  <Button variant="outline" className="mt-2 w-full border-[rgba(31,157,85,0.4)] text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.08)]" onClick={() => restoreOne(rd)} data-testid={`collab-restore-m-${rd.code}`}><ArchiveRestore className="mr-1.5 h-4 w-4" />Restaurer</Button>
                ) : (
                  <Button variant="outline" className="mt-2 w-full" onClick={() => openLink(rd)} data-testid={`collab-link-m-${rd.code}`}><Link2 className="mr-1.5 h-4 w-4" />{rd.population === "independant" ? "Lien de l'espace" : "Lien de pointage"}</Button>
                )}
                {!rd.archived_at && (
                  <Button variant="ghost" className="mt-1.5 w-full text-[#5D3FD3] hover:bg-[rgba(93,63,211,0.08)]" onClick={() => togglePopulation(rd)} data-testid={`collab-toggle-pop-m-${rd.code}`}>{rd.population === "independant" ? <><UserCheck className="mr-1.5 h-4 w-4" />Repasser en salarié</> : <><UserRound className="mr-1.5 h-4 w-4" />Marquer indépendant</>}</Button>
                )}
                {!rd.archived_at && (
                  <Button variant="ghost" className="mt-1.5 w-full text-muted-ink hover:text-[var(--shariti-orange)]" onClick={() => setMergeSource(rd)} data-testid={`collab-merge-m-${rd.code}`}><GitMerge className="mr-1.5 h-4 w-4" />Fusionner avec un autre collaborateur</Button>
                )}
              </div>
            ))}
          </div>

          {/* Desktop : tableau */}
          <div className="hidden overflow-x-auto rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)] sm:block">
            <table className="w-full min-w-[560px] text-sm" data-testid="collab-table">
            <thead>
              <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-2)] text-[10px] uppercase tracking-wide text-muted-ink">
                <th className="px-3 py-2.5 text-left font-bold">Collaborateur</th>
                <th className="px-3 py-2.5 text-left font-bold">Plafond/j</th>
                <th className="px-3 py-2.5 text-left font-bold">Lien</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rds.map((rd) => (
                <tr key={rd.id} className="border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--surface-2)]" data-testid={`collab-row-${rd.code}`}>
                  <td className="px-3 py-2.5"><div className="font-semibold text-ink">{rd.name}
                    {rd.population === "independant" && <span className="ml-1 rounded bg-[rgba(93,63,211,0.12)] px-1 text-[10px] font-bold uppercase text-[#5D3FD3]" data-testid={`collab-indep-${rd.code}`}>Indé</span>}
                    {rd.is_demo && <span className="ml-1 rounded bg-[var(--surface-2)] px-1 text-[10px] text-muted-ink">démo</span>}</div><CodeBadge code={rd.code} /><PrerecHint rd={rd} /><MatriculeHistoryHint rd={rd} /></td>
                  <td className="px-3 py-2.5 tabular-nums text-muted-ink">{rd.plafond_heures_jour != null ? `${fmtH(rd.plafond_heures_jour)} h` : "—"}</td>
                  <td className="px-3 py-2.5">{rd.archived_at ? <ArchivedBadge rd={rd} testid={`collab-archived-${rd.code}`} /> : rd.has_token ? <Badge className="bg-[rgba(31,157,85,0.12)] text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.12)] font-semibold"><ShieldCheck className="mr-1 h-3 w-3" />Actif</Badge> : rd.population === "independant" ? <IndepBadge testid={`collab-indep-${rd.code}`} /> : <CollabStatutBadge rd={rd} testid={`collab-statut-${rd.code}`} />}</td>
                  <td className="px-3 py-2.5"><div className="flex items-center justify-end gap-1">{!rd.archived_at && <Button variant="ghost" size="sm" className="h-8 text-[#5D3FD3] hover:bg-[rgba(93,63,211,0.10)]" onClick={() => togglePopulation(rd)} data-testid={`collab-toggle-pop-${rd.code}`} title={rd.population === "independant" ? "Repasser en salarié (soumis au pointage)" : "Marquer indépendant (hors pointage)"}>{rd.population === "independant" ? <><UserCheck className="mr-1 h-3.5 w-3.5" />Salarié</> : <><UserRound className="mr-1 h-3.5 w-3.5" />Indé</>}</Button>}{!rd.archived_at && <Button variant="ghost" size="sm" className="h-8 text-muted-ink hover:text-[var(--shariti-orange)]" onClick={() => setMergeSource(rd)} data-testid={`collab-merge-${rd.code}`}><GitMerge className="mr-1 h-3.5 w-3.5" />Fusionner</Button>}{rd.archived_at ? <Button variant="ghost" size="sm" className="h-8 text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.10)]" onClick={() => restoreOne(rd)} data-testid={`collab-restore-${rd.code}`}><ArchiveRestore className="mr-1 h-3.5 w-3.5" />Restaurer</Button> : rd.population === "independant" ? <Button variant="ghost" size="sm" className="h-8" onClick={() => openLink(rd)} data-testid={`collab-link-${rd.code}`}><Link2 className="mr-1 h-3.5 w-3.5" />Lien de l'espace</Button> : <Button variant="ghost" size="sm" className="h-8" onClick={() => openLink(rd)} data-testid={`collab-link-${rd.code}`}><Link2 className="mr-1 h-3.5 w-3.5" />Lien de pointage</Button>}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      <Dialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="bulk-archive-dialog">
          <DialogHeader><DialogTitle>Archiver les divergences « absent de PREREC »</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-ink">
            {pendingGhosts.length} collaborateur(s) local(aux) ne sont plus renvoyés par PREREC.
            Ils seront <b>archivés</b> (masqués de la liste et lien de pointage désactivé) mais jamais
            supprimés (traçabilité URSSAF conservée). Vous pourrez les restaurer à tout moment.
          </p>
          <div className="max-h-52 space-y-1 overflow-y-auto rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2" data-testid="bulk-archive-list">
            {pendingGhosts.map((d) => (
              <div key={d.rd_id} className="flex items-center gap-2 text-[13px]">
                <span className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px]">{d.code}</span>
                <span className="truncate text-muted-ink">{d.name || "—"}</span>
              </div>
            ))}
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setBulkOpen(false)} disabled={busyBulk} data-testid="bulk-archive-cancel">Annuler</Button>
            <Button className="w-full bg-[#9B2226] font-semibold text-white hover:bg-[#7f1c1f] sm:w-auto" onClick={archiveAll} disabled={busyBulk} data-testid="bulk-archive-confirm">
              {busyBulk ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Archive className="mr-1.5 h-4 w-4" />}Archiver {pendingGhosts.length} entrée(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LinkDialog row={linkRow} onClose={() => setLinkRow(null)} onChanged={loadRds} />
      <ForceMergeDialog source={mergeSource} onClose={() => setMergeSource(null)} onDone={() => { setMergeSource(null); loadRds(); }} />
    </div>
  );
}

function LinkDialog({ row, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(null);
  useEffect(() => { setLocal(row); }, [row]);
  if (!local) return null;
  const regen = async () => {
    setBusy(true);
    try { const r = await api.post(`/pointage/rd/${local.rd_id || local.id}/token/regenerate`); setLocal({ ...local, ...r.data, revoked: false }); toast.success("Lien régénéré"); onChanged(); }
    catch { toast.error("Régénération impossible"); } finally { setBusy(false); }
  };
  const setAccess = async (mode) => {
    setBusy(true);
    try {
      await api.post(`/pointage/rd/${local.rd_id || local.id}/access-mode`, { access_mode: mode });
      setLocal({ ...local, access_mode: mode });
      toast.success(mode === "active" ? "Accès complet rétabli"
        : mode === "readonly" ? "Passé en lecture seule"
        : "Accès désactivé");
      onChanged();
    } catch { toast.error("Modification de l'accès impossible"); }
    finally { setBusy(false); }
  };
  const isIndep = local.population === "independant";
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto overflow-x-hidden" data-testid="link-dialog">
        <DialogHeader><DialogTitle className="break-words pr-6 text-base sm:text-lg">{isIndep ? "Lien de l'espace personnel" : "Lien de pointage"} — {local.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-ink">
          {isIndep
            ? "Lien personnel et permanent. L'indépendant y consulte son planning et suit sa performance (niveau, série, badges) — sans pointage d'heures. Il l'enregistre une fois sur son écran d'accueil."
            : "Lien personnel et permanent. Le collaborateur l'enregistre une fois sur son écran d'accueil."}
        </p>
        <div className="flex items-start gap-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2">
          <code className="min-w-0 flex-1 break-all px-2 py-1 text-xs" data-testid="link-url">{local.url}</code>
          <Button size="sm" className="shrink-0 bg-[var(--shariti-orange)] hover:bg-[#d9631f]" onClick={() => { navigator.clipboard?.writeText(local.url); toast.success("Lien copié"); }} data-testid="link-copy"><Copy className="h-3.5 w-3.5" /></Button>
        </div>
        {/* Gestion des accès (départ / sécurité) : Actif / Lecture seule / Désactivé */}
        <div className="mt-1 rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid="link-access-section">
          <div className="flex items-center gap-1.5 text-[13px] font-bold text-ink">
            <ShieldCheck className="h-4 w-4 text-[var(--shariti-orange)]" />Accès à l'espace
          </div>
          <p className="mt-1 text-[11px] leading-snug text-muted-ink">
            En cas de départ : <b>Lecture seule</b> laisse le collaborateur consulter ses heures/sa paie sans rien pouvoir modifier ;
            <b> Désactivé</b> coupe totalement l'accès à son lien.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Select value={local.access_mode || "active"} onValueChange={setAccess} disabled={busy}>
              <SelectTrigger className="h-9 w-full text-[13px] sm:w-[210px]" data-testid="link-access-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active" data-testid="link-access-active">Actif — accès complet</SelectItem>
                <SelectItem value="readonly" data-testid="link-access-readonly">Lecture seule</SelectItem>
                <SelectItem value="disabled" data-testid="link-access-disabled">Désactivé</SelectItem>
              </SelectContent>
            </Select>
            <AccessBadge mode={local.access_mode || "active"} />
          </div>
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" className="w-full sm:w-auto" onClick={regen} disabled={busy} data-testid="link-regenerate"><RotateCcw className="mr-1.5 h-4 w-4" />Régénérer le lien</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------------------- API tab */
function ApiTab() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/pointage/service-tokens"); setTokens(r.data); }
    catch { toast.error("Chargement des jetons impossible"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) { toast.error("Nommez le jeton"); return; }
    setBusy(true);
    try { const r = await api.post("/pointage/service-tokens", { name }); setCreated(r.data); setName(""); load(); }
    catch { toast.error("Création impossible"); }
    finally { setBusy(false); }
  };
  const revoke = async (id) => {
    try { await api.delete(`/pointage/service-tokens/${id}`); toast.success("Jeton révoqué"); load(); }
    catch { toast.error("Révocation impossible"); }
  };

  return (
    <div className="space-y-5" data-testid="api-tab">
      <div className="rounded-2xl border border-[rgba(238,114,46,0.30)] bg-[rgba(238,114,46,0.06)] p-5" data-testid="api-intro">
        <div className="flex items-center gap-2 text-sm font-bold text-ink"><Sparkles className="h-4 w-4 text-[var(--shariti-orange)]" />API d'exposition des heures (Reporting)</div>
        <p className="mt-1 text-sm text-muted-ink">Endpoints en lecture seule, protégés par jeton de service (distinct du compte utilisateur). Les données de démonstration sont toujours exclues.</p>
        <div className="mt-2 space-y-1 font-mono text-[12px] text-muted-ink">
          <div>GET /api/service/v1/heures?mois=YYYY-MM</div>
          <div>GET /api/service/v1/confirmations?mois=YYYY-MM</div>
        </div>
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1"><label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Nouveau jeton</label><Input placeholder="ex : Reporting — PROD" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="token-name" /></div>
        <Button className="bg-[var(--shariti-orange)] font-semibold hover:bg-[#d9631f]" onClick={create} disabled={busy} data-testid="token-create"><Plus className="mr-1.5 h-4 w-4" />Créer</Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}</div>
      ) : !tokens.length ? (
        <EmptyState icon={KeyRound} title="Aucun jeton de service" hint="Créez un jeton pour permettre à Reporting de consommer les heures." testid="tokens-empty" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]">
          <table className="w-full text-sm" data-testid="tokens-table">
            <thead><tr className="border-b border-[var(--border-soft)] bg-[var(--surface-2)] text-[10px] uppercase tracking-wide text-muted-ink"><th className="px-3 py-2.5 text-left font-bold">Nom</th><th className="px-3 py-2.5 text-left font-bold">Préfixe</th><th className="px-3 py-2.5 text-right font-bold">Appels</th><th className="px-3 py-2.5 text-left font-bold">État</th><th className="px-3 py-2.5"></th></tr></thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} className="border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--surface-2)]" data-testid={`token-row-${t.id}`}>
                  <td className="px-3 py-2.5 font-semibold text-ink">{t.name}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-muted-ink">{t.token_prefix}…</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{t.calls_count}</td>
                  <td className="px-3 py-2.5">{t.active ? <Badge className="bg-[rgba(31,157,85,0.12)] text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.12)] font-semibold">Actif</Badge> : <Badge className="bg-[var(--surface-2)] text-muted-ink hover:bg-[var(--surface-2)]">Révoqué</Badge>}</td>
                  <td className="px-3 py-2.5 text-right">{t.active && <Button variant="ghost" size="sm" className="h-8 text-[#9B2226] hover:bg-[rgba(214,69,69,0.10)]" onClick={() => revoke(t.id)} data-testid={`token-revoke-${t.id}`}><Ban className="mr-1 h-3.5 w-3.5" />Révoquer</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent className="max-w-lg" data-testid="token-created-dialog">
          <DialogHeader><DialogTitle>Jeton créé — {created?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#9B2226]">Copiez ce jeton maintenant : il ne sera plus jamais affiché.</p>
          <div className="flex items-center gap-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2">
            <code className="min-w-0 flex-1 truncate px-2 text-xs" data-testid="token-created-value">{created?.token}</code>
            <Button size="sm" className="bg-[var(--shariti-orange)] hover:bg-[#d9631f]" onClick={() => { navigator.clipboard?.writeText(created.token); toast.success("Jeton copié"); }} data-testid="token-created-copy"><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------- shared */
function EmptyState({ icon: Icon, title, hint, testid }) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-10 text-center" data-testid={testid}>
      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-white shadow-[var(--shadow-sm)]"><Icon className="h-6 w-6 text-[var(--shariti-orange)]" /></div>
      <h3 className="text-base font-extrabold text-ink">{title}</h3>
      <p className="mt-1 text-sm text-muted-ink">{hint}</p>
    </div>
  );
}
